import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { useMediaQuery } from "react-responsive"; // Import useMediaQuery
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppContext } from "../contexts/AppContext";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Text } from "../components/catalyst/text";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Header } from "../layout/Header";
import CustomerEngagementHistory from "../components/CustomerEngagementHistory";
import {
  ArrowLeft,
  Maximize2,
  Minimize2,
  ChevronDown,
  Clock,
  MapPin,
  Coffee,
  MessageSquare,
  CheckSquare,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ListChecks,
  MessagesSquare,
  Info,
  BarChart3,
  Building2,
  Globe,
  Linkedin,
  Users,
  CalendarIcon,
  Newspaper,
  CheckCircle2,
  FileText,
  Edit,
  Target,
  Package,
  Heart,
  ClipboardCheck,
  AlertCircle, // Added for fallback message
  Trash2, // <-- Add Trash2 icon
  X, // <-- Add X icon
  Copy,
  Star,
  UserPlus, // <-- Add Assign Icons
  Check, // <--
  UserCheck, // <--
  UserX, // <--
  Projector, // <-- Add Projector icon for HubSpot
  RefreshCw, // <--
  UserMinus, // <--
  Loader2, // <-- Used for loading indicators
  Download, // <-- Add Download icon
  Map as MapIcon, // <-- Add Map icon (renamed to avoid conflict)
  Home, // <-- Add Home icon
  MessageCircle, // <-- Add MessageCircle icon
  Mail, // <-- For contact email
  Phone, // <-- For contact phone
  Brain, // <-- For AI insights
  Search, // <-- For search functionality
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup as LeafletPopup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import * as ics from "ics"; // <-- Import ics library
import { marked } from "marked";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { adaptScheduleToCalendarFormat } from "../utils/scheduleV2Adapter";
import { debugScheduleData as debugScheduleDataImport, debugCalendarEvents as debugCalendarEventsImport } from "../utils/debugScheduleData";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import addDays from "date-fns/addDays";
import "react-big-calendar/lib/css/react-big-calendar.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { cn } from "../utils/cn";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "../components/ui/use-toast";

import MapSection from "../components/MapSection";
import FeedbackComponent from "../components/FeedbackComponent";
import SaveScheduleButton from "../components/SaveScheduleButton";
import useSavedSchedules from "../hooks/useSavedSchedules";
import useStopNotes from "../hooks/useStopNotes";
import { formatDate, isValidDate, safeParseISO } from "../utils/dateUtils"; // Added safeParseISO
import ChurnMetricsCard from "../components/ChurnMetricsCard";
import useChurnData from "../hooks/useChurnData";
import MessageCenter from "../components/MessageCenter";
import QORCMetricsCard from "../components/QORCMetricsCard";
import PrioritizedDataCard from "../components/PrioritizedDataCard";
import AssignRsmModal from "../components/AssignRsmModal"; // <-- Add AssignRsmModal import
import { Progress } from "../components/ui/progress"; // Import Progress
import useHubSpot from "../hooks/useHubSpot"; // Import HubSpot hook
import ContactSelector from "../components/ContactSelector"; // Import ContactSelector
import StopContacts from "../components/StopContacts"; // Import StopContacts
import contactService from "../services/contactService"; // Import ContactService
// import { useBulkCustomerContacts } from "../hooks/useCustomerContacts"; // Disabled to prevent performance issues
import { getCustomerDetails, getCustomerOpportunities } from "../services/v2ApiService"; // Import customer details API

// Production-safe logger for error handling only
const logger = {
  log: () => {}, // No-op in production
  warn: () => {}, // No-op in production
  error: console.error, // Keep errors in production
  debug: () => {}, // No-op in production
  group: () => {}, // No-op in production
  groupEnd: () => {}, // No-op in production
};

// Import debug functions but make them no-ops in production
const debugScheduleData = () => {};
const debugCalendarEvents = () => {};

// --- Localizer Setup ---
const locales = {
  "en-US": enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 0 }),
  getDay,
  locales: {
    'en-US': enUS,
  },
  formats: {
    monthHeaderFormat: 'MMMM yyyy',
    dayHeaderFormat: 'EEE M/d',
    weekHeaderFormat: 'MMMM dd',
    dayRangeHeaderFormat: 'MMMM dd',
    timeGutterFormat: 'h:mm a',
    eventTimeRangeFormat: 'h:mm a',
    selectRangeFormat: 'h:mm a',
    agendaHeaderFormat: 'MMMM dd',
    agendaDateFormat: 'EEE M/d',
    agendaTimeFormat: 'h:mm a',
    agendaTimeRangeFormat: 'h:mm a'
  }
});

// Fix for default leaflet icon
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// --- Calendar Formats ---
const calendarFormats = {
  monthHeaderFormat: 'MMMM yyyy',
  dayHeaderFormat: 'EEE M/d',
  weekHeaderFormat: 'MMMM dd',
  dayRangeHeaderFormat: ({ start, end }, culture, localizer) => 
    localizer.format(start, 'MMMM dd', culture) + ' - ' + 
    localizer.format(end, 'MMMM dd', culture),
  timeGutterFormat: 'h:mm a',
  eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, 'h:mm a', culture) + ' - ' +
    localizer.format(end, 'h:mm a', culture),
  selectRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, 'h:mm a', culture) + ' - ' +
    localizer.format(end, 'h:mm a', culture),
  agendaHeaderFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, 'MMMM dd', culture) + ' - ' + 
    localizer.format(end, 'MMMM dd', culture),
  agendaDateFormat: 'EEE M/d',
  agendaTimeFormat: 'h:mm a',
  agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
    localizer.format(start, 'h:mm a', culture) + ' - ' +
    localizer.format(end, 'h:mm a', culture)
};

// --- Create Drag and Drop Calendar ---
const DragAndDropCalendar = withDragAndDrop(Calendar);

// Safe Calendar wrapper to prevent format errors
const SafeCalendar = React.forwardRef((props, ref) => {
  try {
    // Ensure localizer and formats are valid before rendering
    if (!props.localizer || !props.localizer.formats) {
      console.error('SafeCalendar: Invalid localizer or missing formats');
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-muted-foreground">Calendar unavailable</p>
            <p className="text-xs text-muted-foreground">Localizer configuration error</p>
          </div>
        </div>
      );
    }
    return <DragAndDropCalendar ref={ref} {...props} />;
  } catch (error) {
    console.error('SafeCalendar: Error rendering calendar:', error);
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-muted-foreground">Calendar error</p>
          <p className="text-xs text-muted-foreground">Please refresh the page</p>
        </div>
      </div>
    );
  }
});


// --- Helper to parse duration string (e.g., "1.5 hr") to minutes ---
const parseDurationToMinutes = (durationStr) => {
  if (!durationStr || typeof durationStr !== "string") return 60; // Default to 1 hour if invalid
  const parts = durationStr.toLowerCase().split(" ");
  let minutes = 0;
  if (parts.length > 0) { // Ensure parts[0] exists
    const value = parseFloat(parts[0]);
    if (!isNaN(value)) {
      if (parts.length > 1 && parts[1].startsWith("hr")) { // Ensure parts[1] exists for "hr"
        minutes = value * 60;
      } else if (parts.length > 1 && parts[1].startsWith("min")) { // Ensure parts[1] exists for "min"
        minutes = value;
      } else if (parts.length === 1 && value > 0) { 
        // If only a number is provided (e.g., "30"), assume it's minutes.
        // This handles cases where units might be missing but a valid number is present.
        minutes = value;
      }
      // If value is 0 or negative, or parsing failed to set minutes, it will be handled by the return.
    }
  }
  // Ensure a positive duration, defaulting to 60 minutes.
  // If a valid positive duration was parsed, use it, but ensure it's at least, say, 15 minutes.
  return minutes > 0 ? Math.max(15, minutes) : 60; 
};

// --- Helper to transform schedule data to calendar events ---

// Add this helper function within ItineraryDetailsPage.js
function formatDurationToString(totalMinutes) {
  if (totalMinutes <= 0) return "0.25 hr"; // Default to a minimum duration like 15 mins if 0 or less
  const hours = totalMinutes / 60;
  // If hours is a whole number (e.g., 1.0, 2.0), display as "1 hr", "2 hr"
  if (Number.isInteger(hours)) {
    return `${hours} hr`;
  }
  // Otherwise, display with decimals (e.g., "0.5 hr", "1.25 hr")
  return `${parseFloat(hours.toFixed(2))} hr`;
}

const transformScheduleToEvents = (schedule) => {
  const events = [];
  if (!schedule) {
    
    return events;
  }
  
  if (!Array.isArray(schedule)) {
    
    // Try to adapt it if it's an object
    if (typeof schedule === 'object') {
      const adapted = adaptScheduleToCalendarFormat(schedule);
      if (Array.isArray(adapted)) {
        return transformScheduleToEvents(adapted);
      }
    }
    return events;
  }

  // logger.log( // Disabled for brevity
  //   "[transformScheduleToEvents] Input:",
  //   JSON.stringify(schedule, null, 2),
  // );

  // Add dayIndex to the outer loop
  schedule.forEach((daySchedule, dayIndex) => {
    //  // Commented out - logs large objects
    
    // Ensure daySchedule and daySchedule.day are valid before proceeding
    if (!daySchedule || typeof daySchedule.day !== "string") {
      
      return; // Skip invalid day schedule
    }
    let dayDate;
    try {
      // Try parsing as YYYY-MM-DD first
      dayDate = safeParseISO(daySchedule.day + "T00:00:00"); // Ensure it's treated as local date

      if (!dayDate) {
        // Fallback parsing for "EEEE, MMMM d" format (needs current year context)
        const currentYear = new Date().getFullYear();
        const parsedFallback = parse(
          `${daySchedule.day}, ${currentYear}`,
          "EEEE, MMMM d, yyyy",
          new Date(),
        );
        if (isValidDate(parsedFallback)) {
          dayDate = parsedFallback;
        }
      }

      if (!isValidDate(dayDate)) {
        // Check if parsing failed
        return; // Skip day if parsing fails
      }
    } catch (e) {
      console.error(`Error parsing day string: ${daySchedule.day}`, e);
      return; // Skip this day if error
    }

    // Ensure daySchedule.agenda is an array before iterating
    if (!Array.isArray(daySchedule.agenda)) {
      
      return; // Skip if agenda is not an array
    }

    daySchedule.agenda.forEach((item, agendaIndex) => {
      // Ensure item and item.time are valid
      if (!item || typeof item.time !== "string") {
        
        return; // Skip invalid agenda item
      }
      try {
        let hours, minutes;
        
        // Check if time includes AM/PM (12-hour format)
        if (item.time && item.time.includes('AM') || item.time.includes('PM')) {
          // Parse time like "8:00 AM - 9:30 AM" to get start time
          const timeMatch = item.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (timeMatch) {
            hours = parseInt(timeMatch[1]);
            minutes = parseInt(timeMatch[2]);
            const period = timeMatch[3].toUpperCase();
            
            // Convert to 24-hour format
            if (period === 'PM' && hours !== 12) {
              hours += 12;
            } else if (period === 'AM' && hours === 12) {
              hours = 0;
            }
          } else {
            
            return;
          }
        } else {
          // Original 24-hour format parsing
          const timeParts = item.time.split(":");
          if (timeParts.length !== 2) {
            
            return; // Skip item with invalid time format
          }
          [hours, minutes] = timeParts.map(Number);
          if (isNaN(hours) || isNaN(minutes)) {
            
            return; // Skip if hours/minutes are not numbers
          }
        }

        const start = new Date(dayDate); // Use the successfully parsed dayDate
        start.setHours(hours, minutes, 0, 0);

        let durationMinutes = 60; // Default duration
        if (item.type === "break") {
          durationMinutes = 60; // Default break time
        } else if (item.estimated_onsite) {
          durationMinutes = parseDurationToMinutes(item.estimated_onsite);
        }

        const end = new Date(start.getTime() + durationMinutes * 60000);

        let eventTitle;
        let skipEvent = false;

        if (item.type === "break") {
          if (!item.label || String(item.label).trim() === "") {
            // If it's a break and has no valid label, prepare to skip it.
            skipEvent = true;
          } else {
            eventTitle = item.label;
          }
        } else { // For customer stops (item.type !== "break")
          if (!item.customer_name || String(item.customer_name).trim() === "") {
            // If it's a customer stop and has no valid name, prepare to skip it.
            skipEvent = true;
          } else {
            eventTitle = item.customer_name;
          }
        }

        // If we decided to skip based on missing title/label, exit this iteration.
        if (skipEvent) {
          return; // This 'return' is for the forEach callback, effectively a 'continue'.
        }

        // Now, proceed with date/time validation for items that have a valid title.
        const eventIsValid =
            isValidDate(start) &&
            isValidDate(end) &&
            start.getTime() < end.getTime(); // Event must have a positive duration

        if (eventIsValid) {
          const event = {
            title: eventTitle, // At this point, eventTitle is guaranteed to be non-empty.
            start,
            end,
            allDay: false,
            resource: {
              ...item,
              originalDayIndex: dayIndex,
              originalAgendaIndex: agendaIndex,
            },
            type: item.type,
          };
          events.push(event);
        } else {
          // This warning is for items that had a title but failed date/time validation.
        }
      } catch (e) {
        console.error(`Error processing agenda item:`, item, e);
        // Skip this item if there's an error processing it
      }
    });
  });

  // Log the result for debugging
  // logger.log( // Disabled for brevity
  //   `[transformScheduleToEvents] Successfully created ${events.length} calendar events`,
  // );

  return events;
};

// --- Custom Toolbar for React Big Calendar ---
const CustomToolbar = ({ label, onView, onNavigate, views, view, isMobile, isDragging }) => {
  const goToBack = () => {
    
    onNavigate("PREV");
  };
  const goToNext = () => {
    
    onNavigate("NEXT");
  };
  const goToCurrent = () => {
    
    onNavigate("TODAY");
  };

  // Filter views based on isMobile for the toolbar buttons
  const availableViews = isMobile ? ["day"] : views;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 items-center px-2 py-3 bg-background border-b gap-2 sm:gap-0">
      {/* Navigation Buttons */}
      <div className="flex gap-1 justify-center sm:justify-start">
        <Button
          variant="outline"
          size="sm"
          onClick={goToBack}
          className="flex items-center gap-1 px-2"
          title="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goToCurrent}
          className="px-2"
          title="Go to schedule start"
        >
          Schedule Start
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNext}
          className="flex items-center gap-1 px-2"
          title="Next week"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      {/* Label */}
      <div className="text-center">
        <span className="font-semibold text-base md:text-lg flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
          {label}
        </span>
      </div>
      {/* View Buttons */}
      <div className="flex gap-1 justify-center sm:justify-end">
        {availableViews.map((v) => (
          <Button
            key={v}
            type="button"
            variant={v === view ? "default" : "outline"}
            size="sm"
            className="capitalize px-2"
            onClick={() => onView(v)}
          >
            {v}
          </Button>
        ))}
      </div>
    </div>
  );
};

// Small map popup component for showing opportunity location
const OpportunityMapPopup = ({ opportunity, isOpen, onClose }) => {
  // Build address string
  const addressParts = [
    opportunity.address,
    opportunity.city,
    opportunity.state,
    opportunity.zipcode
  ].filter(Boolean);
  
  const fullAddress = addressParts.join(", ");
  
  // Use coordinates if available, otherwise default to US center
  const defaultCenter = [39.8283, -98.5795];
  const [mapCenter, setMapCenter] = useState(
    opportunity.coordinates && opportunity.coordinates.lat && opportunity.coordinates.lng
      ? [opportunity.coordinates.lat, opportunity.coordinates.lng]
      : defaultCenter
  );
  
  useEffect(() => {
    // Update map center if coordinates are available
    if (opportunity.coordinates && opportunity.coordinates.lat && opportunity.coordinates.lng) {
      setMapCenter([opportunity.coordinates.lat, opportunity.coordinates.lng]);
    }
  }, [opportunity.coordinates]);
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {opportunity.opportunityname || opportunity.opportunitynum || opportunity.ordernumber || "Location"}
          </DialogTitle>
          <DialogDescription>
            {fullAddress || "No address available"}
          </DialogDescription>
        </DialogHeader>
        <div style={{ height: "400px", width: "100%" }}>
          <MapContainer
            center={mapCenter}
            zoom={opportunity.coordinates ? 15 : 4}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {fullAddress && (
              <Marker position={mapCenter}>
                <LeafletPopup>
                  <div>
                    <strong>{opportunity.opportunityname || "Opportunity"}</strong>
                    <br />
                    {fullAddress}
                  </div>
                </LeafletPopup>
              </Marker>
            )}
          </MapContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Define OpportunityDialog component
export const OpportunityDialog = ({
  opportunities,
  dataTypeLabel = "Opportunities",
}) => {
  const [sortField, setSortField] = useState("date"); // Default to date
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showMapPopup, setShowMapPopup] = useState(false);

  // Debug logging
  useEffect(() => {
    
    if (opportunities && opportunities.length > 0) {
      
      
    }
  }, [opportunities]);

  // Handle sort change
  const handleSortChange = (field) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field - set it and default to descending
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Get the sorted opportunities
  const sortedOpportunities = [...(opportunities || [])].sort((a, b) => {
    let aValue, bValue;

    // Extract values based on sort field
    switch (sortField) {
      case "opportunitynum": // Use for both quote and order numbers
        aValue = a.opportunitynum || a.ordernumber || "";
        bValue = b.opportunitynum || b.ordernumber || "";
        break;
      case "date":
        // Try to use lastupdate, fall back to createddate
        aValue = a.lastupdate
          ? new Date(a.lastupdate).getTime()
          : a.createddate
            ? new Date(a.createddate).getTime()
            : 0;
        bValue = b.lastupdate
          ? new Date(b.lastupdate).getTime()
          : b.createddate
            ? new Date(b.createddate).getTime()
            : 0;
        break;
      case "amount":
        // Use either field, default to 0
        aValue = a.estimatedvalue || a.amount || 0;
        bValue = b.estimatedvalue || b.amount || 0;
        break;
      default:
        // Default to lastupdate
        aValue = a.lastupdate ? new Date(a.lastupdate).getTime() : 0;
        bValue = b.lastupdate ? new Date(b.lastupdate).getTime() : 0;
    }

    // Apply sorting direction
    if (sortDirection === "asc") {
      // Handle non-numeric comparison gracefully
      if (typeof aValue === "string" && typeof bValue === "string") {
        return aValue.localeCompare(bValue);
      }
      return aValue > bValue ? 1 : -1;
    } else {
      if (typeof aValue === "string" && typeof bValue === "string") {
        return bValue.localeCompare(aValue);
      }
      return aValue < bValue ? 1 : -1;
    }
  });

  // Helper to show sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>; // Use unicode arrows
  };

  // Capitalize first letter for title
  const titleCaseLabel =
    dataTypeLabel.charAt(0).toUpperCase() + dataTypeLabel.slice(1);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg">
          {titleCaseLabel} ({opportunities?.length || 0})
        </DialogTitle>
      </DialogHeader>

      <div className="flex items-center justify-between border-b pb-2 mb-2">
        <div className="text-sm text-muted-foreground">Sort by:</div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={sortField === "opportunitynum" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("opportunitynum")}
          >
            ID/Number
            <SortIndicator field="opportunitynum" />
          </Button>
          <Button
            size="sm"
            variant={sortField === "date" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("date")}
          >
            Date
            <SortIndicator field="date" />
          </Button>
          <Button
            size="sm"
            variant={sortField === "amount" ? "default" : "outline"}
            className="h-7 px-2 text-xs"
            onClick={() => handleSortChange("amount")}
          >
            Amount
            <SortIndicator field="amount" />
          </Button>
        </div>
      </div>

      <ScrollArea className="max-h-[60vh]">
        <div className="space-y-2 pr-4">
          {sortedOpportunities.map((opportunity, i) => {
            // Log each opportunity's address data
            
            return (
            <div key={i} className="p-3 bg-muted/30 rounded-md border">
              <div className="flex justify-between items-start gap-2 mb-1">
                <div className="font-medium flex-1">
                  <div>
                    {opportunity.opportunityname ||
                      opportunity.opportunitynum || // Quote Number
                      opportunity.ordernumber || // Order Number
                      "Unnamed"}
                  </div>
                  {/* Address formatted properly */}
                  {(opportunity.projectcity || opportunity.projectstate) && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <div>
                        {[opportunity.projectcity, opportunity.projectstate]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(opportunity.estimatedvalue || opportunity.amount) && (
                    <div className="text-sm font-semibold">
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        notation: "compact",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(
                        opportunity.estimatedvalue || opportunity.amount || 0,
                      )}
                    </div>
                  )}
                  {/* Map icon button */}
                  {(opportunity.projectcity || opportunity.projectstate) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        setSelectedOpportunity(opportunity);
                        setShowMapPopup(true);
                      }}
                      title="View on map"
                    >
                      <MapPin className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Full Address Display */}
              {(opportunity.projectcity || opportunity.projectstate) && (
                <div className="mt-2 p-2 bg-muted/20 rounded text-sm">
                  <div className="font-medium text-xs text-muted-foreground mb-1">
                    Project Location:
                  </div>
                  <div className="text-sm">
                    {[opportunity.projectcity, opportunity.projectstate]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-muted-foreground mt-2">
                {opportunity.markettype && (
                  <div className="col-span-2 mb-0.5">
                    <span className="inline-block bg-muted px-1.5 py-0.5 rounded-sm">
                      {opportunity.markettype}
                    </span>
                    {opportunity.api_status && (
                      <span className="inline-block ml-1 bg-muted/70 px-1.5 py-0.5 rounded-sm">
                        {opportunity.api_status}
                      </span>
                    )}
                  </div>
                )}


                {opportunity.lastupdate &&
                  isValidDate(opportunity.lastupdate) && (
                    <>
                      <span>Updated:</span>
                      <span>
                        {formatDate(opportunity.lastupdate, "MM/dd/yyyy")}
                      </span>
                    </>
                  )}

                {opportunity.createddate &&
                  isValidDate(opportunity.createddate) && (
                    <>
                      <span>Created:</span>
                      <span>
                        {formatDate(opportunity.createddate, "MM/dd/yyyy")}
                      </span>
                    </>
                  )}

                {(opportunity.opportunitynum || opportunity.ordernumber) && (
                  <>
                    <span>ID/Num:</span>
                    <span>
                      {opportunity.opportunitynum || opportunity.ordernumber}
                    </span>
                  </>
                )}

              </div>
            </div>
            );
          })}
        </div>
      </ScrollArea>
      
      {/* Map popup */}
      {selectedOpportunity && (
        <OpportunityMapPopup
          opportunity={selectedOpportunity}
          isOpen={showMapPopup}
          onClose={() => {
            setShowMapPopup(false);
            setSelectedOpportunity(null);
          }}
        />
      )}
    </>
  );
};

function ItineraryDetailsPage() {
  const {
    data, // Original data from context/localStorage
    selectedClusterId: contextSelectedClusterId, // Renamed to avoid conflict
    loading: appLoading,
    error: appError,
    setData, // <<<< ADD setData from context
    API_BASE_URL,
    directionsFeedback,
    setDirectionsFeedback,
    customerStopsFeedback,
    setCustomerStopsFeedback,
    itineraryFeedback,
    setItineraryFeedback,
    reasoningFeedback,
    setReasoningFeedback,
  } = useContext(AppContext);

  const { user, token } = useAuth();
  const isRSM = user?.role === "RSM";
  const isBM = user?.role === "BM";
  
  // HubSpot integration hook
  const {
    contacts: hubspotContacts,
    loadingContacts: hubspotLoading,
    error: hubspotError,
    hasMoreContacts,
    fetchAllContacts,
    loadMoreContacts,
    searchContact,
    createContact,
    findOrCreateContact,
    createEngagement,
    getCustomerEngagements,
    associateEngagementWithCustomer,
  } = useHubSpot();

  const navigate = useNavigate();
  const { clusterId } = useParams(); // ID from URL takes precedence
  const currentClusterId = clusterId || contextSelectedClusterId; // Use URL param first
  const prevClusterIdRef = React.useRef(null); // <<<< ADD Ref to track previous ID

  // <<< MOVED HOOK HERE - BEFORE EARLY RETURNS >>>
  // Feedback context object
  const feedbackContext = useMemo(
    () => ({
      scheduleId: currentClusterId,
    }),
    [currentClusterId],
  );

  // State for the original fetched/context data for the CURRENT cluster
  const [clusterData, setClusterData] = useState(null);

  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [activeTab, setActiveTab] = useState("schedule"); // Default tab
  const [showReasoningDialog, setShowReasoningDialog] = useState(false);
  const [currentDate, setCurrentDate] = useState(null); // Track current calendar date
  const [calendarInitialized, setCalendarInitialized] = useState(false);
  const [loading, setLoading] = useState(appLoading);
  const [error, setError] = useState(appError);

  // ... Add state for Assign Modal ...
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [actionTargetId, setActionTargetId] = useState(null); // To track loading state for assign/unassign buttons

  // State for modified schedule data (for DnD)
  const [modifiedScheduleData, setModifiedScheduleData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);


  const [stopProgressOpen, setStopProgressOpen] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopStatus, setStopStatus] = useState("");
  const [stopNotes, setStopNotes] = useState("");
  
  // State for highlighting customers on map hover
  const [hoveredCustomerId, setHoveredCustomerId] = useState(null);
  
  // Contact management state
  const [contactSelectorOpen, setContactSelectorOpen] = useState(false);
  const [contactsViewOpen, setContactsViewOpen] = useState(false);
  const [selectedStopForContacts, setSelectedStopForContacts] = useState(null);

  // BM notes dialog state
  const [bmNotesOpen, setBmNotesOpen] = useState(false);
  const [bmNotes, setBmNotes] = useState("");

  // RSM notes dialog state
  const [rsmNotesOpen, setRsmNotesOpen] = useState(false);
  const [rsmNotes, setRsmNotes] = useState("");

  // Contact form state
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: ""
  });

  // Add Stops state
  const [availableOpportunities, setAvailableOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [opportunitiesError, setOpportunitiesError] = useState(null);
  const [opportunitySearchQuery, setOpportunitySearchQuery] = useState("");
  const [selectedOpportunities, setSelectedOpportunities] = useState(new Set());
  
  // Pagination state for stops tab
  const [currentPage, setCurrentPage] = useState(1);
  const [stopsPerPage, setStopsPerPage] = useState(5);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalCustomerPages, setTotalCustomerPages] = useState(0);
  const [paginatedCustomers, setPaginatedCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState(null);

  // Customer details state
  const [customerDetailsData, setCustomerDetailsData] = useState({});
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState({});
  
  // Customer opportunities state
  const [customerOpportunitiesData, setCustomerOpportunitiesData] = useState({});
  const [customerOpportunitiesLoading, setCustomerOpportunitiesLoading] = useState({});
  
  // Opportunities dialog state
  const [opportunitiesDialogOpen, setOpportunitiesDialogOpen] = useState(false);
  const [selectedCustomerOpportunities, setSelectedCustomerOpportunities] = useState(null);
  const [opportunitiesFilter, setOpportunitiesFilter] = useState('all'); // all, quotes, orders, rentals
  const [opportunitiesSort, setOpportunitiesSort] = useState('amount_desc'); // amount_desc, amount_asc, name_asc, date_desc
  const [opportunitiesSearch, setOpportunitiesSearch] = useState('');
  const [showOnlyMappable, setShowOnlyMappable] = useState(false); // Filter for opportunities with coordinates
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [selectedMapOpportunity, setSelectedMapOpportunity] = useState(null);

  // Engagement form state
  const [engagementFormOpen, setEngagementFormOpen] = useState(false);
  const [engagementInfo, setEngagementInfo] = useState({
    engagement_type: "MEETING",
    notes: "",
    contact_id: ""
  });
  
  // Helper function to extract customer data from selected stop
  const extractCustomerData = (stop) => {
    if (!stop) return null;
    
    // Try different possible structures
    const customer = stop.customer || {};
    const contactInfo = customer.contact_info || {};
    
    return {
      first_name: stop.first_name || customer.first_name || contactInfo.first_name || "",
      last_name: stop.last_name || customer.last_name || contactInfo.last_name || "",
      email: stop.email || customer.email || contactInfo.email || "",
      phone: stop.phone || customer.phone || contactInfo.phone || ""
    };
  };
  
  // Function to reset form states
  const resetFormState = () => {
    setContactInfo({
      first_name: "",
      last_name: "",
      email: "",
      phone: ""
    });
    setEngagementInfo({
      engagement_type: "MEETING",
      notes: "",
      contact_id: ""
    });
    setStopNotes("");
    setContactFormOpen(false);
    setEngagementFormOpen(false);
  };
  
  // Function to open HubSpot engagement dialog
  const handleOpenHubspotDialog = (stop) => {
    if (!stop) return;
    
    setSelectedStop(stop);
    
    // Pre-populate contact info if available (for when adding new contacts)
    const customerData = extractCustomerData(stop);
    if (customerData) {
      setContactInfo(customerData);
    }
    
    // Pre-populate notes from existing stop notes if available
    if (stop.notes) {
      setEngagementInfo({
        ...engagementInfo,
        notes: stop.notes,
        contact_id: "" // Reset contact selection
      });
    } else if (stopNotes) {
      setEngagementInfo({
        ...engagementInfo,
        notes: stopNotes,
        contact_id: "" // Reset contact selection
      });
    } else {
      // Make sure contact_id is reset
      setEngagementInfo({
        ...engagementInfo,
        contact_id: "" // Reset contact selection
      });
    }
    
    // Open the engagement form directly
    setEngagementFormOpen(true);
  };

  // Integrate with saved schedules and stop notes hooks
  const { toggleSaveSchedule } = useSavedSchedules();
  const { updateStopNotes, deleteStopNotes } = useStopNotes();

  // Add churn data hook
  const {
    loading: churnLoading,
    fetchChurnData,
    getChurnDataForCustomer,
  } = useChurnData();

  const isMobile = useMediaQuery({ query: '(max-width: 639px)' }); // Tailwind 'sm' breakpoint is 640px

  // Add this near the beginning of the ItineraryDetailsPage function
  // Right after other state declarations
  const [showMobileNav, setShowMobileNav] = useState(true);
  const lastScrollY = useRef(0);

  // Add a scroll effect to hide/show bottom nav
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current + 10) {
        setShowMobileNav(false); // Hide on scroll down
      } else if (currentScrollY < lastScrollY.current - 10) {
        setShowMobileNav(true); // Show on scroll up
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  // Note: HubSpot contacts are fetched on-demand when dialog opens

  // Get customers count and URL from cluster data
  const customersCount = clusterData?.itinerary?.customers_count || 0;
  const customersUrl = clusterData?.itinerary?.customers_url || null;

  // Function to fetch paginated customers
  const fetchPaginatedCustomers = useCallback(
    async (page, limit) => {
      if (!currentClusterId || !customersUrl) {
        
        return;
      }

      setCustomersLoading(true);
      setCustomersError(null);

      try {
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Construct the full URL - customersUrl is like "/schedules/{id}/customers"
        const baseUrl = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';
        const url = `${baseUrl}${customersUrl}?page=${page}&limit=${limit}`;
        

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch customers: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Update state with paginated data
        setPaginatedCustomers(data.customers || []);
        setTotalCustomers(data.pagination?.total || 0);
        setTotalCustomerPages(data.pagination?.pages || 0);

        // Log enhanced opportunities for debugging
        if (data.customers && data.customers.length > 0) {
          data.customers.forEach((customer, index) => {
            if (customer.enhanced_opportunities && customer.enhanced_opportunities.length > 0) {
              
            }
          });
        }
      } catch (error) {
        logger.error('[Pagination] Error fetching customers:', error);
        setCustomersError(error.message);
        toast({
          title: "Error loading customers",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setCustomersLoading(false);
      }
    },
    [currentClusterId, customersUrl, token]
  );

  // Memoize the events generated from the potentially modified schedule data
  // Ensures this hook is called at the top level
  const modifiedCalendarEvents = React.useMemo(() => {
    logger.group('🗺️ Calendar Events Generation');
    
    
    const events = transformScheduleToEvents(modifiedScheduleData || []);
    debugCalendarEvents(events);
    
    // Ensure all events have required properties
    const validEvents = events.filter(event => {
      if (!event.title) {
        
        return false;
      }
      if (!event.start || !event.end) {
        
        return false;
      }
      return true;
    });
    
    
    logger.groupEnd();
    
    return validEvents;
  }, [modifiedScheduleData]);

  // Use useMemo for time settings (already okay as it doesn't depend on changing state)
  const timeSettings = useMemo(() => {
    const minTime = new Date();
    minTime.setHours(8, 0, 0); // 8:00 AM
    const maxTime = new Date();
    maxTime.setHours(18, 0, 0); // 6:00 PM (18:00)
    return { minTime, maxTime }; // Ensure we return maxTime (not maxDate)
  }, []);

  // Calculate derived dates and scroll time (handle potentially null modifiedCalendarEvents)
  // Ensures this hook is called at the top level
  const { minDate, maxDate, scrollToTimeValue } = useMemo(() => {
    const events = modifiedCalendarEvents || []; // Use empty array if null
    if (events.length === 0) {
      const today = new Date();
      today.setHours(8, 0, 0, 0); // Default scroll to 8 AM
      return { minDate: today, maxDate: today, scrollToTimeValue: today };
    }
    // Safely handle potential invalid dates
    let min, max, earliestTime;
    try {
      min = new Date(events[0].start);
      max = new Date(events[0].end);
      earliestTime = new Date(events[0].start);
      
      // Check if dates are valid
      if (isNaN(min.getTime()) || isNaN(max.getTime()) || isNaN(earliestTime.getTime())) {
        console.error('Invalid date in first event:', events[0]);
        const today = new Date();
        today.setHours(8, 0, 0, 0);
        return { minDate: today, maxDate: today, scrollToTimeValue: today };
      }
    } catch (error) {
      console.error('Error parsing event dates:', error);
      const today = new Date();
      today.setHours(8, 0, 0, 0);
      return { minDate: today, maxDate: today, scrollToTimeValue: today };
    }

    events.forEach((event) => {
      if (event.start < min) min = event.start;
      if (event.end > max) max = event.end;
      const eventTime = event.start.getHours() * 60 + event.start.getMinutes();
      const earliestDateTime =
        earliestTime.getHours() * 60 + earliestTime.getMinutes();
      if (eventTime < earliestDateTime) {
        earliestTime = event.start;
      }
    });

    const scrollTarget = new Date(earliestTime);
    scrollTarget.setMinutes(0, 0, 0); // Scroll to the beginning of the hour

    return {
      minDate: min,
      maxDate: addDays(max, 1),
      scrollToTimeValue: scrollTarget,
    };
  }, [modifiedCalendarEvents]); // Depends only on events now

  // Calculate directions request (handle potentially null clusterData)
  // This needs derived `customers`, so it's defined after clusterData is potentially set
  // Note: customers are now loaded via pagination, not from clusterData.itinerary.customers
  
  // Sort customers based on calendar event order
  const sortCustomersByScheduleOrder = useCallback((customers, calendarEvents) => {
    if (!customers || !calendarEvents || customers.length === 0 || calendarEvents.length === 0) {
      return customers;
    }

    // Create a map of customer_id to their earliest event time and order
    const customerEventOrder = new Map();
    
    calendarEvents.forEach((event, index) => {
      const customerId = event.resource?.customer_id;
      if (customerId && event.start) {
        const eventTime = new Date(event.start).getTime();
        
        if (!customerEventOrder.has(customerId) || customerEventOrder.get(customerId).time > eventTime) {
          customerEventOrder.set(customerId, {
            time: eventTime,
            index: index
          });
        }
      }
    });

    // Sort customers based on their event order
    return [...customers].sort((a, b) => {
      const customerIdA = a.customer_id || a.id;
      const customerIdB = b.customer_id || b.id;
      
      const orderA = customerEventOrder.get(customerIdA);
      const orderB = customerEventOrder.get(customerIdB);
      
      // If both customers have events, sort by event time then by event index
      if (orderA && orderB) {
        if (orderA.time !== orderB.time) {
          return orderA.time - orderB.time;
        }
        return orderA.index - orderB.index;
      }
      
      // Customers with events come first
      if (orderA && !orderB) return -1;
      if (!orderA && orderB) return 1;
      
      // If neither has events, maintain original order
      return 0;
    });
  }, []);

  // Use paginated customers for the stops tab, sorted by schedule order
  const enhancedCustomers = useMemo(() => {
    return sortCustomersByScheduleOrder(paginatedCustomers, modifiedCalendarEvents);
  }, [paginatedCustomers, modifiedCalendarEvents, sortCustomersByScheduleOrder]);
  
  // Disabled bulk contacts fetching to prevent performance issues in production
  // Contact counts will be fetched on-demand when needed
  const contactsMap = new Map();
  const contactsLoading = false;
  
  // Convert contacts map to contact counts
  const customerContactCounts = useMemo(() => {
    return {};
  }, []);
  
  // Function to load all customers for map view
  const loadAllCustomersForMap = useCallback(async () => {
    if (!currentClusterId || !customersUrl || customersCount === 0) {
      
      return [];
    }

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const baseUrl = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';
      const limit = 50; // Larger limit for bulk loading
      const totalPages = Math.ceil(customersCount / limit);
      
      
      
      // Create array of page numbers
      const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
      
      // Fetch pages in batches to avoid overwhelming the browser
      const batchSize = 3; // Fetch 3 pages at a time
      const results = [];
      
      for (let i = 0; i < pageNumbers.length; i += batchSize) {
        const batch = pageNumbers.slice(i, i + batchSize);
        const batchPromises = batch.map(page => 
          fetch(`${baseUrl}${customersUrl}?page=${page}&limit=${limit}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }).then(res => res.json())
        );
        
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }
      
      // Combine all customers
      const allCustomers = results.flatMap(result => result.customers || []);
      
      
      
      return allCustomers;
    } catch (error) {
      logger.error('[Map] Error loading all customers:', error);
      toast({
        title: "Error loading map data",
        description: "Failed to load all customer locations",
        variant: "destructive"
      });
      return [];
    }
  }, [currentClusterId, customersUrl, customersCount, token]);

  // State for all customers (for map view)
  const [allCustomersForMap, setAllCustomersForMap] = useState([]);
  const [mapDataLoading, setMapDataLoading] = useState(false);

  // Load all customers when switching to map tab
  useEffect(() => {
    if (activeTab === 'map' && customersCount > 0 && allCustomersForMap.length === 0) {
      setMapDataLoading(true);
      loadAllCustomersForMap().then(customers => {
        setAllCustomersForMap(customers);
        setMapDataLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, customersCount]);

  // Create directionsRequest for route optimization similar to BuildItineraryPage
  const directionsRequest = useMemo(() => {
    // Use all customers for map view
    const customersForRoute = activeTab === 'map' ? allCustomersForMap : enhancedCustomers;
    if (!customersForRoute || customersForRoute.length < 1) return null;
    const waypoints = customersForRoute
      .map((c) => {
        // Handle both formats: separate lat/lng fields or location string
        if (c.lat !== undefined && c.lng !== undefined) {
          return { location: `${c.lat},${c.lng}` };
        } else if (c.location) {
          return { location: c.location };
        }
        return null;
      })
      .filter((wp) => wp && wp.location);
    if (waypoints.length < 1) return null;
    
    // Create optimal route connecting all customer stops in sequence
    if (waypoints.length === 1) {
      return null; // Single customer - no route needed
    } else if (waypoints.length === 2) {
      return {
        origin: waypoints[0],
        destination: waypoints[1],
        waypoints: [],
        optimizeWaypoints: false,
        travelMode: "DRIVING",
        routeType: "simple"
      };
    } else {
      return {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1], 
        waypoints: waypoints.slice(1, -1),
        optimizeWaypoints: true,
        travelMode: "DRIVING",
        routeType: "optimized"
      };
    }
  }, [enhancedCustomers, allCustomersForMap, activeTab]);

  // Reset pagination when switching to stops tab
  useEffect(() => {
    if (activeTab === 'stops' || activeTab === 'add-stops') {
      setCurrentPage(1);
      // Fetch first page of customers when switching to stops or add-stops tab
      if (clusterData && customersCount > 0) {
        fetchPaginatedCustomers(1, stopsPerPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, clusterData, customersCount]);

  // Fetch nearby opportunities when Add Stops tab is accessed
  const fetchNearbyOpportunities = useCallback(async () => {
    if (!enhancedCustomers || enhancedCustomers.length === 0 || !currentClusterId) return;
    
    setOpportunitiesLoading(true);
    setOpportunitiesError(null);
    
    try {
      // Get existing customer codes to exclude them from results
      const existingCustomerCodes = enhancedCustomers.map(c => 
        c.CustomerCode || c.customer_code || c.id
      ).filter(Boolean);
      
      
      
      // Build query parameters for /customers/near endpoint
      const queryParams = new URLSearchParams();
      
      // Add itinerary_id parameter (primary way to find nearby customers)
      queryParams.append('itinerary_id', currentClusterId.toString());
      
      // Add existing customer codes to exclude them from results
      if (existingCustomerCodes.length > 0) {
        queryParams.append('exclude', existingCustomerCodes.join(','));
      }
      
      // Add radius_miles parameter (default 50 miles as specified)
      queryParams.append('radius_miles', '50');
      
      const queryString = queryParams.toString();
      const endpoint = `/customers/near?${queryString}`;
      
      
      
      // Fetch nearby opportunities using the dedicated endpoint
      const response = await axios.get(endpoint);
      let nearbyOpportunities = response.data || [];
      
      // Add unique ID for each opportunity
      const processedOpportunities = nearbyOpportunities.map((opp, index) => ({
        ...opp,
        _uniqueId: opp.customercode || opp.CustomerCode || opp.id || `opp_${index}`,
        customername: opp.customername || opp.CustomerName || 'Unnamed Customer',
        location: opp.location || (opp.lat && opp.lng ? `${opp.lat},${opp.lng}` : null),
        formatted_address: [opp.streetaddress1, opp.city, opp.state, opp.zip].filter(Boolean).join(', ')
      }));
      
      
      setAvailableOpportunities(processedOpportunities);
      
    } catch (error) {
      console.error('Error fetching nearby opportunities:', error);
      setOpportunitiesError('Failed to load nearby opportunities. Please try again.');
    } finally {
      setOpportunitiesLoading(false);
    }
  }, [enhancedCustomers, currentClusterId]);

  // Fetch customer details for main dialog
  const fetchCustomerDetailsForDialog = useCallback(async (customerCode) => {
    if (!customerCode || customerDetailsData[customerCode] || customerDetailsLoading[customerCode]) {
      return; // Already have data or loading
    }

    setCustomerDetailsLoading(prev => ({ ...prev, [customerCode]: true }));
    
    try {
      const response = await getCustomerDetails(customerCode, token);
      setCustomerDetailsData(prev => ({ ...prev, [customerCode]: response }));
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast({
        title: "Failed to load customer details",
        description: "Please try again later",
        variant: "destructive"
      });
    } finally {
      setCustomerDetailsLoading(prev => ({ ...prev, [customerCode]: false }));
    }
  }, [customerDetailsData, customerDetailsLoading, token]);

  // Fetch customer opportunities
  const fetchCustomerOpportunitiesForDialog = useCallback(async (customerCode) => {
    if (!customerCode || customerOpportunitiesData[customerCode] || customerOpportunitiesLoading[customerCode]) {
      return; // Already have data or loading
    }

    setCustomerOpportunitiesLoading(prev => ({ ...prev, [customerCode]: true }));
    
    try {
      const response = await getCustomerOpportunities(customerCode, token);
      setCustomerOpportunitiesData(prev => ({ ...prev, [customerCode]: response }));
    } catch (error) {
      console.error('Error fetching customer opportunities:', error);
      // Don't show toast for opportunities - they're not critical for display
    } finally {
      setCustomerOpportunitiesLoading(prev => ({ ...prev, [customerCode]: false }));
    }
  }, [customerOpportunitiesData, customerOpportunitiesLoading, token]);

  // Remove automatic fetching - only fetch when dialog is opened

  // Filter opportunities based on search query
  const filteredOpportunities = useMemo(() => {
    if (!opportunitySearchQuery) return availableOpportunities;
    
    const query = opportunitySearchQuery.toLowerCase();
    return availableOpportunities.filter(opp => 
      (opp.customername && opp.customername.toLowerCase().includes(query)) ||
      (opp.customercode && opp.customercode.toString().toLowerCase().includes(query)) ||
      (opp.city && opp.city.toLowerCase().includes(query)) ||
      (opp.state && opp.state.toLowerCase().includes(query))
    );
  }, [availableOpportunities, opportunitySearchQuery]);

  // Handle adding selected opportunities to the itinerary
  const handleAddOpportunities = useCallback(async () => {
    if (selectedOpportunities.size === 0) {
      toast({
        title: "No opportunities selected",
        description: "Please select at least one opportunity to add",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const opportunitiesToAdd = Array.from(selectedOpportunities)
        .map(oppId => availableOpportunities.find(opp => opp._uniqueId === oppId))
        .filter(Boolean);
      
      if (opportunitiesToAdd.length === 0) return;
      
      // Convert opportunities to the customer format expected by the itinerary
      const newCustomers = opportunitiesToAdd.map(opp => ({
        id: opp._uniqueId,
        customer_id: opp.customercode || opp._uniqueId,
        CustomerCode: opp.customercode || opp.CustomerCode,
        name: opp.customername,
        customername: opp.customername,
        address: opp.formatted_address || opp.streetaddress1,
        city: opp.city,
        state: opp.state,
        zipcode: opp.zip,
        location: opp.location,
        status: 'PENDING', // Default status for new stops
        primary_focus: 'New opportunity'
      }));
      
      // Create calendar events for new opportunities
      const existingSchedule = modifiedScheduleData || clusterData?.itinerary?.schedule || [];
      
      // Group new opportunities by day
      const opportunitiesByDay = new Map();
      
      opportunitiesToAdd.forEach((opp) => {
        // Calculate start time - add after the last existing event or start at 9 AM
        let startTime;
        
        // Find the last stop across all days
        let lastStopTime = null;
        if (existingSchedule.length > 0) {
          // Look through all days to find the last stop
          for (let i = existingSchedule.length - 1; i >= 0; i--) {
            const daySchedule = existingSchedule[i];
            if (daySchedule.agenda && daySchedule.agenda.length > 0) {
              const lastStop = daySchedule.agenda[daySchedule.agenda.length - 1];
              if (lastStop.time || lastStop.time_slot) {
                // Parse the time to get the end time
                const timeStr = lastStop.time || lastStop.time_slot;
                const dayStr = daySchedule.day || daySchedule.date;
                if (dayStr) {
                  // For time_slot like "9:00 AM - 10:00 AM", extract end time
                  let endTimeStr = timeStr;
                  if (timeStr.includes(' - ')) {
                    endTimeStr = timeStr.split(' - ')[1];
                  }
                  // Convert to 24-hour format if needed
                  const hour24 = convertTo24Hour(endTimeStr);
                  if (hour24) {
                    lastStopTime = new Date(`${dayStr}T${hour24}:00`);
                    // Add visit duration if specified
                    const visitDuration = lastStop.visit_duration || lastStop.estimated_onsite || '1 hour';
                    const durationMinutes = parseDuration(visitDuration);
                    lastStopTime = new Date(lastStopTime.getTime() + (durationMinutes * 60 * 1000));
                  }
                }
                break;
              }
            }
          }
        }
        
        if (lastStopTime && !isNaN(lastStopTime.getTime())) {
          // Add 30 minutes gap between events
          startTime = new Date(lastStopTime.getTime() + (30 * 60 * 1000));
        } else {
          // Start at 9 AM today
          startTime = new Date();
          startTime.setHours(9, 0, 0, 0);
        }
        
        // Each appointment is 1 hour long
        const endTime = new Date(startTime.getTime() + (60 * 60 * 1000));
        
        // Validate the dates
        if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
          startTime = new Date();
          startTime.setHours(9, 0, 0, 0);
          endTime = new Date(startTime.getTime() + (60 * 60 * 1000));
        }
        
        // Format day as YYYY-MM-DD
        const dayStr = startTime.toISOString().split('T')[0];
        
        // Format time as "HH:MM AM/PM - HH:MM AM/PM"
        const startTimeStr = formatTime12Hour(startTime);
        const endTimeStr = formatTime12Hour(endTime);
        const timeSlot = `${startTimeStr} - ${endTimeStr}`;
        
        // Create agenda item
        const agendaItem = {
          type: 'customer',
          time: formatTime24Hour(startTime), // 24-hour format for sorting
          time_slot: timeSlot, // 12-hour format for display
          customer_name: opp.customername,
          customer_id: opp.customercode || opp._uniqueId,
          stop_id: opp._uniqueId,
          address: opp.formatted_address || opp.streetaddress1,
          city: opp.city,
          state: opp.state,
          lat: opp.location?.lat || opp.latitude,
          lng: opp.location?.lng || opp.longitude,
          visit_duration: '1 hour',
          notes: 'Added from nearby opportunities',
          status: 'PENDING',
          opportunities: []
        };
        
        // Add to the appropriate day
        if (!opportunitiesByDay.has(dayStr)) {
          opportunitiesByDay.set(dayStr, []);
        }
        opportunitiesByDay.get(dayStr).push(agendaItem);
      });
      
      // Helper functions for time formatting
      function convertTo24Hour(timeStr) {
        const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!match) return null;
        let hour = parseInt(match[1]);
        const minute = match[2];
        const period = match[3].toUpperCase();
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        return `${hour.toString().padStart(2, '0')}:${minute}`;
      }
      
      function formatTime24Hour(date) {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      
      function formatTime12Hour(date) {
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const period = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        return `${hours}:${minutes} ${period}`;
      }
      
      function parseDuration(duration) {
        const match = duration.match(/(\d+(?:\.\d+)?)\s*(hour|hr|minute|min)/i);
        if (!match) return 60; // Default 1 hour
        const value = parseFloat(match[1]);
        const unit = match[2].toLowerCase();
        return unit.startsWith('hour') || unit.startsWith('hr') ? value * 60 : value;
      }
      
      // Merge with existing schedule
      const updatedSchedule = [...existingSchedule];
      
      // Add new stops to existing days or create new days
      opportunitiesByDay.forEach((agendaItems, dayStr) => {
        const existingDayIndex = updatedSchedule.findIndex(d => d.day === dayStr || d.date === dayStr);
        
        if (existingDayIndex >= 0) {
          // Add to existing day
          const existingDay = updatedSchedule[existingDayIndex];
          if (!existingDay.agenda) {
            existingDay.agenda = [];
          }
          existingDay.agenda.push(...agendaItems);
          // Sort agenda items by time
          existingDay.agenda.sort((a, b) => {
            const timeA = a.time || '00:00';
            const timeB = b.time || '00:00';
            return timeA.localeCompare(timeB);
          });
        } else {
          // Create new day entry
          updatedSchedule.push({
            day: dayStr,
            date: dayStr,
            agenda: agendaItems
          });
          // Sort days by date
          updatedSchedule.sort((a, b) => {
            const dateA = a.day || a.date;
            const dateB = b.day || b.date;
            return dateA.localeCompare(dateB);
          });
        }
      });
      
      // Update both customers and schedule
      // Note: We need to refetch all customers after adding new ones
      const updatedCustomers = [...newCustomers];
      
      // Update customer count
      const newCustomerCount = (clusterData?.itinerary?.customers_count || 0) + newCustomers.length;
      
      const updatedClusterData = {
        ...clusterData,
        itinerary: {
          ...clusterData.itinerary,
          customers: updatedCustomers,
          customers_count: newCustomerCount,
          schedule: updatedSchedule
        }
      };
      
      setClusterData(updatedClusterData);
      setModifiedScheduleData(updatedSchedule);
      setHasUnsavedChanges(true); // Mark as having unsaved changes
      
      // Save new customers to backend using the correct add-stops API
      try {
        console.log('Saving new customers to backend via add-stops API...');
        console.log('New customers being added:', newCustomers);
        
        const baseUrl = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';
        const response = await fetch(`${baseUrl}/schedules/${currentClusterId}/add-stops`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer_stops: newCustomers, // Only the new customers being added
            schedule: updatedSchedule     // Complete schedule including all stops
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to add stops: ${response.statusText}`);
        }
        
        console.log('Successfully saved new stops to backend');
        // No need to mark as unsaved since we've saved to backend
        setHasUnsavedChanges(false);
        
      } catch (saveError) {
        console.error('Error saving new stops to backend:', saveError);
        // Mark as unsaved if save failed
        setHasUnsavedChanges(true);
        toast({
          title: "Warning: Changes not saved",
          description: "New stops were added locally but couldn't be saved to backend. Please try Save Changes.",
          variant: "destructive"
        });
      }
      
      // Clear selections
      setSelectedOpportunities(new Set());
      
      // Show success message
      toast({
        title: "Opportunities added successfully",
        description: `Added ${newCustomers.length} new stop${newCustomers.length !== 1 ? 's' : ''} to the itinerary`
      });
      
      // Switch back to stops tab to show the updated list
      setActiveTab('stops');
      
      // Immediately update pagination state with new customers
      const currentPaginatedCustomers = paginatedCustomers || [];
      const updatedPaginatedCustomers = [...currentPaginatedCustomers, ...newCustomers];
      
      // Update pagination state immediately
      setPaginatedCustomers(updatedPaginatedCustomers);
      const newTotalCustomers = (totalCustomers || 0) + newCustomers.length;
      setTotalCustomers(newTotalCustomers);
      setTotalCustomerPages(Math.ceil(newTotalCustomers / stopsPerPage));
      setCurrentPage(1); // Reset to first page to show new customers
      
      // Customer count already updated in clusterData above
      
      // Refetch pagination to reflect the saved changes from backend
      if (currentClusterId && customersUrl) {
        setTimeout(() => {
          fetchPaginatedCustomers(1, stopsPerPage);
        }, 1000); // Shorter delay since we're using the correct API
      }
      
    } catch (error) {
      console.error('Error adding opportunities:', error);
      toast({
        title: "Failed to add opportunities",
        description: "An error occurred while adding the selected opportunities",
        variant: "destructive"
      });
    }
  }, [selectedOpportunities, availableOpportunities, clusterData, modifiedScheduleData, setActiveTab, currentClusterId, customersUrl, customersCount, fetchPaginatedCustomers, currentPage, stopsPerPage, setCurrentPage, paginatedCustomers, token]);

  // Automatically fetch opportunities when Add Stops tab is accessed
  useEffect(() => {
    if (activeTab === 'add-stops' && availableOpportunities.length === 0 && !opportunitiesLoading) {
      fetchNearbyOpportunities();
    }
  }, [activeTab, availableOpportunities.length, opportunitiesLoading, fetchNearbyOpportunities]);

  // Fetch schedule data directly from API if needed (for RSMs or forced fetch)
  // MODIFIED: Now returns the fetched data on success
  const fetchScheduleFromApi = useCallback(
    async (scheduleId) => {
      if (!scheduleId) return null; // Return null if no ID

      setLoading(true);
      setError("");
      let fetchedData = null; // Variable to store fetched data
      
      // Debug logging for schedule ID format
      
      
      try {
        const response = await axios.get(`/schedules/${scheduleId}`);
        
        if (response.data) {
          fetchedData = response.data; // Store the data
          

          // Debug the raw API response structure
          
          
          // Check if schedule has day/agenda structure
          if (fetchedData.itinerary?.schedule && Array.isArray(fetchedData.itinerary.schedule)) {
            
            
            // Check if it's the enhanced schema with day objects
            if (fetchedData.itinerary.schedule[0]?.agenda) {
              
              fetchedData.itinerary.schedule.forEach((day, dayIdx) => {
                
                if (day.agenda && day.agenda.length > 0) {
                  
                  if (day.agenda[0].enhanced_opportunities) {
                    
                  }
                }
              });
            }
          }

          // Reset calendar initialization first to force recalculation
          setCalendarInitialized(false);

          // Set both data states in a single update cycle
          setClusterData(fetchedData);
          // Debug the fetched data
          debugScheduleData('Fetched Data from API', fetchedData);
          
          // Use adapter to handle both v1 and v2 formats
          const adaptedSchedule = adaptScheduleToCalendarFormat(fetchedData);
          debugScheduleData('Adapted Schedule from API', adaptedSchedule);
          
          setModifiedScheduleData(adaptedSchedule); // Initialize modified state
          setHasUnsavedChanges(false); // Reset save status

          // Log fetch response for debugging
          // logger.log( // Disabled for brevity
          //   "Fetched schedule data from API:",
          //   JSON.stringify(fetchedData.cluster),
          // );
          // logger.log( // Disabled for brevity
          //   "Schedule array from API:",
          //   JSON.stringify(fetchedData.itinerary?.schedule || [], null, 2),
          // );
        } else {
          setError("No data returned from API");
          setClusterData(null);
          setModifiedScheduleData(null); // Clear modified data
        }
      } catch (err) {
        console.error("Error fetching schedule:", err);
        console.error("Request URL:", `/schedules/${scheduleId}`);
        console.error("Response status:", err.response?.status);
        console.error("Response data:", err.response?.data);
        
        let errorMessage = "Failed to fetch schedule data";
        if (err.response?.status === 422) {
          errorMessage = `Schedule ID "${scheduleId}" is not in the correct format or does not exist. Status: 422 - Unprocessable Entity`;
          
        } else {
          errorMessage = err.response?.data?.error || err.message || errorMessage;
        }
        
        setError(errorMessage);
        setClusterData(null);
        setModifiedScheduleData(null); // Clear modified data
      } finally {
        setLoading(false);
      }
      return fetchedData; // Return the fetched data (or null on failure/no data)
    },
    [setCalendarInitialized],
  ); // Add setCalendarInitialized to dependencies

  // Initial data loading effect - REFINED LOGIC
  useEffect(() => {
    const idToFind = currentClusterId;
    const isFirstLoadOrIdChanged = prevClusterIdRef.current !== idToFind;

    // logger.log( // Disabled for brevity
    //   `[Initial Load Effect] Running. Current ID: ${idToFind}, Prev ID: ${prevClusterIdRef.current}, First Load/ID Changed: ${isFirstLoadOrIdChanged}`,
    // );

    if (!idToFind) {
      
      setClusterData(null);
      setModifiedScheduleData(null);
      setHasUnsavedChanges(false);
      setLoading(false);
      setError("No schedule ID specified.");
      prevClusterIdRef.current = idToFind; // Update ref
      return;
    }

    // --- Conditions to FORCE API Fetch ---
    // 1. If it's the first load for this specific component instance OR the clusterId has changed.
    // 2. If the user is an RSM.
    // 3. If context data is missing or empty.
    const shouldForceFetch =
      isFirstLoadOrIdChanged ||
      isRSM ||
      !Array.isArray(data) ||
      data.length === 0;

    if (shouldForceFetch) {
      // logger.log( // Disabled for brevity
      //   `[Initial Load Effect] Condition met: Forcing API fetch for ID ${idToFind}. Reason: ${isFirstLoadOrIdChanged ? "FirstLoad/IDChange" : ""}${isRSM ? " UserIsRSM" : ""}${!Array.isArray(data) || data.length === 0 ? " ContextEmpty" : ""}`,
      // );
      fetchScheduleFromApi(idToFind);
    } else {
      // --- Use Context Data (Only if not forcing fetch) ---
      // logger.log( // Disabled for brevity
      //   `[Initial Load Effect] Condition met: Attempting to use context data for ID ${idToFind}`,
      // );
      const foundData = data.find(
        (item) => String(item.cluster) === String(idToFind),
      );

      if (foundData) {
        // Found in context, use it
        setClusterData(foundData);
        // Debug the found data
        debugScheduleData('Found Data from Context', foundData);
        
        // Use adapter to handle both v1 and v2 formats
        const adaptedSchedule = adaptScheduleToCalendarFormat(foundData);
        debugScheduleData('Adapted Schedule', adaptedSchedule);
        
        setModifiedScheduleData(adaptedSchedule);
        setHasUnsavedChanges(false);
        setCalendarInitialized(false); // Ensure calendar re-initializes with context data
        setLoading(false); // Stop loading
        // logger.log( // Disabled for brevity
        //   "[Initial Load Effect] Found data in context:",
        //   JSON.stringify(foundData.cluster),
        // );
      } else {
        // Not found in context, fetch from API
        fetchScheduleFromApi(idToFind);
      }
    }

    // Update the ref *after* the logic has run for the current render
    prevClusterIdRef.current = idToFind;
  }, [
    currentClusterId, // Trigger effect when ID changes
    isRSM,
    data, // Trigger effect if context data loads/changes
    fetchScheduleFromApi, // Need the fetch function
    // appLoading - removed, local loading state is managed within the effect/fetch function
    // setData - context setter, not needed as dependency for reading
  ]); // Dependencies refined

  // Add effect to fetch churn data when customer data is loaded
  useEffect(() => {
    const fetchChurnDataForCluster = async () => {
      // Use paginated customers instead of clusterData
      if (paginatedCustomers && paginatedCustomers.length > 0) {
        // Extract customer codes from the customer data
        const customerCodes = paginatedCustomers
          .map((customer) => {
            // Try to get code from different possible field names
            let code =
              customer.CustomerCode || 
              customer.customer_code || 
              customer.customer_id ||
              customer.id;

            // Ensure the code is a string
            if (code !== undefined && code !== null) {
              code = String(code);
            }
            
            return code;
          })
          .filter((code) => code); // Filter out undefined or null

        if (customerCodes.length > 0) {
          await fetchChurnData(customerCodes);
        } else {
          
        }
      }
    };

    fetchChurnDataForCluster();
  }, [paginatedCustomers, fetchChurnData]);

  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary"; // Changed from yellow to secondary for better contrast
      case "low":
        return "outline"; // Changed from default (dark) to outline
      default:
        return "outline";
    }
  };

  const toggleFullScreenMap = () => {
    setIsMapFullScreen(!isMapFullScreen);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 150);
  };

  // --- Data Preparation ---
  // const customers = clusterData?.itinerary?.customers || []; // Moved up
  // const scheduleData = clusterData?.itinerary?.schedule || []; // Original data, not used directly for events anymore
  const reasoning = clusterData?.metadata?.brainstorm || null;
  const itineraryName =
    clusterData?.metadata?.itinerary_name ||
    (clusterData ? `Schedule ${clusterData.cluster}` : "Loading Schedule...");
  const itineraryPriority = clusterData?.priority || "N/A";

  // Reset navigation when cluster data changes or events change
  useEffect(() => {
    // When we have data but it's not initialized yet
    if (
      clusterData &&
      modifiedCalendarEvents.length > 0 &&
      !calendarInitialized
    ) {
      // logger.log( // Disabled for brevity
      //   "[Calendar Init Effect] Initializing calendar with first event date",
      //   modifiedCalendarEvents[0],
      // );
      const firstEventDate = modifiedCalendarEvents[0].start;
      // Ensure the date is valid before setting
      if (isValidDate(firstEventDate)) {
        setCurrentDate(firstEventDate); // Set to the first event date of the new schedule
      } else {

        setCurrentDate(new Date());
      }
      setCalendarInitialized(true); // Ensure it's marked initialized
    }
    // When data is missing or empty
    else if (!clusterData || modifiedCalendarEvents.length === 0) {
      // logger.log( // Disabled for brevity
      //   "[Calendar Init Effect] No calendar events available, resetting to current date",
      // );
      setCurrentDate(new Date()); // Fallback to today if no events
      setCalendarInitialized(false); // Reset initialization if data becomes unavailable
    }
    // This effect should run when clusterData changes or events are recalculated
  }, [clusterData, modifiedCalendarEvents, calendarInitialized]);

  // Simplified handleNavigate for debugging
  const handleNavigate = (newDate, view, action) => {
    
    
    let targetDate = newDate;

    // Basic validation
    if (!isValidDate(targetDate)) {
      
      targetDate = new Date();
    }

    // Handle TODAY action
    if (action === "TODAY") {
      if (modifiedCalendarEvents && modifiedCalendarEvents.length > 0) {
        const firstEventDate = modifiedCalendarEvents[0].start;
        if (isValidDate(firstEventDate)) {
          targetDate = firstEventDate;
          
        } else {
          targetDate = new Date();
          
        }
      } else {
        targetDate = new Date();
        
      }
    }

    
    setCurrentDate(targetDate);
  };

  // --- Drag and Drop Handlers ---

  // Add state for tracking drag operations
  const [isDragging, setIsDragging] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [autoNavTimeout, setAutoNavTimeout] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null); // For cross-week drag persistence
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);

  // Cleanup effect for drag operations
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts and listeners on unmount
      if (autoNavTimeout) {
        clearTimeout(autoNavTimeout);
      }
      if (window.dragMouseCleanup) {
        window.dragMouseCleanup();
        delete window.dragMouseCleanup;
      }
      // Remove any lingering drag messages
      const dragMessage = document.getElementById('drag-message');
      if (dragMessage) {
        document.body.removeChild(dragMessage);
      }
    };
  }, [autoNavTimeout]);

  // Keyboard navigation effect for arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle arrow keys and prevent default browser navigation
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Don't interfere if user is typing in an input field (unless dragging)
        if (!isDragging && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable)) {
          return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        
        
        
        // During drag, we want the same behavior as the edge detection
        if (isDragging) {
          if (event.key === 'ArrowLeft') {
            
            setIsAutoNavigating(true);
            setPendingDrop({
              event: draggedEvent,
              direction: 'prev'
            });
            const prevWeek = startOfWeek(addDays(currentDate, -7));
            handleNavigate(prevWeek, isMobile ? 'day' : 'week', 'PREV');
          } else if (event.key === 'ArrowRight') {
            
            setIsAutoNavigating(true);
            setPendingDrop({
              event: draggedEvent,
              direction: 'next'
            });
            const nextWeek = startOfWeek(addDays(currentDate, 7));
            handleNavigate(nextWeek, isMobile ? 'day' : 'week', 'NEXT');
          }
        } else {
          // Normal navigation when not dragging
          if (event.key === 'ArrowLeft') {
            handleNavigate(currentDate, isMobile ? 'day' : 'week', 'PREV');
          } else if (event.key === 'ArrowRight') {
            handleNavigate(currentDate, isMobile ? 'day' : 'week', 'NEXT');
          }
        }
      }
    };

    // Add event listener to the document with capture phase for drag operations
    // Use window instead of document for better event capture during drag
    window.addEventListener('keydown', handleKeyDown, true);
    
    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [currentDate, isMobile, isDragging, draggedEvent]);

  // We'll move this effect after handleEventDrop is defined

  // Simplified drag start handler
  const handleDragStart = useCallback((args) => {
    
    setIsDragging(true);
    setDraggedEvent(args.event);
    
    // Add visual feedback
    const dragMessage = document.createElement('div');
    dragMessage.id = 'drag-message';
    dragMessage.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: hsl(var(--primary));
      color: hsl(var(--primary-foreground));
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      z-index: 10000;
      pointer-events: none;
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    `;
    document.body.appendChild(dragMessage);

    // Simplified mouse move listener for edge detection
    const handleMouseMove = (e) => {
      const calendarContainer = document.querySelector('.rbc-calendar');
      
      if (calendarContainer) {
        const rect = calendarContainer.getBoundingClientRect();
        const edgeThreshold = 50;
        const autoNavDelay = 1000;
        
        // Clear existing timeout
        if (autoNavTimeout) {
          clearTimeout(autoNavTimeout);
          setAutoNavTimeout(null);
        }
        
        // Check if mouse is within calendar area vertically
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          // Check for right edge (next week)
          if (e.clientX > rect.right - edgeThreshold) {
            const timeout = setTimeout(() => {
              
              setIsAutoNavigating(true);
              setPendingDrop({
                event: args.event, // Use the original event from args
                direction: 'next'
              });
              const nextWeek = startOfWeek(addDays(currentDate, 7));
              handleNavigate(nextWeek, 'week', 'NEXT');
            }, autoNavDelay);
            setAutoNavTimeout(timeout);
          }
          // Check for left edge (previous week)
          else if (e.clientX < rect.left + edgeThreshold) {
            const timeout = setTimeout(() => {
              
              setIsAutoNavigating(true);
              setPendingDrop({
                event: args.event, // Use the original event from args
                direction: 'prev'
              });
              const prevWeek = startOfWeek(addDays(currentDate, -7));
              handleNavigate(prevWeek, 'week', 'PREV');
            }, autoNavDelay);
            setAutoNavTimeout(timeout);
          }
        }
      }
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    
    // Store cleanup function
    window.dragMouseCleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (autoNavTimeout) {
        clearTimeout(autoNavTimeout);
        setAutoNavTimeout(null);
      }
    };
  }, [currentDate, handleNavigate, autoNavTimeout]);

  // Enhanced drag end handler
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedEvent(null);
    setPendingDrop(null); // Clear pending drop state
    setIsAutoNavigating(false); // Reset auto-navigation state
    
    // Remove visual feedback
    const dragMessage = document.getElementById('drag-message');
    if (dragMessage) {
      document.body.removeChild(dragMessage);
    }
    
    // Cleanup mouse move listener and timeout
    if (window.dragMouseCleanup) {
      window.dragMouseCleanup();
      delete window.dragMouseCleanup;
    }
    
    // Clear any pending auto-navigation timeout
    if (autoNavTimeout) {
      clearTimeout(autoNavTimeout);
      setAutoNavTimeout(null);
    }
  }, [autoNavTimeout]);

  const handleEventDrop = useCallback(
    ({ event, start, end }) => {
      // Use the pending drop event if available (cross-week drag)
      const eventToMove = pendingDrop?.event || event;
      
      // Ensure drag end cleanup is called
      handleDragEnd();
      
      // Check if event, resource, start, and end are valid
      if (!eventToMove?.resource || !start || !end) {
        console.error("Invalid arguments received for handleEventDrop:", {
          event: eventToMove,
          start,
          end,
        });
        toast({
          title: "Update Error",
          description: "Could not move the event.",
          variant: "destructive",
        });
        return; // Stop execution if crucial data is missing
      }
      
      // Log cross-week drop detection
      if (pendingDrop) {
        
      }

      // Find the original day and item based on stored indices
      const { originalDayIndex, originalAgendaIndex } = eventToMove.resource;

      // Validate indices
      if (originalDayIndex === undefined || originalAgendaIndex === undefined) {
        console.error(
          "Missing original indices on event resource:",
          event.resource,
        );
        toast({
          title: "Update Error",
          description: "Event data is corrupted.",
          variant: "destructive",
        });
        return;
      }

      // Create a deep copy to avoid mutating state directly
      let nextSchedule;
      try {
        // Use structuredClone if available (modern browsers), fallback to JSON method
        if (typeof structuredClone !== 'undefined') {
          nextSchedule = structuredClone(modifiedScheduleData);
        } else {
          // Fallback for older browsers - but this might crash on Azure with large data
          nextSchedule = JSON.parse(JSON.stringify(modifiedScheduleData));
        }
      } catch (parseError) {
        console.error("Error cloning schedule data:", parseError);
        toast({
          title: "Update Error",
          description: "Could not process schedule data.",
          variant: "destructive",
        });
        return;
      }

      try {
        // Get the specific item being moved
        const movedItem =
          nextSchedule[originalDayIndex]?.agenda?.[originalAgendaIndex];
        if (!movedItem) {
          console.error(
            "Could not find moved item in schedule data at indices:",
            originalDayIndex,
            originalAgendaIndex,
          );
          toast({
            title: "Update Error",
            description: "Original event not found.",
            variant: "destructive",
          });
          return; // Exit if item not found
        }

        // Format the new start time (HH:MM)
        const newStartTime = format(start, "HH:mm");

        // Remove the item from its original position
        const originalAgenda = nextSchedule[originalDayIndex]?.agenda;
        if (originalAgenda) {
          originalAgenda.splice(originalAgendaIndex, 1);
          // If the original day becomes empty, consider removing it (optional)
          // if (originalAgenda.length === 0) {
          //   nextSchedule.splice(originalDayIndex, 1);
          // }
        } else {
          console.error("Original day or agenda not found during removal.");
          // Decide how to handle this - maybe revert? For now, log and proceed carefully.
        }

        // Find the target day based on the 'start' date of the drop
        const targetDayString = format(start, "yyyy-MM-dd");
        let targetDayIndex = nextSchedule.findIndex(
          (day) => day.day === targetDayString,
        );

        // If the target day doesn't exist, create it
        if (targetDayIndex === -1) {

          const newDayEntry = { day: targetDayString, agenda: [] };
          nextSchedule.push(newDayEntry);
          // Sort days after adding (important for consistent indexing if needed elsewhere)
          nextSchedule.sort((a, b) => {
            const dateA = safeParseISO(a.day + "T00:00:00");
            const dateB = safeParseISO(b.day + "T00:00:00");
            if (!dateA || !dateB) return 0; // Handle potential parse errors
            return dateA.getTime() - dateB.getTime();
          });
          // Find the index again after sorting
          targetDayIndex = nextSchedule.findIndex(
            (day) => day.day === targetDayString,
          );
          // Ensure targetDayIndex is valid after potential add/sort
          if (targetDayIndex === -1) {
            console.error(
              "Failed to find or create target day index after adding and sorting.",
            );
            toast({
              title: "Update Error",
              description: "Failed to place event in the new day.",
              variant: "destructive",
            });
            return;
          }
        }

        // Update the item's time
        const updatedItem = { ...movedItem, time: newStartTime };

        // Get the target day's agenda (ensure it exists)
        const targetAgenda = nextSchedule[targetDayIndex]?.agenda;
        if (!targetAgenda) {
          console.error("Target agenda not found at index:", targetDayIndex);
          toast({
            title: "Update Error",
            description: "Could not add event to the target day.",
            variant: "destructive",
          });
          return;
        }

        // Find the correct insertion point in the target day's agenda based on time
        let insertionIndex = targetAgenda.findIndex((item) => {
          // Compare times numerically HHMM
          return (
            parseInt(item.time.replace(":", ""), 10) >
            parseInt(newStartTime.replace(":", ""), 10)
          );
        });

        if (insertionIndex === -1) {
          insertionIndex = targetAgenda.length; // Insert at the end if it's the latest
        }

        // Insert the updated item into the target day's agenda
        targetAgenda.splice(insertionIndex, 0, updatedItem);

        // Update the state
        setModifiedScheduleData(nextSchedule);
        setHasUnsavedChanges(true);
        
        // Success notification with cross-week info
        const targetDateStr = format(start, "EEEE, MMMM d");
        const originalDateStr = format(new Date(eventToMove.start), "EEEE, MMMM d");
        const isCrossWeek = format(start, "ww-yyyy") !== format(new Date(eventToMove.start), "ww-yyyy") || !!pendingDrop;
        
        toast({
          title: isCrossWeek ? "Event Moved Across Weeks ✅" : "Schedule Updated ✅",
          description: isCrossWeek 
            ? `Moved from ${originalDateStr} to ${targetDateStr} at ${newStartTime}. Click 'Save Changes' to persist.`
            : `Event moved to ${newStartTime}. Click 'Save Changes' to persist.`,
        });
        //  // Disabled for brevity
      } catch (error) {
        console.error("Error handling event drop:", error);
        toast({
          title: "Error updating schedule",
          variant: "destructive",
          description: error.message,
        });
        // Optionally revert to previous state if needed
        // Fetching again might be safer than relying on potentially stale clusterData
        fetchScheduleFromApi(currentClusterId); // Revert on error by fetching fresh data
        setHasUnsavedChanges(false);
      }
    },
    [
      modifiedScheduleData,
      setModifiedScheduleData,
      setHasUnsavedChanges,
      fetchScheduleFromApi,
      currentClusterId,
      pendingDrop,
      handleDragEnd,
    ],
  ); // Dependencies

  // Effect to handle pending drop after auto-navigation (moved here to avoid circular dependency)
  useEffect(() => {
    if (pendingDrop && isAutoNavigating && !loading) {
      const timer = setTimeout(() => {
        
        
        // Restore drag visual feedback
        setIsDragging(true);
        setDraggedEvent(pendingDrop.event);
        
        // Show simplified drag message
        const dragMessage = document.createElement('div');
        dragMessage.id = 'drag-message';
        dragMessage.style.cssText = `
          position: fixed;
          top: 10px;
          right: 10px;
          background: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          z-index: 10000;
          pointer-events: none;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        `;
        dragMessage.innerHTML = `🔄 Drop event in new week`;
        document.body.appendChild(dragMessage);
        
        setIsAutoNavigating(false);
        
        // Auto-trigger drop in the center of the new week after a short delay
        setTimeout(() => {
          // Calculate center of first day of the week
          const weekStart = startOfWeek(currentDate);
          const dropDate = weekStart;
          
          // Use 9 AM as default drop time
          const dropTime = new Date(dropDate);
          dropTime.setHours(9, 0, 0, 0);
          
          // Calculate end time (preserve original duration)
          const originalDuration = pendingDrop.event.end.getTime() - pendingDrop.event.start.getTime();
          const dropEndTime = new Date(dropTime.getTime() + originalDuration);
          
          
          
          // Trigger the drop handler
          handleEventDrop({
            event: pendingDrop.event,
            start: dropTime,
            end: dropEndTime
          });
        }, 500);
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [pendingDrop, isAutoNavigating, loading, currentDate, handleEventDrop]);

  // Track which customer details we've attempted to fetch
  const fetchedCustomerDetailsRef = useRef(new Set());
  
  // Fetch customer details for visible customers with rate limiting
  useEffect(() => {
    if (activeTab === 'stops' && paginatedCustomers.length > 0 && token) {
      // Fetch details for each customer with a delay to avoid overwhelming the browser
      const fetchDetailsSequentially = async () => {
        for (let i = 0; i < paginatedCustomers.length; i++) {
          const customer = paginatedCustomers[i];
          const customerCode = customer.customer_id || customer.id;
          
          // Only fetch if we haven't tried before
          if (customerCode && !fetchedCustomerDetailsRef.current.has(customerCode)) {
            fetchedCustomerDetailsRef.current.add(customerCode);
            
            if (!customerDetailsData[customerCode] && !customerDetailsLoading[customerCode]) {
              await fetchCustomerDetailsForDialog(customerCode);
              // Add a small delay between requests to avoid overwhelming the server
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
        }
      };
      
      // Start fetching with a slight delay to let the UI render first
      const timeoutId = setTimeout(() => {
        fetchDetailsSequentially();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [activeTab, paginatedCustomers, token, customerDetailsData, customerDetailsLoading, fetchCustomerDetailsForDialog]);

  // Effect to handle body scroll when opportunities dialog is open
  useEffect(() => {
    if (opportunitiesDialogOpen) {
      // Store original styles
      const originalBodyOverflow = document.body.style.overflow;
      const originalDocumentOverflow = document.documentElement.style.overflow;
      
      // Prevent all scrolling
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalDocumentOverflow;
      };
    }
  }, [opportunitiesDialogOpen]);

  // --- Save Changes Handler ---

  const handleSaveChanges = async () => {
    if (!hasUnsavedChanges || !modifiedScheduleData || !currentClusterId)
      return;

    setLoading(true);
    setError("");
    try {
      // Log schedule data being sent to API
      // logger.log( // Disabled for brevity
      //   `Saving updated schedule data for cluster ${currentClusterId}:`,
      //   JSON.stringify(modifiedScheduleData, null, 2),
      // );

      // Log the schedule structure for debugging
      
      
      // Send the updated schedule to the server
      const response = await axios.put(
        `/schedules/${currentClusterId}/agenda`,
        {
          schedule: modifiedScheduleData,
        },
      );

      // Log the response to help with debugging
      //  // Disabled for brevity

      toast({ title: "Success!", description: "Schedule changes saved." });
      setHasUnsavedChanges(false);

      // Fetch the updated data to update local state
      const updatedData = await fetchScheduleFromApi(currentClusterId); // fetchScheduleFromApi now returns data

      // <<<< START: Update AppContext data >>>>
      if (updatedData && Array.isArray(data)) {
        setData((prevContextData) => {
          const newData = prevContextData.map((item) =>
            String(item.cluster) === String(currentClusterId)
              ? updatedData
              : item,
          );
          // If the item wasn't in context before (e.g., direct load/refresh), add it.
          // Note: This might not be strictly necessary if context is only populated on GeneratePage
          if (
            !newData.some(
              (item) => String(item.cluster) === String(currentClusterId),
            )
          ) {
            newData.push(updatedData);
          }
          
          return newData;
        });
      }
      // <<<< END: Update AppContext data >>>>
    } catch (err) {
      console.error("Error saving schedule changes:", err);
      const errorMsg =
        err.response?.data?.error || err.message || "Failed to save changes.";
      setError(errorMsg);
      toast({
        title: "Save Failed",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Add a function to update stop progress
  const handleUpdateStopProgress = async () => {
    if (!selectedStop || !selectedStop.stop_id) return;

        // Update status without opening HubSpot forms - now they're separate

    setLoading(true);
    try {
      const response = await axios.put(
        `/progress/stop/${selectedStop.stop_id}`,
        {
          status: stopStatus,
          notes: stopNotes,
        },
      );

      if (response.data) {
        toast({
          title: "Success",
          description: `Stop status updated to ${stopStatus}`,
        });

        // Refresh the schedule data
        await fetchScheduleFromApi(currentClusterId);
        setStopProgressOpen(false);
      }
    } catch (err) {
      console.error(`Error updating stop ${selectedStop.stop_id}:`, err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        `Failed to update stop status.`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenProgressDialog = (stop) => {
    setSelectedStop(stop);
    setStopStatus(stop.status || "PENDING");
    setStopNotes(stop.notes || "");
    setStopProgressOpen(true);
  };

  // Contact management functions
  const handleOpenContactSelector = (stop) => {
    
    
    // Check if this might be an older itinerary without stops
    if ((!clusterData?.itinerary?.stops || clusterData.itinerary.stops.length === 0) && 
        clusterData?.itinerary?.customers && clusterData.itinerary.customers.length > 0) {
      
    }
    
    setSelectedStopForContacts(stop);
    setContactSelectorOpen(true);
  };

  const handleViewContacts = (stop) => {
    setSelectedStopForContacts(stop);
    setContactsViewOpen(true);
  };

  // Function to update BM notes (handles both stop-level and schedule-level)
  const handleUpdateBmNotes = async () => {
    setLoading(true);
    try {
      let url = "";
      if (selectedStop && selectedStop.stop_id) {
        // Stop-level note
        url = `/notes/stops/${selectedStop.stop_id}`;
      } else {
        // Schedule-level note
        if (!currentClusterId) return;
        url = `/schedules/${currentClusterId}/bm-notes`;
      }

      if (bmNotes.trim()) {
        // Add or update notes
        await axios.post(url, { notes: bmNotes });
      } else {
        // Delete notes if empty
        await axios.delete(url);
      }

      // Refresh the schedule data
      await fetchScheduleFromApi(currentClusterId);
      setBmNotesOpen(false);
      toast({
        title: "Success",
        description: "BM notes updated",
      });
    } catch (err) {
      console.error(`Error updating BM notes:`, err);
      toast({
        title: "Error",
        description: "Failed to update BM notes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBmNotesDialog = (stop) => {
    setSelectedStop(stop); // Could be null for schedule-level
    let initialNotes = "";
    if (stop && stop.bm_notes) {
      initialNotes = stop.bm_notes.notes || "";
    } else if (!stop && clusterData?.bm_notes) {
      initialNotes = clusterData.bm_notes.notes || "";
    }
    setBmNotes(initialNotes);
    setBmNotesOpen(true);
  };

  // Function to update RSM notes (only schedule-level)
  const handleUpdateRsmNotes = async () => {
    if (!currentClusterId) return;

    setLoading(true);
    try {
      if (rsmNotes.trim()) {
        // Update RSM notes for the schedule
        await axios.post(`/schedules/${currentClusterId}/rsm-notes`, {
          notes: rsmNotes,
        });
      } else {
        // Delete notes if empty
        await axios.delete(`/schedules/${currentClusterId}/rsm-notes`);
      }

      // Refresh the schedule data
      await fetchScheduleFromApi(currentClusterId);
      setRsmNotesOpen(false);
      toast({
        title: "Success",
        description: "RSM notes updated",
      });
    } catch (err) {
      console.error(`Error updating RSM notes:`, err);
      toast({
        title: "Error",
        description: "Failed to update notes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRsmNotesDialog = () => {
    setSelectedStop(null); // Ensure no stop is selected
    setRsmNotes(clusterData?.rsm_notes?.notes || "");
    setRsmNotesOpen(true);
  };

  // Add a function to handle saving/unsaving schedules
  const handleToggleSaveSchedule = async (scheduleId, isSaved) => {
    if (!scheduleId) return;

    setLoading(true);
    try {
      const success = await toggleSaveSchedule(scheduleId, isSaved);

      if (success) {
        // Refresh data to get updated is_saved status
        await fetchScheduleFromApi(scheduleId);
      }
      return success;
    } catch (err) {
      console.error(`Error ${isSaved ? "saving" : "unsaving"} schedule:`, err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Function to handle deleting a stop
  const handleDeleteStop = async (stopId) => {
    if (!stopId || !currentClusterId) {
      console.error("Missing stopId or scheduleId for deletion");
      return;
    }

    // Optional: Add a confirmation dialog here
    if (
      !window.confirm(
        "Are you sure you want to permanently remove this stop? This cannot be undone.",
      )
    )
      return;

    setLoading(true);
    try {
      // Use fetch with explicit authentication like other API calls
      const baseUrl = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';
      const response = await fetch(`${baseUrl}/schedules/${currentClusterId}/stops/${stopId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Check for successful 204 No Content response
      if (response.status !== 204) {
        throw new Error(`Unexpected response status: ${response.status} ${response.statusText}`);
      }

      toast({
        title: "Success",
        description: "Stop removed successfully.",
      });

      // Refresh the schedule data to reflect the removal and any backend adjustments
      await fetchScheduleFromApi(currentClusterId);
      
      // Also refetch paginated customers to ensure count is correct
      if (currentClusterId && customersUrl) {
        await fetchPaginatedCustomers(currentPage, stopsPerPage);
      }
    } catch (err) {
      console.error(`Error deleting stop ${stopId}:`, err);
      
      // For fetch errors, we need to handle them differently than axios
      let errorMessage = `Failed to remove stop.`;
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      // If it's a response error, try to get more details
      if (err.response) {
        try {
          const errorData = await err.response.json();
          errorMessage = errorData.detail || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          errorMessage = `Failed to remove stop: ${err.response.statusText}`;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle deleting any agenda item from the calendar view
  const handleDeleteAgendaItem = async (itemResource) => {
    if (!itemResource) {
      console.error("handleDeleteAgendaItem called with invalid itemResource");
      toast({
        title: "Error",
        description: "Could not identify item to delete.",
        variant: "destructive",
      });
      return;
    }

    const itemType = itemResource.type === "break" ? "break" : "stop";
    const itemName =
      itemResource.label || itemResource.customer_name || `this ${itemType}`;

    if (
      !window.confirm(
        `Are you sure you want to remove ${itemName} from the schedule?`,
      )
    ) {
      return; // User cancelled
    }

    // --- Stop Deletion (uses backend endpoint) ---
    if (itemType === "stop") {
      if (!itemResource.stop_id) {
        console.error(
          "Cannot delete stop: Missing stop_id in itemResource",
          itemResource,
        );
        toast({
          title: "Error",
          description: "Stop ID missing, cannot delete.",
          variant: "destructive",
        });
        return;
      }
      // Call the existing function that handles backend deletion and refresh
      await handleDeleteStop(itemResource.stop_id);
      // handleDeleteStop already handles loading state and toasts
      return;
    }

    // --- Break Deletion (modifies local state, requires save) ---
    if (itemType === "break") {
      const { originalDayIndex, originalAgendaIndex } = itemResource;

      if (originalDayIndex === undefined || originalAgendaIndex === undefined) {
        console.error(
          "Cannot delete break: Missing original indices",
          itemResource,
        );
        toast({
          title: "Error",
          description: "Break data corrupted, cannot remove.",
          variant: "destructive",
        });
        return;
      }

      try {
        // Create a deep copy to avoid direct state mutation
        const nextSchedule = JSON.parse(JSON.stringify(modifiedScheduleData));

        // Find the agenda for the specific day
        const targetAgenda = nextSchedule[originalDayIndex]?.agenda;

        if (
          !targetAgenda ||
          originalAgendaIndex < 0 ||
          originalAgendaIndex >= targetAgenda.length
        ) {
          console.error(
            `Agenda item not found at index ${originalAgendaIndex} for day index ${originalDayIndex}`,
          );
          toast({
            title: "Error",
            description: "Could not find the break item to remove.",
            variant: "destructive",
          });
          return;
        }

        // Remove the item using splice
        targetAgenda.splice(originalAgendaIndex, 1);

        // Update the state
        setModifiedScheduleData(nextSchedule);
        setHasUnsavedChanges(true);
        toast({
          title: "Break Removed",
          description: "Click 'Save Changes' to update the schedule.",
        });
        //  // Disabled for brevity
      } catch (error) {
        console.error("Error removing break from local state:", error);
        toast({
          title: "Error",
          description: "Failed to remove break locally.",
          variant: "destructive",
        });
      }
    }
  };

  // --- Add handlers for Assign/Unassign (similar to ClusterListPage) ---
  const openAssignModal = () => {
    // We already have clusterData for the current schedule
    if (clusterData) {
      setIsAssignModalOpen(true);
    }
  };

  const handleAssignComplete = () => {
    setIsAssignModalOpen(false);
    // Refresh data after assignment
    fetchScheduleFromApi(currentClusterId);
  };

  const handleUnassign = async () => {
    if (!currentClusterId || !clusterData?.assigned_to) {
      console.error("Cannot unassign: missing data");
      return;
    }

    if (!window.confirm("Are you sure you want to unassign this schedule?")) {
      return;
    }

    setUnassignLoading(true);
    setActionTargetId(currentClusterId);
    try {
      // MODIFIED: Use PUT /schedules/{id} with assigned_to_id: null per backend docs
      await axios.put(`/schedules/${currentClusterId}`, {
        assigned_to_id: null,
      });
      toast({
        title: "Success!",
        description: "Schedule unassigned.",
      });
      // Refresh data after unassignment
      fetchScheduleFromApi(currentClusterId);
    } catch (err) {
      console.error("Error unassigning schedule:", err);
      toast({
        title: "Unassignment Failed",
        description: err.response?.data?.error || err.message,
        variant: "destructive",
      });
    } finally {
      setUnassignLoading(false);
      setActionTargetId(null);
    }
  };

  // --- ICS Export Logic ---
  const generateICSContent = () => {
    //  // Disabled for brevity
    const eventsForICS = modifiedCalendarEvents
      .map((event) => {
        // Skip if dates are invalid
        if (!isValidDate(event.start) || !isValidDate(event.end)) {
          //  // Disabled for brevity
          return null; // Skip this event
        }

        return {
          title: event.title,
          start: [
            event.start.getFullYear(),
            event.start.getMonth() + 1, // Month is 0-indexed
            event.start.getDate(),
            event.start.getHours(),
            event.start.getMinutes(),
          ],
          end: [
            event.end.getFullYear(),
            event.end.getMonth() + 1,
            event.end.getDate(),
            event.end.getHours(),
            event.end.getMinutes(),
          ],
          description: event.resource?.customer_name
            ? `Visit ${event.resource.customer_name}`
            : event.title,
          location: event.resource?.address || "", // Add location if available
          uid: `${currentClusterId}-${event.resource?.stop_id || event.resource?.originalDayIndex + "-" + event.resource?.originalAgendaIndex}`, // Basic unique ID
        };
      })
      .filter(Boolean); // Remove null entries from invalid dates

    //  // Disabled for brevity

    if (eventsForICS.length === 0) {
      //  // Disabled for brevity
      toast({
        title: "Export Warning",
        description: "No valid event data to export.",
        variant: "default",
      });
      return null; // Return null if no events
    }

    let result;
    try {
      result = ics.createEvents(eventsForICS);
      //  // Disabled for brevity
    } catch (e) {
      console.error("[generateICSContent] Error calling ics.createEvents:", e); // Added log
      toast({
        title: "Export Error",
        description: `Failed during ICS generation: ${e.message}`,
        variant: "destructive",
      });
      return null; // Return null on catch
    }

    // Now destructure after logging and try-catch
    const { value: icsString, error } = result || {}; // <<< NEW LINE: Correctly gets content from 'value'

    if (error) {
      console.error(
        "[generateICSContent] Error reported by ics.createEvents:",
        error,
      ); // Modified log message
      toast({
        title: "Export Error",
        description: "Could not generate ICS file content.",
        variant: "destructive",
      });
      return null;
    }

    if (!icsString) {
      // <<< NEW CHECK: Check the correct variable
      console.error(
        "[generateICSContent] ics.createEvents succeeded but returned undefined/null content.",
      );
      toast({
        title: "Export Error",
        description: "Failed to generate ICS content string.",
        variant: "destructive",
      });
      return null;
    }

    //  // Disabled for brevity
    return icsString; // <<< NEW RETURN: Return the correct variable
  };

  const handleExportICS = () => {
    //  // Disabled for brevity
    //  // Disabled for brevity

    const icsContent = generateICSContent();
    //  // Disabled for brevity

    if (!icsContent) {
 // Added log
      // Consider adding a user-facing message here if desired, e.g., using toast
      // toast({ title: "Export Failed", description: "No valid schedule data to export.", variant: "warning" });
      return;
    }

    try {
      // Added try-catch for blob/download part
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `isco_schedule_${currentClusterId || "export"}.ics`,
      );
      document.body.appendChild(link);
      //  // Disabled for brevity
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({
        title: "Success",
        description: "Schedule exported as ICS file.",
      });
      //  // Disabled for brevity
    } catch (downloadError) {
      console.error(
        "[handleExportICS] Error during blob creation or download:",
        downloadError,
      ); // Added log
      toast({
        title: "Export Error",
        description: "Failed to initiate file download.",
        variant: "destructive",
      });
    }
  };
  // --- End ICS Export Logic ---

  const resizableAccessor = useCallback((event) => {
    // Only allow BMs to resize, and do not allow breaks to be resized
    return isBM && event.resource?.type !== "break";
  }, [isBM]);

  const handleEventResize = useCallback(
    ({ event, start: newStart, end: newEnd }) => {
      if (!isBM || event.resource?.type === "break") {
        return;
      }

      const { originalDayIndex, originalAgendaIndex } = event.resource;

      if (originalDayIndex === undefined || originalAgendaIndex === undefined) {
        console.error("Resize error: Missing original indices on event resource.", event.resource);
        toast({
          title: "Update Error",
          description: "Event data is corrupted, cannot resize.",
          variant: "destructive",
        });
        return;
      }

      try {
        const newFormattedStartTime = format(newStart, "HH:mm");
        const newDurationMinutes = (newEnd.getTime() - newStart.getTime()) / 60000;
        const newEstimatedOnsite = formatDurationToString(newDurationMinutes);

        let nextSchedule;
        try {
          nextSchedule = JSON.parse(JSON.stringify(modifiedScheduleData));
        } catch (parseError) {
          console.error("Error cloning schedule data during resize:", parseError);
          toast({
            title: "Update Error",
            description: "Could not process schedule data.",
            variant: "destructive",
          });
          return;
        }

        const dayAgenda = nextSchedule[originalDayIndex]?.agenda;
        if (!dayAgenda) {
          console.error("Resize error: Could not find the day's agenda.");
          toast({ title: "Update Error", description: "Day data not found.", variant: "destructive" });
          return;
        }

        const resizedItem = dayAgenda[originalAgendaIndex];
        if (!resizedItem) {
          console.error("Resize error: Could not find the item in the agenda.");
          toast({ title: "Update Error", description: "Original event not found.", variant: "destructive" });
          return;
        }

        resizedItem.time = newFormattedStartTime;
        resizedItem.estimated_onsite = newEstimatedOnsite;

        dayAgenda.splice(originalAgendaIndex, 1);

        let insertionIndex = dayAgenda.findIndex(
          (item) => parseInt(item.time.replace(":", ""), 10) > parseInt(newFormattedStartTime.replace(":", ""), 10)
        );
        if (insertionIndex === -1) {
          insertionIndex = dayAgenda.length;
        }
        dayAgenda.splice(insertionIndex, 0, resizedItem);
        
        nextSchedule[originalDayIndex].agenda.forEach((item, index) => {
          // Ensure item has a resource object, create if not (though unlikely for existing items)
          if (!item.resource) item.resource = {};
          item.resource.originalDayIndex = originalDayIndex; 
          item.resource.originalAgendaIndex = index;
        });

        setModifiedScheduleData(nextSchedule);
        setHasUnsavedChanges(true);
        toast({
          title: "Schedule Updated",
          description: "Event duration changed. Click 'Save Changes' to persist.",
        });
      } catch (error) {
        console.error("Error handling event resize:", error);
        toast({
          title: "Error Resizing Event",
          variant: "destructive",
          description: error.message || "Could not update event duration.",
        });
        fetchScheduleFromApi(currentClusterId); 
        setHasUnsavedChanges(false);
      }
    },
    [isBM, modifiedScheduleData, setModifiedScheduleData, setHasUnsavedChanges, fetchScheduleFromApi, currentClusterId]
  );

  // --- Render Logic ---
  // Early returns MUST come AFTER all hook calls
  if (loading && !clusterData)
    return <div className="p-10 text-center">Loading schedule data...</div>;
  if (error)
    return <div className="p-10 text-center text-red-600">Error: {error}</div>;
  if (!loading && !error && !clusterData) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <Header />
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="p-10 text-center text-zinc-500">
          Itinerary data not found for ID '{currentClusterId}'. Please select a
          valid schedule from the
          <Link
            to={isRSM ? "/rsm-schedules" : "/schedules"}
            className="text-primary hover:underline ml-1"
          >
            {isRSM ? "My Schedules" : "View Schedules"}
          </Link>{" "}
          page.
        </div>
      </div>
    );
  }

  // --- Event Styling for Calendar ---
  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = "hsl(var(--primary) / 0.85)";
    let color = "hsl(var(--primary-foreground))";
    let borderColor = "hsl(var(--primary))";
    let opacity = isSelected ? 1 : 0.9;

    if (event.type === "break") {
      backgroundColor = "hsl(38 92% 50% / 0.85)"; // Amber-500 ish
      color = "hsl(38 95% 10%)"; // Dark text for amber
      borderColor = "hsl(38 92% 50%)";
    } else if (event.type === "travel") {
      backgroundColor = "hsl(var(--muted) / 0.7)";
      color = "hsl(var(--muted-foreground))";
      borderColor = "hsl(var(--border))"; // Use border color for travel
      opacity = isSelected ? 0.9 : 0.7;
    }

    const style = {
      backgroundColor,
      color,
      borderRadius: "calc(var(--radius) - 2px)", // Slightly smaller radius
      border: `1px solid ${borderColor}`,
      opacity: opacity,
      display: "flex", // Use flex for better control
      flexDirection: "column", // Stack title and time
      padding: "3px 6px", // Adjust padding
      fontSize: "0.7rem", // Smaller font
      lineHeight: "1.3", // Adjust line height
      boxShadow: isSelected
        ? "0 0 0 1px hsl(var(--background)), 0 0 0 3px hsl(var(--primary))"
        : "none",
      transition: "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
      overflow: "hidden", // Hide overflow
      cursor: event.type !== "break" ? "grab" : "default", // Set cursor for draggable items
    };

    return {
      style: style,
      // className: "your-custom-event-class" // Optionally add a class
    };
  };

  // --- Event Component for Calendar ---
  const CustomEvent = ({ event }) => {
    const status = event.resource?.status || "PENDING";

    // Style based on status
    let statusIndicator = null;
    if (status && event.type !== "break") {
      // Don't show status for breaks
      let bgColor;
      switch (status) {
        case "COMPLETED":
          bgColor = "bg-green-500";
          break;
        case "IN_PROGRESS":
          bgColor = "bg-blue-500";
          break;
        case "CANCELLED":
          bgColor = "bg-red-500";
          break;
        case "RESCHEDULED":
          bgColor = "bg-amber-500";
          break;
        default:
          bgColor = "bg-muted";
      }

      statusIndicator = (
        <div className={`absolute top-0 left-0 w-1 h-full ${bgColor}`}></div>
      );
    }

    const handleMouseEnter = () => {
      if (event.resource?.customer_id) {
        setHoveredCustomerId(event.resource.customer_id);
      }
    };

    const handleMouseLeave = () => {
      setHoveredCustomerId(null);
    };

    return (
      // Add group class for hover effect
      <div 
        title={event.title} 
        className="relative pl-1.5 h-full group cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {statusIndicator}
        <strong className="text-xs font-medium block truncate">
          {event.title}
        </strong>
        <span className="text-xs opacity-80 block">
          {isValidDate(event.start) ? format(event.start, "h:mm a") : "--:--"} -
          {isValidDate(event.end) ? format(event.end, "h:mm a") : "--:--"}
        </span>

        {/* Delete Button (Visible on Hover) */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-0 right-0 h-5 w-5 p-0 text-destructive opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full"
          onClick={(e) => {
            e.stopPropagation(); // Prevent calendar event click/drag actions
            handleDeleteAgendaItem(event.resource); // Call the new handler
          }}
          aria-label="Remove item"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  };

  // Add a new component for displaying stop status
  const StopStatusBadge = ({ status }) => {
    let className = "bg-muted/50";
    if (status === "COMPLETED") {
      className = "bg-green-500/10 text-green-700 border-green-500/20";
    } else if (status === "IN_PROGRESS") {
      className = "bg-blue-500/10 text-blue-700 border-blue-500/20";
    } else if (status === "CANCELLED") {
      className = "bg-red-500/10 text-red-700 border-red-500/20";
    } else if (status === "RESCHEDULED") {
      className = "bg-amber-500/10 text-amber-700 border-amber-500/20";
    }

    return (
      <Badge variant="outline" className={className}>
        {status || "PENDING"}
      </Badge>
    );
  };

  // --- ADDING LOGGING BEFORE CALENDAR RENDER ---
  // logger.log("[Calendar Render] Props:", { // Disabled for brevity
  //   currentDate: currentDate,
  //   minDate: minDate,
  //   eventsCount: modifiedCalendarEvents.length,
  //   firstEventDate: modifiedCalendarEvents.length > 0 ? modifiedCalendarEvents[0].start : null,
  // });

  // Add this new component for Mobile Bottom Navigation
  const MobileBottomNav = () => {
    return (
      <div className={`fixed bottom-0 left-0 right-0 bg-background border-t shadow-md z-40 pb-safe transition-transform duration-300 ${showMobileNav ? 'translate-y-0' : 'translate-y-full'}`}>
        <TabsList className="grid grid-cols-4 w-full rounded-none h-16 bg-background">
          <TabsTrigger 
            value="schedule" 
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <Home className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Schedule</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="stops" 
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <Users className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Stops</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <FileText className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Notes</span>
            </div>
          </TabsTrigger>
          <TabsTrigger 
            value="messages" 
            className="flex flex-col items-center justify-center space-y-1 h-full rounded-none data-[state=active]:bg-transparent data-[state=active]:border-t-2 data-[state=active]:border-primary data-[state=active]:text-primary"
          >
            <div className="flex flex-col items-center">
              <MessageCircle className="h-5 w-5 mb-1" />
              <span className="text-xs font-medium">Messages</span>
            </div>
          </TabsTrigger>
        </TabsList>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col ${isMapFullScreen ? "fixed inset-0 bg-background z-50" : "relative p-4 md:p-6 lg:p-8"}`}
    >
      {/* Header and Top Bar (conditional rendering) */}
      {!isMapFullScreen && (
        <div className="flex-shrink-0">
          <Header />
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            {" "}
            {/* Added flex-wrap */}
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="w-full flex items-center">
                <h2 className="text-xl md:text-2xl font-semibold truncate mr-2">
                  {itineraryName}
                </h2>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Badge
                    variant={getPriorityBadgeVariant(itineraryPriority)}
                    className="capitalize whitespace-nowrap"
                  >
                    {itineraryPriority}
                  </Badge>
                  {reasoning && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setShowReasoningDialog(true)}
                      title="View generation reasoning"
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isBM && (
                <SaveScheduleButton
                  scheduleId={currentClusterId}
                  isSaved={clusterData?.is_saved}
                  onToggleSave={handleToggleSaveSchedule}
                />
              )}
              <Button
                onClick={toggleFullScreenMap}
                variant="ghost"
                size="icon"
                className="flex-shrink-0"
              >
                <Maximize2 className="h-5 w-5" />
                <span className="sr-only">Fullscreen Map</span>
              </Button>
            </div>
          </div>
          
          {/* New dedicated RSM Assignment Card - HIGHLY VISIBLE */}
          {(isBM || (isRSM && clusterData?.assigned_to)) && (
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                  <UserCheck className="h-4 w-4" />
                  RSM Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                {isBM && (
                  <div className="flex flex-wrap items-center gap-2">
                    {clusterData?.assigned_to ? (
                      <div className="w-full flex items-center justify-between">
                        <Badge variant="secondary" className="py-1.5 px-3 h-auto text-sm flex items-center gap-2">
                          <UserCheck className="h-3.5 w-3.5" />
                          Assigned to: {clusterData.assigned_to.full_name}
                    </Badge>
                        <div className="flex items-center gap-2">
                    <Button
                            variant="outline"
                            size="sm"
                      onClick={openAssignModal}
                      disabled={unassignLoading && actionTargetId === currentClusterId}
                            className="h-8"
                    >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" />
                            Reassign
                    </Button>
                    <Button
                            variant="outline"
                            size="sm"
                      onClick={handleUnassign}
                      disabled={unassignLoading && actionTargetId === currentClusterId}
                            className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      {unassignLoading && actionTargetId === currentClusterId ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                              <UserMinus className="h-3.5 w-3.5 mr-1" />
                      )}
                            Unassign
                    </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openAssignModal}
                        className="h-8"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Assign RSM
                      </Button>
                    )}
                  </div>
                )}
                {isRSM && clusterData?.assigned_to && (
                  <Badge variant="secondary" className="py-1.5 px-3 h-auto text-sm flex items-center gap-2">
                    <UserCheck className="h-3.5 w-3.5" />
                    Assigned to: {clusterData.assigned_to.full_name}
                  </Badge>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Map Container */}
      <Card
        className={`overflow-hidden flex flex-col ${isMapFullScreen ? "flex-grow border-0 rounded-none shadow-none" : "h-[300px] md:h-[350px] mb-6 shadow-md flex-shrink-0"}`}
      >
        {isMapFullScreen && (
          <CardHeader className="p-2 flex-shrink-0 flex justify-end absolute top-2 right-2 z-10 bg-background/80 rounded">
            <Button onClick={toggleFullScreenMap} variant="ghost" size="icon">
              <Minimize2 className="h-5 w-5" />
              <span className="sr-only">Exit Fullscreen</span>
            </Button>
          </CardHeader>
        )}
        <CardContent
          className={`p-0 flex-grow ${isMapFullScreen ? "" : "h-full"}`}
        >
          {mapDataLoading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Loading map data...</p>
                  <p className="text-xs text-muted-foreground">Fetching {customersCount} customer locations</p>
                </div>
              </div>
            </div>
          ) : (
            (() => {
              // Get all customer coordinates from the schedule (new backend field)
              const allCoordinates = clusterData?.itinerary?.customer_coordinates_summary || [];
              
              // Always use ALL customers for the map (either new field or existing allCustomersForMap)
              const customersForMap = allCoordinates.length > 0 
                ? allCoordinates 
                : allCustomersForMap; // Always show all customers, not just current page
              
              // Get current page customer IDs for highlighting
              const currentPageCustomerIds = enhancedCustomers.map(c => c.customer_id || c.id);
              
              return (
                <MapSection
                  customers={customersForMap}
                  currentPageCustomerIds={currentPageCustomerIds}
                  directionsRequest={directionsRequest}
                  hoveredCustomerId={hoveredCustomerId}
                />
              );
            })()
          )}
        </CardContent>
      </Card>

      {/* Tabs and Content Area (Only if not fullscreen) */}
      {!isMapFullScreen && (
        <div className={`${isMobile ? "pb-16" : ""}`}> {/* Add bottom padding on mobile for the nav bar */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            {/* Desktop TabsList - hidden on mobile */}
            <TabsList className={cn(
              "grid w-full grid-cols-2 sm:grid-cols-5 mb-4 flex-shrink-0 sticky top-0 bg-background z-10 p-1 rounded-md", 
              isMobile && "hidden"
            )}>
              <TabsTrigger
                value="schedule"
                className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
              >
                Schedule
              </TabsTrigger>
              <TabsTrigger
                value="stops"
                className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
              >
                Stops ({customersCount})
              </TabsTrigger>
              <TabsTrigger
                value="add-stops"
                className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
              >
                Add Stops
              </TabsTrigger>
              <TabsTrigger
                value="notes"
                className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
              >
                Notes
              </TabsTrigger>
              <TabsTrigger
                value="messages"
                className="transition-all text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow"
              >
                Messages
              </TabsTrigger>
            </TabsList>
            
            {/* Tabs Content - Make it scrollable on mobile */}
            <div className={`${isMobile ? "h-[calc(100vh-480px)] overflow-y-auto pb-16" : ""}`}>
              {/* Tab Content (Schedule) */}
              <TabsContent value="schedule" className="mt-0">
                {/* START: Moved Save/Export Buttons Container */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 mb-2">
                  {" "}
                  {/* Removed absolute positioning, added mb-2 */}
                  {hasUnsavedChanges && (
                    <Button
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={handleSaveChanges}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </Button>
                  )}
                  {/* Export ICS Button (Modified) */}
                  <Button
                    variant="default" // Keep variant for general structure/padding
                    size="sm"
                    onClick={handleExportICS}
                    disabled={
                      !modifiedCalendarEvents ||
                      modifiedCalendarEvents.length === 0
                    }
                    title="Export schedule as .ics file"
                    className="w-full sm:w-auto" // Added responsive width
                    style={{
                      // Added inline style for specific color
                      backgroundColor: "#0078D4",
                      borderColor: "#0078D4",
                      color: "#FFFFFF",
                    }}
                  >
                    <CalendarIcon className="mr-1.5 h-4 w-4" />{" "}
                    {/* Changed icon to CalendarIcon */}
                    Export ICS
                  </Button>
                </div>
                {/* END: Moved Save/Export Buttons Container */}

                <Card>
                  {/* REMOVED the absolute button container from here */}
                  <CardContent className="p-0 relative">
                    {/* Calendar explicit height - now responsive */}
                    <div className="h-[60vh] sm:h-[600px] relative">
                      {(() => {

                        return isValidDate(currentDate) && Array.isArray(modifiedCalendarEvents) && localizer && localizer.formats;
                      })() ? (
                        <SafeCalendar
                          localizer={localizer}
                          events={modifiedCalendarEvents.length > 0 ? modifiedCalendarEvents : []} // Ensure it's never null
                          startAccessor="start"
                          endAccessor="end"
                          defaultView={isMobile ? "day" : "week"}
                          views={isMobile ? ["day"] : ["week", "day"]}
                          step={30}
                          timeslots={2}
                          showMultiDayTimes
                          popup
                          doShowMoreDrillDown
                          eventPropGetter={eventStyleGetter}
                          min={timeSettings.minTime}
                          max={timeSettings.maxTime}
                          scrollToTime={scrollToTimeValue}
                          onNavigate={handleNavigate}
                          date={currentDate}
                          getNow={() => new Date()} // Ensure proper current time reference
                          components={{
                            toolbar: (toolbarProps) => <CustomToolbar {...toolbarProps} isMobile={isMobile} isDragging={isDragging} />,
                            event: CustomEvent,
                          }}
                          style={{ height: "100%" }}
                          onEventDrop={handleEventDrop}
                          resizableAccessor={resizableAccessor}
                          onEventResize={handleEventResize}
                          selectable
                          dragFromOutsideItem={null}
                          onDropFromOutside={null}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => {
                            // Allow cross-week dropping
                            e.preventDefault();
                            return true;
                          }}
                          // Force calendar to show all events by expanding the visible range
                          formats={calendarFormats}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center bg-muted/30 rounded-md border">
                          <div className="text-center p-4">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                            <p className="text-muted-foreground">Loading calendar...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <div className={`${isMobile ? "" : "mt-6"}`}>
                  {/* Directions Feedback */}
                  <div className={`${isMobile ? "mt-4 px-1" : "mt-6"}`}>
                    <Card className="shadow-sm">
                      <CardContent className={`p-3 ${isMobile ? 'pt-3 pb-2' : 'p-4'}`}> 
                        <FeedbackComponent
                          question="How accurate are the driving directions/times?"
                          feedback={directionsFeedback}
                          setFeedback={setDirectionsFeedback}
                          feedbackKey="directions"
                          context={feedbackContext}
                          showRatings={true}
                          ratingLabels={[
                            "Accuracy",
                            "Efficiency",
                            "Clarity",
                            "Overall",
                          ]}
                          isMobile={isMobile}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Tab Content (Stops) */}
              <TabsContent value="stops" className="space-y-4 mt-0">
                <Card>
                  <CardContent className="p-4">
                    {customersLoading && currentPage === 1 ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Loading customers...</p>
                            <p className="text-xs text-muted-foreground">Fetching page {currentPage} of {totalCustomerPages || '...'}</p>
                          </div>
                        </div>
                      </div>
                    ) : enhancedCustomers.length > 0 ? (
                      <div className="rounded-md border">
                        <div className="divide-y divide-border">
                          {enhancedCustomers.map((customer, idx) => (
                            <Dialog key={customer.id || idx}>
                              <DialogTrigger asChild>
                                <button
                                  type="button"
                                  className="w-full text-left px-0 py-0 bg-transparent border-0 cursor-pointer hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-sm overflow-visible"
                                  onClick={(e) => {
                                    // Debug: Log customer object to find correct identifier
                                    // Fetch customer details when dialog opens
                                    // Use customer.customer_id which contains the actual customer code (e.g., "100423")
                                    const customerCode = customer.customer_id || customer.id;
                                    
                                    if (customerCode) {
                                      fetchCustomerDetailsForDialog(customerCode);
                                      fetchCustomerOpportunitiesForDialog(customerCode);
                                    }
                                  }}
                                >
                                  <div className="flex items-center justify-between p-3 sm:p-4 border-b border-muted">
                                     {/* Left side: Index + Customer Name + Primary Focus */}
                                    <div className="flex flex-1 items-center gap-2 sm:gap-3 min-w-0"> {/* Added flex-1 and min-w-0 */}
                                       <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                                         {(currentPage - 1) * stopsPerPage + idx + 1}
                                       </div>
                                       <div className="flex flex-col min-w-0"> {/* Name + focus */}
                                         <div className="font-medium text-sm uppercase truncate">
                                           {customer.name || "Unnamed Customer"}
                                         </div>
                                         {/* Contact Status Indicator */}
                                         <div className="flex items-center gap-1 mt-1">
                                           {(() => {
                                             const customerId = customer.customer_id || customer.id;
                                             const contactCount = customerContactCounts[customerId] || 0;
                                             return contactCount > 0 ? (
                                               <Badge variant="secondary" className="text-xs px-2 py-0.5">
                                                 <Users className="h-3 w-3 mr-1" />
                                                 {contactCount} contact{contactCount !== 1 ? 's' : ''}
                                               </Badge>
                                             ) : (
                                               <Badge variant="outline" className="text-xs px-2 py-0.5 text-muted-foreground border-muted-foreground/30">
                                                 <Users className="h-3 w-3 mr-1" />
                                                 No contacts
                                               </Badge>
                                             );
                                           })()}
                                         </div>
                                         
                                         {/* Address display */}
                                         {(customer.address || customer.street_address) && (
                                           <div className="mt-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-md p-2">
                                             <div className="flex items-start gap-2">
                                               <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 rounded-full p-1">
                                                 <MapPin className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                               </div>
                                               <div className="text-xs">
                                                 <div className="font-medium text-blue-900 dark:text-blue-100">{customer.address || customer.street_address}</div>
                                                 {(customer.city || customer.state || customer.zip_code) && (
                                                   <div className="text-blue-700 dark:text-blue-300">{[customer.city, customer.state, customer.zip_code].filter(Boolean).join(', ')}</div>
                                                 )}
                                               </div>
                                             </div>
                                           </div>
                                         )}
                                         
                                         {/* Address Specific Opportunities Popup Trigger */}
                                         {(() => {
                                           
                                           return customer.enhanced_opportunities && customer.enhanced_opportunities.length > 0;
                                         })() && (
                                           <Dialog>
                                             <DialogTrigger asChild>
                                               <button
                                                 type="button"
                                                 onClick={(e) => {
                                                   e.stopPropagation();
                                                 }}
                                                 className="mt-2 flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary hover:text-primary/80 hover:bg-primary/10 rounded-lg transition-all duration-200 group"
                                               >
                                                 <Target className="h-4 w-4" />
                                                 <span>View Opportunities</span>
                                                 <Badge variant="secondary" className="ml-1 text-xs">
                                                   {customer.enhanced_opportunities.length}
                                                 </Badge>
                                                 <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                               </button>
                                             </DialogTrigger>
                                             <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
                                               <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
                                                 <DialogTitle className="flex items-center gap-2">
                                                   <Target className="h-5 w-5" />
                                                   Address Specific Opportunities
                                                   <Badge variant="secondary" className="ml-2">
                                                     {customer.enhanced_opportunities.length}
                                                   </Badge>
                                                 </DialogTitle>
                                                 <DialogDescription>
                                                   Opportunities for {customer.name || customer.customer_name || "Unknown Customer"}
                                                 </DialogDescription>
                                               </DialogHeader>
                                               <div className="flex-1 overflow-y-auto px-6 py-4">
                                                 {/* Filters Section */}
                                                 <div className="mb-4 p-4 bg-muted/30 rounded-lg border">
                                                   <div className="flex flex-wrap items-center gap-3">
                                                     <div className="flex items-center gap-2">
                                                       <span className="text-sm font-medium">Filters:</span>
                                                     </div>
                                                     
                                                     {/* Type Filter */}
                                                     <Select 
                                                       value={(() => {
                                                         const key = `oppFilter_type_${customer.id}`;
                                                         return window[key] || 'all';
                                                       })()} 
                                                       onValueChange={(value) => {
                                                         const key = `oppFilter_type_${customer.id}`;
                                                         window[key] = value;
                                                         // Force re-render by updating a state or trigger
                                                         const event = new CustomEvent('filterUpdate');
                                                         window.dispatchEvent(event);
                                                       }}
                                                     >
                                                       <SelectTrigger className="w-32">
                                                         <SelectValue placeholder="Type" />
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                         <SelectItem value="all">All Types</SelectItem>
                                                         <SelectItem value="Quote">Quotes</SelectItem>
                                                         <SelectItem value="Order">Orders</SelectItem>
                                                         <SelectItem value="Rental">Rentals</SelectItem>
                                                       </SelectContent>
                                                     </Select>

                                                     {/* Status Filter */}
                                                     <Select 
                                                       value={(() => {
                                                         const key = `oppFilter_status_${customer.id}`;
                                                         return window[key] || 'all';
                                                       })()} 
                                                       onValueChange={(value) => {
                                                         const key = `oppFilter_status_${customer.id}`;
                                                         window[key] = value;
                                                         const event = new CustomEvent('filterUpdate');
                                                         window.dispatchEvent(event);
                                                       }}
                                                     >
                                                       <SelectTrigger className="w-36">
                                                         <SelectValue placeholder="Status" />
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                         <SelectItem value="all">All Status</SelectItem>
                                                         <SelectItem value="active">Active/Open</SelectItem>
                                                         <SelectItem value="pending">Pending</SelectItem>
                                                         <SelectItem value="closed">Closed/Won</SelectItem>
                                                         <SelectItem value="lost">Lost/Cancelled</SelectItem>
                                                       </SelectContent>
                                                     </Select>

                                                     {/* Value Filter */}
                                                     <Select 
                                                       value={(() => {
                                                         const key = `oppFilter_value_${customer.id}`;
                                                         return window[key] || 'all';
                                                       })()} 
                                                       onValueChange={(value) => {
                                                         const key = `oppFilter_value_${customer.id}`;
                                                         window[key] = value;
                                                         const event = new CustomEvent('filterUpdate');
                                                         window.dispatchEvent(event);
                                                       }}
                                                     >
                                                       <SelectTrigger className="w-36">
                                                         <SelectValue placeholder="Value" />
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                         <SelectItem value="all">All Values</SelectItem>
                                                         <SelectItem value="high">High ($100K+)</SelectItem>
                                                         <SelectItem value="medium">Medium ($50K+)</SelectItem>
                                                         <SelectItem value="standard">Standard ($10K+)</SelectItem>
                                                         <SelectItem value="entry">Entry Level</SelectItem>
                                                       </SelectContent>
                                                     </Select>

                                                     {/* Clear Filters */}
                                                     <Button 
                                                       variant="outline" 
                                                       size="sm"
                                                       onClick={() => {
                                                         const keys = [
                                                           `oppFilter_type_${customer.id}`,
                                                           `oppFilter_status_${customer.id}`,
                                                           `oppFilter_value_${customer.id}`
                                                         ];
                                                         keys.forEach(key => delete window[key]);
                                                         const event = new CustomEvent('filterUpdate');
                                                         window.dispatchEvent(event);
                                                       }}
                                                     >
                                                       <X className="h-3 w-3 mr-1" />
                                                       Clear
                                                     </Button>
                                                   </div>
                                                 </div>

                                                 {/* Results Summary */}
                                                 {(() => {
                                                   const filteredOpps = customer.enhanced_opportunities.filter(opp => {
                                                     const typeFilter = window[`oppFilter_type_${customer.id}`] || 'all';
                                                     const statusFilter = window[`oppFilter_status_${customer.id}`] || 'all';
                                                     const valueFilter = window[`oppFilter_value_${customer.id}`] || 'all';

                                                     if (typeFilter !== 'all' && opp.opportunity_type !== typeFilter) return false;
                                                     if (statusFilter !== 'all') {
                                                       const status = opp.detailed?.Status?.toLowerCase() || '';
                                                       switch (statusFilter) {
                                                         case 'active': if (!status.includes('active') && !status.includes('open')) return false; break;
                                                         case 'pending': if (!status.includes('pending') && !status.includes('proposal')) return false; break;
                                                         case 'closed': if (!status.includes('closed') && !status.includes('won')) return false; break;
                                                         case 'lost': if (!status.includes('lost') && !status.includes('cancelled')) return false; break;
                                                       }
                                                     }
                                                     if (valueFilter !== 'all' && opp.detailed?.Value) {
                                                       const value = opp.detailed.Value;
                                                       switch (valueFilter) {
                                                         case 'high': if (value < 100000) return false; break;
                                                         case 'medium': if (value < 50000 || value >= 100000) return false; break;
                                                         case 'standard': if (value < 10000 || value >= 50000) return false; break;
                                                         case 'entry': if (value >= 10000) return false; break;
                                                       }
                                                     }
                                                     return true;
                                                   });
                                                   
                                                   const totalValue = filteredOpps.reduce((sum, opp) => sum + (opp.detailed?.Value || 0), 0);
                                                   const isFiltered = (window[`oppFilter_type_${customer.id}`] && window[`oppFilter_type_${customer.id}`] !== 'all') ||
                                                                     (window[`oppFilter_status_${customer.id}`] && window[`oppFilter_status_${customer.id}`] !== 'all') ||
                                                                     (window[`oppFilter_value_${customer.id}`] && window[`oppFilter_value_${customer.id}`] !== 'all');

                                                   return (
                                                     <div className="mb-4 flex items-center justify-between p-3 bg-background border rounded-lg">
                                                       <div className="flex items-center gap-2">
                                                         <span className="text-sm text-muted-foreground">
                                                           Showing {filteredOpps.length} of {customer.enhanced_opportunities.length} opportunities
                                                         </span>
                                                         {isFiltered && (
                                                           <Badge variant="secondary" className="text-xs">
                                                             Filtered
                                                           </Badge>
                                                         )}
                                                       </div>
                                                       {totalValue > 0 && (
                                                         <div className="text-sm font-medium">
                                                           Total: {new Intl.NumberFormat("en-US", {
                                                             style: "currency",
                                                             currency: "USD",
                                                             notation: "compact",
                                                             minimumFractionDigits: 0,
                                                             maximumFractionDigits: 1,
                                                           }).format(totalValue)}
                                                         </div>
                                                       )}
                                                     </div>
                                                   );
                                                 })()}

                                                 <div className="space-y-3 pb-4">
                                                   {customer.enhanced_opportunities.filter(opp => {
                                                     // Apply filters
                                                     const typeFilter = window[`oppFilter_type_${customer.id}`] || 'all';
                                                     const statusFilter = window[`oppFilter_status_${customer.id}`] || 'all';
                                                     const valueFilter = window[`oppFilter_value_${customer.id}`] || 'all';

                                                     // Type filter
                                                     if (typeFilter !== 'all' && opp.opportunity_type !== typeFilter) {
                                                       return false;
                                                     }

                                                     // Status filter
                                                     if (statusFilter !== 'all') {
                                                       const status = opp.detailed?.Status?.toLowerCase() || '';
                                                       switch (statusFilter) {
                                                         case 'active':
                                                           if (!status.includes('active') && !status.includes('open')) return false;
                                                           break;
                                                         case 'pending':
                                                           if (!status.includes('pending') && !status.includes('proposal')) return false;
                                                           break;
                                                         case 'closed':
                                                           if (!status.includes('closed') && !status.includes('won')) return false;
                                                           break;
                                                         case 'lost':
                                                           if (!status.includes('lost') && !status.includes('cancelled')) return false;
                                                           break;
                                                       }
                                                     }

                                                     // Value filter
                                                     if (valueFilter !== 'all' && opp.detailed?.Value) {
                                                       const value = opp.detailed.Value;
                                                       switch (valueFilter) {
                                                         case 'high':
                                                           if (value < 100000) return false;
                                                           break;
                                                         case 'medium':
                                                           if (value < 50000 || value >= 100000) return false;
                                                           break;
                                                         case 'standard':
                                                           if (value < 10000 || value >= 50000) return false;
                                                           break;
                                                         case 'entry':
                                                           if (value >= 10000) return false;
                                                           break;
                                                       }
                                                     }

                                                     return true;
                                                   }).map((opp, oppIdx) => {
                                                     const OpportunityIcon = (() => {
                                                       switch (opp.opportunity_type) {
                                                         case 'Quote': return FileText;
                                                         case 'Order': return CheckCircle2;
                                                         case 'Rental': return Package;
                                                         default: return Target;
                                                       }
                                                     })();
                                                     
                                                     const colors = (() => {
                                                       switch (opp.opportunity_type) {
                                                         case 'Quote': 
                                                           return {
                                                             bg: 'bg-blue-50 dark:bg-blue-950/20',
                                                             border: 'border-blue-200 dark:border-blue-800/30',
                                                             iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                                                             iconColor: 'text-blue-600 dark:text-blue-400',
                                                             accent: 'border-l-blue-400'
                                                           };
                                                         case 'Order': 
                                                           return {
                                                             bg: 'bg-green-50 dark:bg-green-950/20',
                                                             border: 'border-green-200 dark:border-green-800/30',
                                                             iconBg: 'bg-green-100 dark:bg-green-900/30',
                                                             iconColor: 'text-green-600 dark:text-green-400',
                                                             accent: 'border-l-green-400'
                                                           };
                                                         case 'Rental': 
                                                           return {
                                                             bg: 'bg-purple-50 dark:bg-purple-950/20',
                                                             border: 'border-purple-200 dark:border-purple-800/30',
                                                             iconBg: 'bg-purple-100 dark:bg-purple-900/30',
                                                             iconColor: 'text-purple-600 dark:text-purple-400',
                                                             accent: 'border-l-purple-400'
                                                           };
                                                         default: 
                                                           return {
                                                             bg: 'bg-gray-50 dark:bg-gray-950/20',
                                                             border: 'border-gray-200 dark:border-gray-800/30',
                                                             iconBg: 'bg-gray-100 dark:bg-gray-900/30',
                                                             iconColor: 'text-gray-600 dark:text-gray-400',
                                                             accent: 'border-l-gray-400'
                                                           };
                                                       }
                                                     })();
                                                     
                                                     const getStatusColor = (status) => {
                                                       if (!status) return 'default';
                                                       const statusLower = status.toLowerCase();
                                                       if (statusLower.includes('won') || statusLower.includes('closed') || statusLower.includes('active')) return 'default';
                                                       if (statusLower.includes('pending') || statusLower.includes('open') || statusLower.includes('proposal')) return 'secondary';
                                                       if (statusLower.includes('lost') || statusLower.includes('cancelled')) return 'destructive';
                                                       return 'outline';
                                                     };
                                                     
                                                     const getValueColor = (value) => {
                                                       if (!value) return 'text-muted-foreground';
                                                       if (value >= 100000) return 'text-emerald-600 dark:text-emerald-400';
                                                       if (value >= 50000) return 'text-green-600 dark:text-green-400';
                                                       if (value >= 10000) return 'text-blue-600 dark:text-blue-400';
                                                       return 'text-foreground';
                                                     };
                                                     
                                                     return (
                                                       <div 
                                                         key={oppIdx}
                                                         className={`${colors.bg} ${colors.border} border-l-4 ${colors.accent} rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200`}
                                                       >
                                                         {/* Header with icon, type, and value */}
                                                         <div className="flex items-start justify-between mb-3">
                                                           <div className="flex items-center gap-3">
                                                             <div className={`flex items-center justify-center w-10 h-10 rounded-full ${colors.iconBg} ${colors.iconColor} shadow-sm`}>
                                                               <OpportunityIcon className="h-5 w-5" />
                                                             </div>
                                                             <div>
                                                               <div className="flex items-center gap-2 mb-1">
                                                                 <Badge variant="outline" className={`text-xs font-medium ${colors.iconColor} border-current`}>
                                                                   {opp.opportunity_type}
                                                                 </Badge>
                                                                 {opp.detailed?.Status && (
                                                                   <Badge variant={getStatusColor(opp.detailed.Status)} className="text-xs">
                                                                     {opp.detailed.Status}
                                                                   </Badge>
                                                                 )}
                                                               </div>
                                                               <span className="text-sm font-semibold text-foreground">
                                                                 {opp.value_id}
                                                               </span>
                                                             </div>
                                                           </div>
                                                           {opp.detailed?.Value && (
                                                             <div className="text-right">
                                                               <div className={`text-lg font-bold ${getValueColor(opp.detailed.Value)}`}>
                                                                 {new Intl.NumberFormat("en-US", {
                                                                   style: "currency",
                                                                   currency: "USD",
                                                                   notation: "compact",
                                                                   minimumFractionDigits: 0,
                                                                   maximumFractionDigits: 1,
                                                                 }).format(opp.detailed.Value)}
                                                               </div>
                                                               <div className="text-xs text-muted-foreground">
                                                                 {opp.detailed.Value >= 100000 ? 'High Value' : 
                                                                  opp.detailed.Value >= 50000 ? 'Medium Value' : 
                                                                  opp.detailed.Value >= 10000 ? 'Standard' : 'Entry Level'}
                                                               </div>
                                                             </div>
                                                           )}
                                                         </div>
                                                         
                                                         {/* Details grid */}
                                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                                           {opp.detailed?.Market && (
                                                             <div className="flex items-center gap-2">
                                                               <Globe className="h-4 w-4 text-muted-foreground" />
                                                               <span className="text-muted-foreground">Market:</span>
                                                               <span className="font-medium">{opp.detailed.Market}</span>
                                                             </div>
                                                           )}
                                                           {opp.detailed?.EntryDate && (
                                                             <div className="flex items-center gap-2">
                                                               <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                                               <span className="text-muted-foreground">Date:</span>
                                                               <span className="font-medium">{new Date(opp.detailed.EntryDate).toLocaleDateString()}</span>
                                                             </div>
                                                           )}
                                                           {opp.detailed?.SalesTeam && (
                                                             <div className="flex items-center gap-2">
                                                               <Users className="h-4 w-4 text-muted-foreground" />
                                                               <span className="text-muted-foreground">Team:</span>
                                                               <span className="font-medium">{opp.detailed.SalesTeam}</span>
                                                             </div>
                                                           )}
                                                           {opp.flattened?.location_city && opp.flattened?.location_state && (
                                                             <div className="flex items-center gap-2">
                                                               <MapPin className="h-4 w-4 text-muted-foreground" />
                                                               <span className="text-muted-foreground">Location:</span>
                                                               <span className="font-medium">{opp.flattened.location_city}, {opp.flattened.location_state}</span>
                                                             </div>
                                                           )}
                                                         </div>
                                                         
                                                         {/* Type-specific fields */}
                                                         {(opp.opportunity_type === 'Quote' && opp.detailed?.QuoteNumber) ||
                                                          (opp.opportunity_type === 'Order' && opp.detailed?.OrderNumber) ||
                                                          (opp.opportunity_type === 'Rental' && (opp.detailed?.CommonName || opp.detailed?.ProductCode)) ? (
                                                           <div className="mt-3 pt-3 border-t border-border/40">
                                                             {opp.opportunity_type === 'Quote' && opp.detailed?.QuoteNumber && (
                                                               <div className="flex items-center gap-2 text-sm">
                                                                 <FileText className="h-4 w-4 text-muted-foreground" />
                                                                 <span className="text-muted-foreground">Quote #:</span>
                                                                 <span className="font-mono font-medium bg-muted px-2 py-1 rounded text-xs">
                                                                   {opp.detailed.QuoteNumber}
                                                                 </span>
                                                               </div>
                                                             )}
                                                             {opp.opportunity_type === 'Order' && opp.detailed?.OrderNumber && (
                                                               <div className="flex items-center gap-2 text-sm">
                                                                 <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                                                                 <span className="text-muted-foreground">Order #:</span>
                                                                 <span className="font-mono font-medium bg-muted px-2 py-1 rounded text-xs">
                                                                   {opp.detailed.OrderNumber}
                                                                 </span>
                                                               </div>
                                                             )}
                                                             {opp.opportunity_type === 'Rental' && (
                                                               <div className="space-y-2">
                                                                 {opp.detailed?.CommonName && (
                                                                   <div className="flex items-center gap-2 text-sm">
                                                                     <Package className="h-4 w-4 text-muted-foreground" />
                                                                     <span className="text-muted-foreground">Equipment:</span>
                                                                     <span className="font-medium">{opp.detailed.CommonName}</span>
                                                                   </div>
                                                                 )}
                                                                 {opp.detailed?.ProductCode && (
                                                                   <div className="flex items-center gap-2 text-sm">
                                                                     <span className="text-muted-foreground ml-6">Product Code:</span>
                                                                     <span className="font-mono font-medium bg-muted px-2 py-1 rounded text-xs">
                                                                       {opp.detailed.ProductCode}
                                                                     </span>
                                                                   </div>
                                                                 )}
                                                               </div>
                                                             )}
                                                           </div>
                                                         ) : null}
                                                         
                                                         {/* Address information */}
                                                         {opp.flattened?.street_address && (
                                                           <div className="mt-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border border-indigo-200 dark:border-indigo-800/50 rounded-lg p-3">
                                                             <div className="flex items-start gap-2">
                                                               <div className="flex-shrink-0 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 rounded-full p-1.5 shadow-sm">
                                                                 <MapPin className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                                                               </div>
                                                               <div className="text-sm">
                                                                 <div className="font-semibold text-indigo-900 dark:text-indigo-100">{opp.flattened.street_address}</div>
                                                                 {(opp.flattened.location_city || opp.flattened.location_state || opp.flattened.location_zip) && (
                                                                   <div className="text-indigo-700 dark:text-indigo-300 text-xs mt-0.5">
                                                                     {[opp.flattened.location_city, opp.flattened.location_state, opp.flattened.location_zip].filter(Boolean).join(', ')}
                                                                   </div>
                                                                 )}
                                                               </div>
                                                             </div>
                                                           </div>
                                                         )}
                                                       </div>
                                                     );
                                                   })}
                                                 </div>
                                               </div>
                                             </DialogContent>
                                           </Dialog>
                                         )}
                                         
                                       </div>
                                     </div>
                                     
                                     {/* Right side with action buttons - ensure vertical alignment */}
                                    <div className="flex flex-shrink-0 items-center space-x-1">
                                      {/* Status Badge for RSM, shown first on the right */}
                                      {isRSM && <StopStatusBadge status={customer.status} />}
                                      {/* Prepare maps URL first */}
                                      {(() => {
                                         let googleMapsQuery = "";
                                        let canOpenMap = false;

                                        if (customer.location) {
                                          let lat, lng;
                                          if (typeof customer.location === 'object' && customer.location.lat != null && customer.location.lng != null) {
                                            lat = customer.location.lat;
                                            lng = customer.location.lng;
                                          } else if (typeof customer.location === 'string') {
                                            const parts = customer.location.replace(/[()]/g, "").split(',');
                                            if (parts.length === 2) {
                                              const parsedLat = parseFloat(parts[0]);
                                              const parsedLng = parseFloat(parts[1]);
                                              if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
                                                lat = parsedLat;
                                                lng = parsedLng;
                                              }
                                            }
                                          }

                                          if (lat != null && lng != null) {
                                            googleMapsQuery = `${lat},${lng}`;
                                            canOpenMap = true;
                                          }
                                        }

                                        if (!canOpenMap) { // Fallback to address
                                          const addressParts = [
                                            customer.address,
                                            customer.city,
                                            customer.state,
                                            customer.zipcode,
                                          ].filter(Boolean);
                                          const fullAddress = addressParts.join(", ");
                                          if (fullAddress) {
                                            googleMapsQuery = fullAddress;
                                            canOpenMap = true;
                                          }
                                        }
                                        customer.googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleMapsQuery)}`;
                                        customer.canOpenMap = canOpenMap;
                                      })()}
                                    
                                      {/* Status badge for RSM */}
                                      {isRSM && <StopStatusBadge status={customer.status} />}
                                      
                                      {/* Maps button */}
                                      <button
                                        type="button"
                                        className="flex items-center justify-center h-6 w-6 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent dialog from opening
                                          e.preventDefault();
                                          if (customer.canOpenMap) {
                                            window.open(customer.googleMapsUrl, '_blank', 'noopener,noreferrer');
                                          } else {
                                            toast({
                                              title: "Location Data Missing",
                                              description: "Cannot open in Google Maps, coordinates or address are incomplete.",
                                              variant: "destructive",
                                            });
                                          }
                                        }}
                                        disabled={!customer.canOpenMap}
                                        aria-label="Open in Google Maps"
                                        title="Open in Google Maps"
                                      >
                                       <MapIcon className="h-3.5 w-3.5" />
                                      </button>
                                       
                                    {/* Contact management buttons */}
                                    {(isBM || isRSM) && (
                                      <button
                                        type="button"
                                        className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleOpenContactSelector(customer);
                                        }}
                                        aria-label="Manage contacts"
                                        title="Manage contacts"
                                      >
                                        <UserPlus className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                    
                                    <button
                                      type="button"
                                      className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-primary/10 hover:text-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleViewContacts(customer);
                                      }}
                                      aria-label="View contacts"
                                      title="View contacts"
                                    >
                                      <Users className="h-3.5 w-3.5" />
                                    </button>
                                    
                                    {/* Delete button for BM */}
                                    {isBM && (
                                      <button
                                        type="button"
                                        className="flex items-center justify-center h-7 w-7 p-1 rounded-full bg-muted/30 hover:bg-destructive/10 hover:text-destructive"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent dialog from opening
                                          e.preventDefault();
                                          
                                          if (!customer.stop_id) {
                                            console.error('❌ No stop_id found in customer object:', customer);
                                            alert('Error: Cannot delete stop - no stop_id found. Please refresh the page and try again.');
                                            return;
                                          }
                                          
                                          if (window.confirm("Are you sure you want to permanently remove this stop? This cannot be undone.")) {
                                            handleDeleteStop(customer.stop_id);
                                          }
                                        }}
                                        aria-label="Remove stop"
                                        title="Remove stop"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                       </button>
                                     )}
                                       
                                      {/* Chevron indicator */}
                                     <div className="flex items-center justify-center h-7 w-7 p-1">
                                       <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                     </div>
                                   </div>
                                 </div>
                               </button>
                             </DialogTrigger>
                              
                             <DialogContent className="w-full max-w-[320px] sm:max-w-lg md:max-w-3xl lg:max-w-4xl xl:max-w-5xl p-0">
                               <DialogHeader className="p-3 sm:p-4 border-b bg-muted/30">
                                 <div className="flex items-center justify-between w-full">
                                   <div className="flex items-center gap-2 sm:gap-3">
                                     <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base sm:text-lg font-bold">
                                        {(currentPage - 1) * stopsPerPage + idx + 1}
                                     </div>
                                   </div>
                                   {(isRSM || isBM) && (
                                     <Dialog>
                                       <DialogTrigger asChild>
                                         <Button 
                                           variant="outline" 
                                           size="sm" 
                                           className="gap-1.5 mr-16"
                                           onClick={() => {
                                             // Fetch HubSpot contacts when dialog opens for both RSM and BM
                                             fetchAllContacts();
                                           }}
                                         >
                                           <Users className="h-4 w-4" />
                                           Manage Contacts
                                         </Button>
                                       </DialogTrigger>
                                     <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
                                       <DialogHeader className="pb-4">
                                         <DialogTitle className="text-lg">Manage Contacts</DialogTitle>
                                         <DialogDescription className="text-sm">
                                           View and manage all contacts in HubSpot. Add new contacts for customer engagements.
                                         </DialogDescription>
                                       </DialogHeader>
                                       <div className="grid gap-6 overflow-y-auto flex-1 pr-2">
                                         {/* Existing Contacts Section */}
                                         <div className="space-y-3">
                                           <div className="flex items-center justify-between">
                                             <h3 className="text-sm font-semibold flex items-center gap-2">
                                               <Users className="h-4 w-4 text-muted-foreground" />
                                               Existing Contacts
                                               {hubspotContacts && hubspotContacts.length > 0 && (
                                                 <span className="text-xs text-muted-foreground font-normal">
                                                   ({hubspotContacts.length} total)
                                                 </span>
                                               )}
                                             </h3>
                                           </div>
                                           
                                           <div className="border rounded-lg bg-muted/10 p-1">
                                             {hubspotLoading ? (
                                               <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-8">
                                                 <Loader2 className="h-4 w-4 animate-spin" />
                                                 Loading contacts...
                                               </div>
                                             ) : hubspotContacts && hubspotContacts.length > 0 ? (
                                               <ScrollArea className="h-[200px] pr-3">
                                                 <div className="space-y-2 p-2">
                                                   {hubspotContacts.map((contact) => (
                                                     <div key={contact.id} className="p-3 border rounded-lg bg-background hover:bg-muted/50 transition-colors">
                                                       <div className="flex items-start justify-between gap-3">
                                                         <div className="flex-1 min-w-0">
                                                           <div className="font-medium text-sm">
                                                             {contact.properties.firstname} {contact.properties.lastname}
                                                           </div>
                                                           <div className="text-xs text-muted-foreground space-y-1 mt-1">
                                                             {contact.properties.email && (
                                                               <div className="flex items-center gap-1.5">
                                                                 <Mail className="h-3 w-3 flex-shrink-0" />
                                                                 <span className="truncate">{contact.properties.email}</span>
                                                               </div>
                                                             )}
                                                             {contact.properties.phone && (
                                                               <div className="flex items-center gap-1.5">
                                                                 <Phone className="h-3 w-3 flex-shrink-0" />
                                                                 <span>{contact.properties.phone}</span>
                                                               </div>
                                                             )}
                                                           </div>
                                                         </div>
                                                         <div className="text-xs text-muted-foreground">
                                                           #{contact.id}
                                                         </div>
                                                       </div>
                                                     </div>
                                                   ))}
                                                 </div>
                                               </ScrollArea>
                                             ) : hubspotError ? (
                                               <div className="flex items-center justify-center gap-2 text-sm text-destructive py-8">
                                                 <AlertCircle className="h-4 w-4" />
                                                 Error: {hubspotError}
                                               </div>
                                             ) : (
                                               <div className="flex items-center justify-center text-sm text-muted-foreground py-8">
                                                 No contacts found in HubSpot
                                               </div>
                                             )}
                                           </div>
                                           
                                           {hasMoreContacts && hubspotContacts && hubspotContacts.length > 0 && (
                                             <Button 
                                               variant="outline" 
                                               size="sm" 
                                               onClick={loadMoreContacts}
                                               disabled={hubspotLoading}
                                               className="w-full mt-2"
                                             >
                                               {hubspotLoading ? (
                                                 <>
                                                   <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                   Loading more...
                                                 </>
                                               ) : (
                                                 `Load More Contacts`
                                               )}
                                             </Button>
                                           )}
                                         </div>
                                         
                                         {/* Add New Contact Form */}
                                         <div className="space-y-3">
                                           <Separator />
                                           <h3 className="text-sm font-semibold flex items-center gap-2">
                                             <UserPlus className="h-4 w-4 text-muted-foreground" />
                                             Add New Contact
                                           </h3>
                                           <div className="grid gap-4 p-4 border rounded-lg bg-muted/10">
                                             <div className="grid grid-cols-2 gap-3">
                                               <div className="space-y-2">
                                                 <label htmlFor="firstName" className="text-sm font-medium">
                                                   First Name <span className="text-destructive">*</span>
                                                 </label>
                                                 <Input 
                                                   id="firstName" 
                                                   placeholder="John" 
                                                   className="h-9"
                                                 />
                                               </div>
                                               <div className="space-y-2">
                                                 <label htmlFor="lastName" className="text-sm font-medium">
                                                   Last Name <span className="text-destructive">*</span>
                                                 </label>
                                                 <Input 
                                                   id="lastName" 
                                                   placeholder="Doe" 
                                                   className="h-9"
                                                 />
                                               </div>
                                             </div>
                                             <div className="space-y-2">
                                               <label htmlFor="email" className="text-sm font-medium">
                                                 Email <span className="text-destructive">*</span>
                                               </label>
                                               <Input 
                                                 id="email" 
                                                 placeholder="john.doe@example.com" 
                                                 type="email" 
                                                 className="h-9"
                                               />
                                             </div>
                                             <div className="space-y-2">
                                               <label htmlFor="phone" className="text-sm font-medium">
                                                 Phone Number
                                               </label>
                                               <Input 
                                                 id="phone" 
                                                 placeholder="+1 (555) 123-4567" 
                                                 className="h-9"
                                               />
                                             </div>
                                           </div>
                                         </div>
                                       </div>
                                       <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 flex-shrink-0 border-t">
                                         <Button 
                                           type="button" 
                                           variant="outline"
                                           size="sm"
                                           className="w-full sm:w-auto"
                                           onClick={() => {
                                             // Clear the form when canceling
                                             const firstNameInput = document.getElementById('firstName');
                                             const lastNameInput = document.getElementById('lastName');
                                             const emailInput = document.getElementById('email');
                                             const phoneInput = document.getElementById('phone');
                                             
                                             if (firstNameInput) firstNameInput.value = '';
                                             if (lastNameInput) lastNameInput.value = '';
                                             if (emailInput) emailInput.value = '';
                                             if (phoneInput) phoneInput.value = '';
                                           }}
                                         >
                                           Clear Form
                                         </Button>
                                         <Button 
                                           type="button"
                                           size="sm"
                                           className="w-full sm:w-auto"
                                           onClick={async () => {
                                             // Validate required fields
                                             const firstNameInput = document.getElementById('firstName');
                                             const lastNameInput = document.getElementById('lastName');
                                             const emailInput = document.getElementById('email');
                                             const phoneInput = document.getElementById('phone');
                                             
                                             if (!firstNameInput?.value || !lastNameInput?.value || !emailInput?.value) {
                                               toast({
                                                 title: "Missing Information",
                                                 description: "Please fill in all required fields (First Name, Last Name, Email)",
                                                 variant: "destructive",
                                               });
                                               return;
                                             }
                                             
                                             // Validate email format
                                             const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                             if (!emailRegex.test(emailInput.value)) {
                                               toast({
                                                 title: "Invalid Email",
                                                 description: "Please enter a valid email address",
                                                 variant: "destructive",
                                               });
                                               return;
                                             }
                                             
                                             // Create contact in HubSpot (for both RSM and BM)
                                             const newContact = {
                                               first_name: firstNameInput.value,
                                               last_name: lastNameInput.value,
                                               email: emailInput.value,
                                               phone: phoneInput?.value || ""
                                             };
                                             
                                             // Call the HubSpot API
                                             try {
                                               const response = await createContact(newContact);
                                               
                                               if (response?.status === "success") {
                                                 toast({
                                                   title: "Success",
                                                   description: "Contact added successfully",
                                                 });
                                                 
                                                 // Clear the form
                                                 if (firstNameInput) firstNameInput.value = '';
                                                 if (lastNameInput) lastNameInput.value = '';
                                                 if (emailInput) emailInput.value = '';
                                                 if (phoneInput) phoneInput.value = '';
                                                 
                                                 // The createContact function already refreshes the contacts list
                                               } else {
                                                 throw new Error("Failed to create contact in HubSpot");
                                               }
                                             } catch (error) {
                                               console.error("Error adding HubSpot contact:", error);
                                               toast({
                                                 title: "Error",
                                                 description: error.message || "Failed to add contact to HubSpot",
                                                 variant: "destructive",
                                               });
                                             }
                                           }}
                                         >
                                           <UserPlus className="h-4 w-4 mr-1" />
                                           Add Contact
                                         </Button>
                                       </DialogFooter>
                                     </DialogContent>
                                   </Dialog>
                                   )}
                                 </div>
                                 <div className="flex items-center gap-2 sm:gap-3">
                                      <div>
                                        <DialogTitle className="text-xl font-semibold uppercase">
                                          {customer.name || "Unnamed Customer"}
                                        </DialogTitle>
                                        <DialogDescription className="flex items-center gap-2 text-xs text-muted-foreground">
                                          <span>ID: {customer.customer_id || customer.id || "N/A"}</span>
                                          {customer.priority?.tier && (
                    <Badge
                      variant="secondary"
                                              size="sm"
                                              className="capitalize"
                    >
                                              {customer.priority.tier} Tier
                    </Badge>
                  )}
                                      </DialogDescription>
                                    </div>
                                  </div>
                                </DialogHeader>
                                {/* For debugging - log QORC data */}
                                {/* {logger.log("Customer QORC data:", { // Disabled for brevity
                                  id: customer.id,
                                  name: customer.name,
                                  qorc: customer.qorc,
                                  primary_focus: customer.primary_focus,
                                  has_open_quotes: Array.isArray(customer?.open_quotes) && customer.open_quotes.length > 0,
                                  has_open_orders: Array.isArray(customer?.open_orders) && customer.open_orders.length > 0,
                                  has_active_rentals: Array.isArray(customer?.active_rentals) && customer.active_rentals.length > 0,
                                  open_quotes_count: customer.open_quotes?.length || 0,
                                  open_orders_count: customer.open_orders?.length || 0,
                                  active_rentals_count: customer.active_rentals?.length || 0,
                                })} */}
                                {/* Check if customer details are loading */}
                                {(() => {
                                  const customerCode = customer.id;
                                  const isLoading = customerDetailsLoading[customerCode];
                                  const customerDetails = customerDetailsData[customerCode];

                                  if (isLoading) {
                                    return (
                                      <div className="flex items-center justify-center py-12">
                                        <div className="text-center space-y-4">
                                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                                          <div className="space-y-2">
                                            <p className="text-sm font-medium text-foreground">Loading customer details...</p>
                                            <p className="text-xs text-muted-foreground">Fetching business metrics, opportunities, and insights</p>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <ScrollArea className="max-h-[70vh]">
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 md:p-6">
                                    {/* --- Left Column --- */}
                                    <div className="space-y-3 sm:space-y-4">
                                        {/* Combined Customer & Company Info Card */}
                                        {(() => {
                                          const customerCode = customer.customer_id || customer.id;
                                          const customerDetails = customerDetailsData[customerCode];
                                          
                                          return (
                                        <Card className="bg-background shadow-sm">
                                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                                              <Building2 className="w-4 h-4" />
                                              Customer & Company Info
                                            </CardTitle>
                                          </CardHeader>
                                          <CardContent>
                                            <div className="space-y-4">
                                              {/* Customer Basic Info */}
                                              <div className="space-y-2">
                                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Customer Details</h4>
                                                <div className="space-y-1.5 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Name:</span>
                                                    <span className="font-medium">{customer.name || "Unknown Customer"}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-muted-foreground">ID:</span>
                                                    <span className="font-medium">{customer.customer_id || customer.id || "N/A"}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Address:</span>
                                                    <span className="font-medium text-right flex-1 ml-2">{customer.address || "No address provided"}</span>
                                                  </div>
                                                  {(customer.city || customer.state || customer.zip_code) && (
                                                    <div className="flex justify-between">
                                                      <span className="text-muted-foreground">Location:</span>
                                                      <span className="font-medium">
                                                        {[customer.city, customer.state, customer.zip_code].filter(Boolean).join(", ")}
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Customer Notes */}
                                              {customer.notes && (
                                                <div className="space-y-2 pt-2 border-t">
                                                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</h4>
                                                  <p className="text-sm text-foreground bg-muted/30 rounded p-2">{customer.notes}</p>
                                                </div>
                                              )}

                                              {/* Company Profile Info */}
                                              {customerDetails?.company_profile && (
                                                <div className="space-y-2 pt-2 border-t">
                                                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Company Profile</h4>
                                                  <div className="space-y-1.5 text-sm">
                                                    {customerDetails.company_profile.industry && (
                                                      <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Industry:</span>
                                                        <Badge variant="secondary" className="text-xs">{customerDetails.company_profile.industry}</Badge>
                                                      </div>
                                                    )}
                                                    {customerDetails.company_profile.website_url && (
                                                      <div className="flex justify-between">
                                                        <span className="text-muted-foreground">Website:</span>
                                                        <a href={customerDetails.company_profile.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                                                          <Globe className="w-3 h-3" />
                                                          Visit
                                                        </a>
                                                      </div>
                                                    )}
                                                    {customerDetails.company_profile.company_summary && (
                                                      <div className="space-y-1">
                                                        <span className="text-muted-foreground text-xs">Summary:</span>
                                                        <p className="text-xs text-foreground leading-relaxed bg-muted/30 rounded p-2">{customerDetails.company_profile.company_summary}</p>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              )}

                                            </div>
                                          </CardContent>
                                        </Card>
                                        );
                                      })()}

                                      {/* Discussion Points Card */}
                                      {(() => {
                                        const customerCode = customer.customer_id || customer.id;
                                        const customerDetails = customerDetailsData[customerCode];
                                        
                                        return customerDetails?.discussion_points && customerDetails.discussion_points.length > 0 ? (
                                          <Card className="bg-background shadow-sm">
                                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                                                <MessagesSquare className="w-4 h-4" />
                                                Discussion Points
                                              </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                              <ul className="space-y-2">
                                                {customerDetails.discussion_points.map((point, i) => (
                                                  <li key={i} className="flex gap-2 items-start">
                                                    <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                                                    <span className="text-sm">{point}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                            </CardContent>
                                          </Card>
                                        ) : null;
                                      })()}

                                      {/* HubSpot Engagement History Card */}
                                      {(isRSM || isBM) && (
                                        <CustomerEngagementHistory customer={customer} />
                                      )}

                                        {/* QORC Analysis Card - enhanced */}
                                        {(() => {
                                          const customerCode = customer.customer_id || customer.id;
                                          const customerDetails = customerDetailsData[customerCode];
                                          
                                          return customerDetails?.qorc_scores ? (
                                          <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 shadow-md border-slate-200 dark:border-slate-700">
                                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                <div className="p-1.5 bg-primary/10 rounded-lg">
                                                  <BarChart3 className="w-4 h-4 text-primary" />
                                                </div>
                                                Customer Scorecard
                                              </CardTitle>
                                              {customerDetails.qorc_scores.prioritybucket && (
                                                <Badge 
                                                  variant={
                                                    customerDetails.qorc_scores.prioritybucket === 'High' ? 'default' : 
                                                    customerDetails.qorc_scores.prioritybucket === 'Medium' ? 'secondary' : 'outline'
                                                  }
                                                  className="text-xs font-medium px-3 py-1"
                                                >
                                                  {customerDetails.qorc_scores.prioritybucket} Priority
                                                </Badge>
                                              )}
                                            </CardHeader>
                                            <CardContent>
                                              <div className="grid grid-cols-2 gap-3 mb-4">
                                                <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                  <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.qscore || 'N/A'}</p>
                                                  <p className="text-xs text-muted-foreground font-medium">Q-Score</p>
                                                </div>
                                                <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                  <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.oscore || 'N/A'}</p>
                                                  <p className="text-xs text-muted-foreground font-medium">O-Score</p>
                                                </div>
                                                <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                  <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.rscore || 'N/A'}</p>
                                                  <p className="text-xs text-muted-foreground font-medium">R-Score</p>
                                                </div>
                                                <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                  <p className="text-2xl font-bold text-foreground">{customerDetails.qorc_scores.cscore || 'N/A'}</p>
                                                  <p className="text-xs text-muted-foreground font-medium">C-Score</p>
                                                </div>
                                              </div>
                                              <div className="bg-muted/20 rounded-lg p-3 border">
                                                <div className="flex justify-between items-center mb-2">
                                                  <span className="text-sm text-muted-foreground font-medium">Priority Score:</span>
                                                  <span className="text-lg font-bold text-foreground">{customerDetails.qorc_scores.priorityscore || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm text-muted-foreground font-medium">Ranking:</span>
                                                  <Badge variant="outline" className="text-xs font-semibold">
                                                    #{customerDetails.qorc_scores.priorityranking || 'N/A'}
                                                  </Badge>
                                                </div>
                                              </div>
                                            </CardContent>
                                          </Card>
                                        ) : null;
                                      })()}

                                    </div>

                                    {/* --- Right Column --- */}
                                    <div className="space-y-3 sm:space-y-4">

                                      {/* Customer Details Loading/Error State */}
                                      {(() => {
                                        const customerCode = customer.customer_id || customer.id;
                                        const isLoading = customerDetailsLoading[customerCode];
                                        const customerDetails = customerDetailsData[customerCode];

                                        if (isLoading) {
                                          return (
                                            <Card className="bg-background shadow-sm">
                                              <CardContent className="py-6">
                                                <div className="flex items-center justify-center">
                                                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                                  <span className="ml-2 text-sm text-muted-foreground">Loading customer details...</span>
                                                </div>
                                              </CardContent>
                                            </Card>
                                          );
                                        }

                                        if (!customerDetails) {
                                          return (
                                            <Card className="bg-background shadow-sm">
                                              <CardContent className="py-6 text-center">
                                                <p className="text-sm text-muted-foreground">Customer details not available</p>
                                              </CardContent>
                                            </Card>
                                          );
                                        }

                                        return (
                                          <>
                                            {/* Business Metrics Card */}
                                            {customerDetails?.business_metrics && (
                                              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 shadow-md border-green-200 dark:border-green-700">
                                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                    <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-lg">
                                                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                                                    </div>
                                                    Business Performance
                                                  </CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                  <div className="grid grid-cols-2 gap-3">
                                                    <div className="bg-muted/30 border rounded-lg p-3">
                                                      <p className="text-lg font-bold text-foreground">
                                                        {customerDetails.business_metrics.order_count || 0}
                                                      </p>
                                                      <p className="text-xs text-muted-foreground font-medium">Total Orders</p>
                                                      <p className="text-sm font-semibold text-foreground mt-1">
                                                        ${(customerDetails.business_metrics.order_value || 0).toLocaleString()}
                                                      </p>
                                                    </div>
                                                    <div className="bg-muted/30 border rounded-lg p-3">
                                                      <p className="text-lg font-bold text-foreground">
                                                        {customerDetails.business_metrics.quote_count || 0}
                                                      </p>
                                                      <p className="text-xs text-muted-foreground font-medium">Total Quotes</p>
                                                      <p className="text-sm font-semibold text-foreground mt-1">
                                                        ${(customerDetails.business_metrics.quote_value || 0).toLocaleString()}
                                                      </p>
                                                    </div>
                                                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                      <p className="text-lg font-bold text-foreground">
                                                        {customerDetails.business_metrics.rental_count || 0}
                                                      </p>
                                                      <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                                                    </div>
                                                    <div className="bg-muted/30 border rounded-lg p-3 text-center">
                                                      <p className="text-lg font-bold text-foreground">
                                                        {customerDetails.business_metrics.churn_rank || 'N/A'}
                                                      </p>
                                                      <p className="text-xs text-muted-foreground font-medium">Churn Risk</p>
                                                    </div>
                                                  </div>
                                                </CardContent>
                                              </Card>
                                            )}

                                            {/* Active Opportunities Card */}
                                            {(() => {
                                              const customerCode = customer.customer_id || customer.id;
                                              const customerOpportunities = customerOpportunitiesData[customerCode];
                                              
                                              // Calculate counts from opportunities response
                                              let quoteCount = 0;
                                              let orderCount = 0;
                                              let rentalCount = 0;
                                              
                                              if (customerOpportunities?.data) {
                                                customerOpportunities.data.forEach(opp => {
                                                  if (opp.type === 'quote') quoteCount++;
                                                  else if (opp.type === 'order') orderCount++;
                                                  else if (opp.type === 'rental') rentalCount++;
                                                });
                                              }
                                              
                                              return (
                                            <Card className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 shadow-md border-indigo-200 dark:border-indigo-700">
                                              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                                                    <Target className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                                  </div>
                                                  Active Quotes, Orders and Rentals
                                                </CardTitle>
                                                {/* View All Button */}
                                                {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && (
                                                  <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    className="text-xs h-6"
                                                    onClick={() => {
                                                      // Format opportunities for the dialog  
                                                      const allOpportunities = customerOpportunities?.data || [];
                                                      const quotes = allOpportunities.filter(opp => opp.type === 'quote');
                                                      const orders = allOpportunities.filter(opp => opp.type === 'order');
                                                      const rentals = allOpportunities.filter(opp => opp.type === 'rental');
                                                      
                                                      setSelectedCustomerOpportunities({
                                                        customer: customer,
                                                        details: {
                                                          ...customerDetails,
                                                          open_quotes: quotes,
                                                          open_orders: orders,
                                                          active_rentals: rentals
                                                        },
                                                        opportunities: customerOpportunities?.data || []
                                                      });
                                                      setOpportunitiesDialogOpen(true);
                                                    }}
                                                  >
                                                    View All
                                                  </Button>
                                                )}
                                              </CardHeader>
                                              <CardContent>
                                                {/* Summary counts */}
                                                <div className="grid grid-cols-3 gap-3 text-center mb-4">
                                                  <div className="bg-muted/30 border rounded-lg p-3">
                                                    <p className="text-xl font-bold text-foreground">{quoteCount}</p>
                                                    <p className="text-xs text-muted-foreground font-medium">Quotes</p>
                                                  </div>
                                                  <div className="bg-muted/30 border rounded-lg p-3">
                                                    <p className="text-xl font-bold text-foreground">{orderCount}</p>
                                                    <p className="text-xs text-muted-foreground font-medium">Orders</p>
                                                  </div>
                                                  <div className="bg-muted/30 border rounded-lg p-3">
                                                    <p className="text-xl font-bold text-foreground">{rentalCount}</p>
                                                    <p className="text-xs text-muted-foreground font-medium">Rentals</p>
                                                  </div>
                                                </div>

                                                {/* Details - only show if there are opportunities */}
                                                {(quoteCount > 0 || orderCount > 0 || rentalCount > 0) && customerOpportunities?.data && (
                                                  <div className="space-y-3 max-h-48 overflow-y-auto">
                                                    {/* Show opportunities grouped by type */}
                                                    {customerOpportunities.data
                                                      .filter(opp => opp.type === 'quote')
                                                      .slice(0, 3)
                                                      .map((quote, idx) => (
                                                      <div key={idx} className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded p-2">
                                                        <div className="flex items-center justify-between mb-1">
                                                          <div className="flex items-center gap-1">
                                                            <FileText className="w-3 h-3 text-blue-600" />
                                                            <span className="text-xs font-semibold">Quote</span>
                                                          </div>
                                                          <span className="text-xs font-bold text-blue-600">
                                                            ${(quote.estimatedvalue || 0).toLocaleString()}
                                                          </span>
                                                        </div>
                                                        <p className="text-xs font-medium truncate">{quote.opportunityname}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                          #{quote.opportunitynum} {quote.markettype ? `• ${quote.markettype}` : ''}
                                                        </p>
                                                      </div>
                                                    ))}
                                                    {quoteCount > 3 && (
                                                      <p className="text-xs text-muted-foreground text-center">
                                                        +{quoteCount - 3} more quotes
                                                      </p>
                                                    )}
                                                    
                                                    {/* Orders */}
                                                    {customerOpportunities.data
                                                      .filter(opp => opp.type === 'order')
                                                      .slice(0, 2)
                                                      .map((order, idx) => (
                                                      <div key={idx} className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800/30 rounded p-2">
                                                        <div className="flex items-center justify-between mb-1">
                                                          <div className="flex items-center gap-1">
                                                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                            <span className="text-xs font-semibold">Order</span>
                                                          </div>
                                                          <span className="text-xs font-bold text-green-600">
                                                            ${(order.estimatedvalue || 0).toLocaleString()}
                                                          </span>
                                                        </div>
                                                        <p className="text-xs font-medium truncate">{order.opportunityname}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                          #{order.opportunitynum} {order.markettype ? `• ${order.markettype}` : ''}
                                                        </p>
                                                      </div>
                                                    ))}
                                                    
                                                    {/* Rentals */}
                                                    {customerOpportunities.data
                                                      .filter(opp => opp.type === 'rental')
                                                      .slice(0, 2)
                                                      .map((rental, idx) => (
                                                      <div key={idx} className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800/30 rounded p-2">
                                                        <div className="flex items-center justify-between mb-1">
                                                          <div className="flex items-center gap-1">
                                                            <Package className="w-3 h-3 text-purple-600" />
                                                            <span className="text-xs font-semibold">Rental</span>
                                                          </div>
                                                          <span className="text-xs font-bold text-purple-600">
                                                            ${(rental.estimatedvalue || 0).toLocaleString()}
                                                          </span>
                                                        </div>
                                                        <p className="text-xs font-medium truncate">{rental.opportunityname}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                          #{rental.opportunitynum}
                                                        </p>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </CardContent>
                                            </Card>
                                            );
                                          })()}

                                            {/* Action Items Card */}
                                            {customerDetails?.action_items && customerDetails.action_items.length > 0 && (
                                              <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 shadow-md border-amber-200 dark:border-amber-700">
                                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                                                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900 rounded-lg">
                                                      <ListChecks className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                                    </div>
                                                    Action Items ({customerDetails.action_items.length})
                                                  </CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                  <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {customerDetails.action_items.map((action, i) => (
                                                      <div key={i} className="group flex items-start gap-3 bg-muted/20 hover:bg-muted/30 border rounded-lg p-3 transition-colors">
                                                        <CheckSquare className="w-4 h-4 mt-0.5 text-muted-foreground group-hover:text-foreground flex-shrink-0 transition-colors" />
                                                        <div className="flex-1">
                                                          <p className="text-sm text-foreground leading-relaxed">{action}</p>
                                                          <div className="flex items-center gap-2 mt-2">
                                                            <Badge variant="outline" className="text-xs">
                                                              Item #{i + 1}
                                                            </Badge>
                                                            <span className="text-xs text-muted-foreground">Priority: Normal</span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    ))}
                                                    {customerDetails.action_items.length === 0 && (
                                                      <div className="text-center py-4 text-muted-foreground">
                                                        <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                        <p className="text-sm">No action items found</p>
                                                      </div>
                                                    )}
                                                  </div>
                                                </CardContent>
                                              </Card>
                                            )}

                                            {/* Churn Metrics Card */}
                                            <ChurnMetricsCard 
                                              customerCode={customer.customer_id || customer.id}
                                              churnData={getChurnDataForCustomer(customer.customer_id || customer.customer_code || customer.code || customer.id)}
                                              isLoading={churnLoading}
                                            />
                                          </>
                                        );
                                      })()}

                                      {/* PrioritizedDataCard - if it shows different data than what we have above */}
                                      <PrioritizedDataCard
                                        customer={customer}
                                      />
                                    </div>
                                  </div>
                                </ScrollArea>
                                    );
                                })()}
                                {/* End of main dialog ScrollArea */}
                                {/* Add update status button for RSMs at the bottom of the dialog content */}
                                {isRSM && (
                                  <div className="p-4 border-t grid grid-cols-2 gap-2">
                                    <Button
                                      onClick={() =>
                                        handleOpenProgressDialog(customer)
                                      }
                                    >
                                      Update Status
                                    </Button>
                                    <Button
                                      onClick={() =>
                                        handleOpenHubspotDialog(customer)
                                      }
                                      variant="outline"
                                    >
                                      <Projector className="mr-2 h-4 w-4" />
                                      Update HubSpot
                                    </Button>
                                  </div>
                                )}
                                {/* Add BM notes button in the customer dialog for BMs */}
                                {isBM && (
                                  <div className="p-4 border-t">
                                    <Button
                                      onClick={() =>
                                        handleOpenBmNotesDialog(customer)
                                      }
                                      className="w-full"
                  variant="outline"
                                    >
                                      {customer.bm_notes
                                        ? "Edit Notes"
                                        : "Add Notes"}
                                    </Button>
                                  </div>
                                )}
                                {/* Show BM notes if they exist and user is RSM or BM */}
                                {customer.bm_notes && (
                                  <div className="px-4 pb-4">
                                    <div className="bg-muted/40 border rounded-md p-3">
                                      <div className="font-medium text-sm mb-1">
                                        Business Manager Notes:
                                      </div>
                                      <div className="text-sm whitespace-pre-wrap">
                                        {customer.bm_notes.notes}
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-1">
                                        Updated:{" "}
                                        {formatDate(
                                          customer.bm_notes.updated_at,
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Text className="text-muted-foreground italic text-center py-6">
                        No customer stops in this itinerary.
                      </Text>
                    )}
                    
                    {/* Pagination Controls */}
                    {totalCustomers > 0 && (
                      <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-muted-foreground">
                          {customersLoading ? (
                            "Loading..."
                          ) : (
                            <>
                              Showing {((currentPage - 1) * stopsPerPage) + 1} - {Math.min(currentPage * stopsPerPage, totalCustomers)} of {totalCustomers} customers
                            </>
                          )}
                        </div>
                        {totalCustomerPages > 1 && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newPage = Math.max(1, currentPage - 1);
                                setCurrentPage(newPage);
                                fetchPaginatedCustomers(newPage, stopsPerPage);
                              }}
                              disabled={currentPage === 1 || customersLoading}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </Button>
                            <div className="flex items-center gap-1">
                              <span className="text-sm">
                                Page {currentPage} of {totalCustomerPages}
                              </span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newPage = Math.min(totalCustomerPages, currentPage + 1);
                                setCurrentPage(newPage);
                                fetchPaginatedCustomers(newPage, stopsPerPage);
                              }}
                              disabled={currentPage === totalCustomerPages || customersLoading}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    
                    <div className={`${isMobile ? "" : "mt-6"}`}>
                      {/* Customer Stops Feedback */}
                      <div className={`${isMobile ? "mt-4 px-1" : "mt-6"}`}>
                        <Card className="shadow-sm">
                          <CardContent className={`p-3 ${isMobile ? 'pt-3 pb-2' : 'p-4'}`}> 
                            <FeedbackComponent
                              question="How useful are the customer details and prioritized data?"
                              feedback={customerStopsFeedback}
                              setFeedback={setCustomerStopsFeedback}
                              feedbackKey="customer-stops"
                              context={feedbackContext}
                              showRatings={true}
                              ratingLabels={[
                                "Completeness",
                                "Accuracy",
                                "Prioritization",
                                "Overall",
                              ]}
                              isMobile={isMobile}
                            />
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Save Changes Button for Stops Tab */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex justify-center">
                      <Button
                        onClick={handleSaveChanges}
                        disabled={!hasUnsavedChanges || loading}
                        className="w-full sm:w-auto"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving Changes...
                          </>
                        ) : hasUnsavedChanges ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            No Changes to Save
                          </>
                        )}
                      </Button>
                    </div>
                    {hasUnsavedChanges && (
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        You have unsaved changes that need to be saved.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Add Stops tab content */}
              <TabsContent value="add-stops" className="space-y-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5" />
                      Add Nearby Opportunities
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Find and add nearby opportunities to your itinerary. Showing opportunities in the same market, region, or within reasonable distance.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Search and Load Section */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder="Search opportunities by name, code, city, or state..."
                        value={opportunitySearchQuery}
                        onChange={(e) => setOpportunitySearchQuery(e.target.value)}
                        className="flex-1"
                      />
                      <Button 
                        onClick={fetchNearbyOpportunities}
                        disabled={opportunitiesLoading}
                        variant="outline"
                      >
                        {opportunitiesLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Error State */}
                    {opportunitiesError && (
                      <div className="p-4 border border-red-200 bg-red-50 rounded-md">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertCircle className="h-4 w-4" />
                          <span className="font-medium">Error loading opportunities</span>
                        </div>
                        <p className="text-sm text-red-600 mt-1">{opportunitiesError}</p>
                      </div>
                    )}

                    {/* Selected Count and Actions */}
                    {selectedOpportunities.size > 0 && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <span className="text-sm font-medium text-blue-900">
                          {selectedOpportunities.size} {selectedOpportunities.size !== 1 ? 'opportunities' : 'opportunity'} selected
                        </span>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedOpportunities(new Set())}
                          >
                            Clear Selection
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleAddOpportunities}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Add to Itinerary
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Opportunities List */}
                    <div className="space-y-2">
                      {opportunitiesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Loading nearby opportunities...</p>
                          </div>
                        </div>
                      ) : filteredOpportunities.length === 0 ? (
                        <div className="text-center py-8">
                          <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                          <h3 className="font-medium text-muted-foreground mb-1">
                            {availableOpportunities.length === 0 ? 'No nearby opportunities found' : 'No opportunities match your search'}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {availableOpportunities.length === 0 
                              ? 'Try refreshing or check if there are opportunities in nearby areas.'
                              : 'Try adjusting your search terms or clear the search to see all opportunities.'
                            }
                          </p>
                          {availableOpportunities.length === 0 && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="mt-3"
                              onClick={fetchNearbyOpportunities}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Try Again
                            </Button>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-muted-foreground mb-2">
                            Showing {filteredOpportunities.length} nearby opportunities
                          </div>
                          <ScrollArea className="h-[400px]">
                            <div className="space-y-2 pr-4">
                              {filteredOpportunities.map((opportunity) => {
                                const isSelected = selectedOpportunities.has(opportunity._uniqueId);
                                return (
                                  <div
                                    key={opportunity._uniqueId}
                                    className={cn(
                                      "p-3 border rounded-md cursor-pointer transition-colors",
                                      isSelected 
                                        ? "border-blue-500 bg-blue-50" 
                                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                    )}
                                  >
                                    <div className="flex items-start gap-3">
                                      <Checkbox 
                                        id={`opportunity-${opportunity._uniqueId}`}
                                        checked={isSelected}
                                        onCheckedChange={(checked) => {
                                          const newSelected = new Set(selectedOpportunities);
                                          if (checked) {
                                            newSelected.add(opportunity._uniqueId);
                                          } else {
                                            newSelected.delete(opportunity._uniqueId);
                                          }
                                          setSelectedOpportunities(newSelected);
                                        }}
                                        className="mt-1"
                                      />
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-medium text-sm">
                                            {opportunity.customername}
                                          </h4>
                                          {opportunity.customercode && (
                                            <Badge variant="outline" className="text-xs">
                                              {opportunity.customercode}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground space-y-1">
                                          {opportunity.formatted_address && (
                                            <div className="flex items-center gap-1">
                                              <MapPin className="h-3 w-3" />
                                              {opportunity.formatted_address}
                                            </div>
                                          )}
                                          {opportunity.salesregion && (
                                            <div className="flex items-center gap-1">
                                              <Building2 className="h-3 w-3" />
                                              Region: {opportunity.salesregion}
                                            </div>
                                          )}
                                          {opportunity.market && (
                                            <div className="flex items-center gap-1">
                                              <Target className="h-3 w-3" />
                                              Market: {opportunity.market}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </ScrollArea>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* New Notes tab content */}
              <TabsContent value="notes" className="space-y-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Schedule Notes</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* BM Notes Card */}
                    <div>
                      <h3 className="text-base font-medium mb-2">
                        Business Manager Notes
                      </h3>
                      <Card className="bg-background shadow-sm">
                        <CardHeader className="p-3 pb-2">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-sm font-medium">
                              Business Manager Notes
                            </CardTitle>
                            {isBM && (
                              <Button
                                variant="ghost"
                  size="sm"
                                className="flex items-center gap-1 h-7"
                                onClick={() => handleOpenBmNotesDialog(null)} // Pass null for schedule-level notes
                >
                                <Edit className="h-3.5 w-3.5" />
                                {clusterData?.bm_notes ? "Edit" : "Add"}
                </Button>
              )}
                          </div>
                        </CardHeader>
                        <CardContent className="p-3 pt-0">
                          {clusterData?.bm_notes ? (
                            <div>
                              <div className="text-sm whitespace-pre-wrap border-l-2 border-primary/20 pl-3 py-1">
                                {clusterData.bm_notes.notes}
                              </div>
                              <div className="text-xs text-muted-foreground mt-2">
                                Updated:{" "}
                                {formatDate(clusterData.bm_notes.updated_at)}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              No business manager notes for this schedule.
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* RSM Notes Card */}
                    <div>
                      <h3 className="text-base font-medium mb-2">RSM Notes</h3>
                      <Card className="bg-background shadow-sm">
                        <CardHeader className="p-3 pb-2">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-sm font-medium">
                              RSM Notes
                            </CardTitle>
                            {isRSM && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-1 h-7"
                                onClick={handleOpenRsmNotesDialog}
                              >
                                <Edit className="h-3.5 w-3.5" />
                                {clusterData?.rsm_notes ? "Edit" : "Add"}
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="p-3 pt-0">
                          {clusterData?.rsm_notes ? (
                            <div>
                              <div className="text-sm whitespace-pre-wrap border-l-2 border-secondary/30 pl-3 py-1">
                                {clusterData.rsm_notes.notes}
                              </div>
                              <div className="text-xs text-muted-foreground mt-2">
                                Updated:{" "}
                                {formatDate(clusterData.rsm_notes.updated_at)}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              No RSM notes for this schedule.
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* New Messages tab content */}
              <TabsContent value="messages" className="mt-0 h-[70vh] sm:h-[600px]">
                <MessageCenter
                  scheduleId={currentClusterId}
                  rsmId={clusterData?.assigned_to?.id}
                  rsmName={clusterData?.assigned_to?.full_name}
                />
              </TabsContent>
            </div>
            
            {/* Overall Itinerary Feedback - Move inside scrollable area on mobile */}
            <div className={`${isMobile ? "mt-4 mb-20" : "mt-8 border-t pt-6"} flex-shrink-0 px-1 pb-4`}>
              {/* Overall Itinerary Feedback */}
              <Card className="shadow-sm">
                <CardContent className={`p-3 ${isMobile ? 'pt-3 pb-2' : 'p-4'}`}> 
                  <FeedbackComponent
                    question="Overall, how helpful is this generated itinerary?"
                    feedback={itineraryFeedback}
                    setFeedback={setItineraryFeedback}
                    feedbackKey="itinerary" 
                    context={feedbackContext}
                    showRatings={true}
                    ratingLabels={["Grouping", "Efficiency", "Coverage", "Overall"]}
                    isMobile={isMobile}
                  />
                </CardContent>
              </Card>
            </div>
            
            {/* Mobile Bottom Navigation */}
            {isMobile && !isMapFullScreen && <MobileBottomNav />}
          </Tabs>
        </div>
      )}

      {/* Reasoning Dialog */}
      {reasoning && (
        <Dialog
          open={showReasoningDialog}
          onOpenChange={setShowReasoningDialog}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Generation Reasoning</DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 p-4 rounded-md border">
              <div
                dangerouslySetInnerHTML={{
                  __html: marked.parse(reasoning || ""),
                }}
              />
            </div>
            <div className="mt-6">
              {/* Reasoning Feedback */}
              <FeedbackComponent
                question="Was this reasoning helpful?"
                feedback={reasoningFeedback}
                setFeedback={setReasoningFeedback}
                feedbackKey="reasoning"
                context={feedbackContext}
                isMobile={isMobile}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add stop progress dialog at the end of the component */}
      {isRSM && (
        <Dialog open={stopProgressOpen} onOpenChange={setStopProgressOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Customer Stop Status</DialogTitle>
              <DialogDescription>
                Update status and add notes for {selectedStop?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select value={stopStatus} onValueChange={setStopStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">PENDING</SelectItem>
                    <SelectItem value="IN_PROGRESS">IN PROGRESS</SelectItem>
                    <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                    <SelectItem value="CANCELLED">CANCELLED</SelectItem>
                    <SelectItem value="RESCHEDULED">RESCHEDULED</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={stopNotes}
                  onChange={(e) => setStopNotes(e.target.value)}
                  placeholder="Add notes about this customer stop"
                  rows={4}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setStopProgressOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleUpdateStopProgress}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* HubSpot Contact Info Dialog */}
      {isRSM && (
        <Dialog 
          open={contactFormOpen} 
          onOpenChange={(open) => {
            setContactFormOpen(open);
            if (!open) {
              // Only reset if both forms are closed
              if (!engagementFormOpen) {
                resetFormState();
              }
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
                <DialogTitle>HubSpot Contact Information</DialogTitle>
                <DialogDescription>
                  Enter customer contact details for HubSpot
                </DialogDescription>
              </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">First Name*</label>
                  <Input 
                    value={contactInfo.first_name}
                    onChange={(e) => setContactInfo({...contactInfo, first_name: e.target.value})}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Last Name*</label>
                  <Input 
                    value={contactInfo.last_name}
                    onChange={(e) => setContactInfo({...contactInfo, last_name: e.target.value})}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Email Address*</label>
                <Input 
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                  placeholder="email@example.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone Number</label>
                <Input 
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setContactFormOpen(false);
                  setStopProgressOpen(true);
                  resetFormState();
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={loading}
                onClick={() => {
                  // Validate required fields
                  if (!contactInfo.first_name || !contactInfo.last_name || !contactInfo.email) {
                    toast({
                      title: "Missing Information",
                      description: "Please fill in all required fields",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  setContactFormOpen(false);
                  setEngagementFormOpen(true);
                }}
              >
                Next
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* HubSpot Engagement Type Dialog */}
      {isRSM && (
        <Dialog 
          open={engagementFormOpen} 
          onOpenChange={(open) => {
            setEngagementFormOpen(open);
            if (!open) {
              // Only reset if both forms are closed
              if (!contactFormOpen) {
                resetFormState();
              }
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
                <DialogTitle>HubSpot Engagement Details</DialogTitle>
                <DialogDescription>
                  Enter details about this HubSpot engagement
                </DialogDescription>
              </DialogHeader>
            
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Whom did you meet?*</label>
                <Select 
                  value={engagementInfo.contact_id || ""}
                  onValueChange={(value) => {
                    if (value === "new_contact") {
                      // Handle new contact flow - Go back to contact form
                      setEngagementFormOpen(false);
                      setContactFormOpen(true);
                    } else {
                      // Find selected contact and populate contactInfo
                      const selectedContact = hubspotContacts.find(contact => contact.id === value);
                      if (selectedContact) {
                        setContactInfo({
                          first_name: selectedContact.properties.firstname || "",
                          last_name: selectedContact.properties.lastname || "",
                          email: selectedContact.properties.email || "",
                          phone: selectedContact.properties.phone || ""
                        });
                      }
                      setEngagementInfo({...engagementInfo, contact_id: value});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {hubspotLoading ? (
                      <SelectItem value="" disabled>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading contacts...
                      </SelectItem>
                    ) : hubspotContacts && hubspotContacts.length > 0 ? (
                      <>
                        {hubspotContacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.properties.firstname} {contact.properties.lastname} 
                            {contact.properties.email ? ` (${contact.properties.email})` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="new_contact" className="text-primary font-medium">
                          + Add New Contact
                        </SelectItem>
                      </>
                    ) : (
                      <SelectItem value="new_contact">
                        + Add New Contact
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {hubspotError && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Error loading contacts: {hubspotError}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Engagement Type*</label>
                <Select 
                  value={engagementInfo.engagement_type || ""} 
                  onValueChange={(value) => setEngagementInfo({...engagementInfo, engagement_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CALL">CALL</SelectItem>
                    <SelectItem value="MEETING">MEETING</SelectItem>
                    <SelectItem value="EMAIL">EMAIL</SelectItem>
                    <SelectItem value="TASK">TASK</SelectItem>
                    <SelectItem value="NOTE">NOTE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes*</label>
                <Textarea
                  value={engagementInfo.notes}
                  onChange={(e) => setEngagementInfo({...engagementInfo, notes: e.target.value})}
                  placeholder="Add details about what was discussed during this engagement"
                  rows={5}
                  required
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEngagementFormOpen(false);
                  resetFormState();
                }}
              >
                Cancel
              </Button>
              <Button 
                disabled={loading}
                onClick={async () => {
                  // Validate required fields
                  if (!engagementInfo.contact_id) {
                    toast({
                      title: "Missing Information",
                      description: "Please select a contact or add a new one",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  if (!engagementInfo.engagement_type || !engagementInfo.notes) {
                    toast({
                      title: "Missing Information",
                      description: "Please select an engagement type and provide notes",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  // Double check contact info requirements
                  if (!contactInfo.first_name || !contactInfo.last_name || !contactInfo.email) {
                    toast({
                      title: "Missing Contact Information",
                      description: "Please go back and complete all required contact fields",
                      variant: "destructive",
                    });
                    setEngagementFormOpen(false);
                    setContactFormOpen(true);
                    return;
                  }
                  
                  // Validate email format
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(contactInfo.email)) {
                    toast({
                      title: "Invalid Email",
                      description: "Please enter a valid email address",
                      variant: "destructive",
                    });
                    setEngagementFormOpen(false);
                    setContactFormOpen(true);
                    return;
                  }
                  
                  // Submit HubSpot engagement
                  setLoading(true);
                  try {
                    let contactId = engagementInfo.contact_id;
                    
                    // If "new_contact" was selected, create the contact first
                    if (contactId === "new_contact") {
                      
                      const contactResult = await createContact(contactInfo);
                      if (contactResult?.status === "success" && contactResult?.data?.id) {
                        contactId = contactResult.data.id;
                        
                      } else {
                        throw new Error("Failed to create contact in HubSpot");
                      }
                    }
                    
                    // Validate we have a valid contact ID
                    if (!contactId || contactId === "new_contact") {
                      throw new Error("No valid contact selected for engagement");
                    }
                    
                    // Get the customer_id from the selected stop
                    const customerId = selectedStop?.id;
                    

                    
                    // Create HubSpot engagement using updated API with customer_id and itinerary_id
                    const engagementResponse = await createEngagement({
                      engagement_type: engagementInfo.engagement_type || "MEETING",
                      notes: engagementInfo.notes,
                      contact_id: contactId,
                      customer_id: customerId, // Add customer_id for proper association
                      itinerary_id: currentClusterId // Add itinerary_id to track in itinerary
                    });
                    
                    toast({
                      title: "Success",
                      description: `${engagementInfo.engagement_type || "Meeting"} engagement recorded successfully in HubSpot`,
                    });
                    
                    // Log the engagement response
                    
                    
                    // Handle the case where the engagement was created but not properly associated
                    if (engagementResponse?.status === "success" && 
                        engagementResponse?.data?.engagement?.id && 
                        customerId) {
                      
                      // Check if we need to manually associate the engagement with the customer
                      const engagementId = engagementResponse.data.engagement.id;
                      
                      try {
                        
                        
                        // Prepare the engagement data for association
                        const engagementData = {
                          id: engagementId,
                          contact_id: contactId,
                          engagement_type: engagementInfo.engagement_type || "MEETING",
                          notes: engagementInfo.notes
                        };
                        
                        // Try to manually associate using the new endpoint
                        const associationResult = await associateEngagementWithCustomer(
                          customerId,
                          engagementData
                        );
                        
                      } catch (associationError) {
                        console.error("Error manually associating engagement with customer:", associationError);
                        // We don't want to fail the whole transaction if just the association fails
                      }
                    }
                    
                    // Close dialog and refresh data
                    await fetchScheduleFromApi(currentClusterId);
                    setEngagementFormOpen(false);
                    resetFormState();
                  } catch (err) {
                    console.error("Error creating HubSpot engagement:", err);
                    const errorMessage =
                      err.response?.data?.message ||
                      err.message ||
                      "Failed to create HubSpot engagement";
                    toast({
                      title: "Error",
                      description: errorMessage,
                      variant: "destructive",
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      
      {/* Add BM notes dialog */}
      {isBM && (
        <Dialog open={bmNotesOpen} onOpenChange={setBmNotesOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                BM Notes for {selectedStop ? selectedStop.name : "Schedule"}
              </DialogTitle>
              <DialogDescription>
                Add or update notes{" "}
                {selectedStop ? "for this customer stop" : "for this schedule"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={bmNotes}
                  onChange={(e) => setBmNotes(e.target.value)}
                  placeholder="Add notes..."
                  rows={6}
                />
            </div>
          </div>
          
            <DialogFooter>
              <Button variant="outline" onClick={() => setBmNotesOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateBmNotes}>Save Notes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* RSM Notes Dialog */}
      {isRSM && (
        <Dialog open={rsmNotesOpen} onOpenChange={setRsmNotesOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>RSM Notes for Schedule</DialogTitle>
              <DialogDescription>
                Add or update your notes for this schedule
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={rsmNotes}
                  onChange={(e) => setRsmNotes(e.target.value)}
                  placeholder="Add your notes about this schedule"
                  rows={6}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setRsmNotesOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRsmNotes}>Save Notes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Assign RSM Modal */}
      {isBM && isAssignModalOpen && clusterData && (
        <AssignRsmModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          clusterId={currentClusterId}
          clusterName={clusterData.metadata?.itinerary_name}
          onAssignComplete={handleAssignComplete}
        />
      )}

      {/* Contact Selector Dialog (BM and RSM) */}
      {(isBM || isRSM) && (
        <Dialog open={contactSelectorOpen} onOpenChange={setContactSelectorOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Contacts for {selectedStopForContacts?.name}</DialogTitle>
              <DialogDescription>
                Select contacts to associate with this stop
              </DialogDescription>
            </DialogHeader>
            
            {selectedStopForContacts && (
              <ContactSelector
                customerId={selectedStopForContacts.customer_id || selectedStopForContacts.id}
                stopId={selectedStopForContacts.stop_id}  // Pass the actual stop ID
                onContactsSelected={() => {
                  setContactSelectorOpen(false);
                  // Optionally refresh data
                }}
                onClose={() => setContactSelectorOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Contacts View Dialog (Both BM and RSM) */}
      <Dialog open={contactsViewOpen} onOpenChange={setContactsViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contacts for {selectedStopForContacts?.name}</DialogTitle>
            <DialogDescription>
              {isRSM ? "View contacts and sync engagement data to HubSpot" : "View and manage contacts for this stop"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedStopForContacts && (() => {
            
            
            // Use customer_id from the customer object (not 'id')
            const customerId = selectedStopForContacts.customer_id || selectedStopForContacts.id;
            

            
            return (
              <StopContacts
                key={`contacts-view-${customerId}-${Date.now()}`}
                stopId={selectedStopForContacts.stop_id || null}
                customerId={customerId}
                onUpdate={() => {
                  
                }}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Opportunities Dialog using standard Dialog component */}
      <Dialog open={opportunitiesDialogOpen && selectedCustomerOpportunities} onOpenChange={(open) => {
        if (!open) {
          setOpportunitiesDialogOpen(false);
          setOpportunitiesFilter('all');
          setOpportunitiesSort('amount_desc');
          setOpportunitiesSearch('');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomerOpportunities?.customer?.name || 'Customer'} - Opportunities
            </DialogTitle>
          </DialogHeader>
          
          {/* Filters and Search */}
          <div className="flex flex-col gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search opportunities..."
                  value={opportunitiesSearch}
                  onChange={(e) => setOpportunitiesSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={opportunitiesFilter}
                  onChange={(e) => setOpportunitiesFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Types</option>
                  <option value="quotes">Quotes Only</option>
                  <option value="orders">Orders Only</option>
                  <option value="rentals">Rentals Only</option>
                </select>
                
                <select
                  value={opportunitiesSort}
                  onChange={(e) => setOpportunitiesSort(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="amount_desc">Amount (High to Low)</option>
                  <option value="amount_asc">Amount (Low to High)</option>
                  <option value="name_asc">Name (A to Z)</option>
                  <option value="date_desc">Created (Newest First)</option>
                  <option value="date_asc">Created (Oldest First)</option>
                  <option value="updated_desc">Updated (Newest First)</option>
                  <option value="status_asc">Status (A to Z)</option>
                  <option value="delivery_asc">Delivery Date (Earliest First)</option>
                </select>
              </div>
            </div>
            
            {/* Additional Filters */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="mappable-only"
                checked={showOnlyMappable}
                onCheckedChange={setShowOnlyMappable}
              />
              <label htmlFor="mappable-only" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Show only opportunities with location coordinates
              </label>
            </div>
          </div>
          
          <div className="space-y-6">
              {(() => {
                // Safety check for null data
                if (!selectedCustomerOpportunities || !selectedCustomerOpportunities.details) {
                  
                  return (
                    <div className="text-center py-8 text-gray-500">
                      No opportunity data available.
                    </div>
                  );
                }
                
                const quotes = selectedCustomerOpportunities.details.open_quotes || [];
                const orders = selectedCustomerOpportunities.details.open_orders || [];
                const rentals = selectedCustomerOpportunities.details.active_rentals || [];
                
                // Combine all opportunities with type information
                const allOpportunities = [
                  ...quotes.map(q => ({ 
                    ...q, 
                    type: 'quote', 
                    displayName: q.opportunityname || 'Unnamed Quote', 
                    id: q.opportunitynum,
                    amount: q.estimatedvalue || 0,
                    latitude: q.coordinates?.lat || null,
                    longitude: q.coordinates?.lng || null
                  })),
                  ...orders.map(o => ({ 
                    ...o, 
                    type: 'order', 
                    displayName: o.opportunityname || 'Unnamed Order', 
                    id: o.opportunitynum,
                    amount: o.estimatedvalue || 0,
                    latitude: o.coordinates?.lat || null,
                    longitude: o.coordinates?.lng || null
                  })),
                  ...rentals.map(r => ({ 
                    ...r, 
                    type: 'rental', 
                    displayName: r.opportunityname || 'Unnamed Rental', 
                    id: r.opportunitynum,
                    amount: r.estimatedvalue || 0,
                    latitude: r.coordinates?.lat || null,
                    longitude: r.coordinates?.lng || null
                  }))
                ];
                
                // Apply search filter
                const searchFiltered = allOpportunities.filter(item => {
                  if (!opportunitiesSearch) return true;
                  const searchTerm = opportunitiesSearch.toLowerCase();
                  return (
                    item.displayName?.toLowerCase().includes(searchTerm) ||
                    item.id?.toString().toLowerCase().includes(searchTerm) ||
                    item.amount?.toString().includes(searchTerm) ||
                    item.description?.toLowerCase().includes(searchTerm) ||
                    item.statusname?.toLowerCase().includes(searchTerm) ||
                    item.address?.toLowerCase().includes(searchTerm) ||
                    item.deliveryaddress?.toLowerCase().includes(searchTerm) ||
                    item.siteaddress?.toLowerCase().includes(searchTerm) ||
                    item.city?.toLowerCase().includes(searchTerm) ||
                    item.state?.toLowerCase().includes(searchTerm) ||
                    item.ProductName?.toLowerCase().includes(searchTerm) ||
                    item.Category?.toLowerCase().includes(searchTerm)
                  );
                });
                
                // Apply type filter
                const typeFiltered = searchFiltered.filter(item => {
                  if (opportunitiesFilter === 'all') return true;
                  return item.type === opportunitiesFilter.slice(0, -1); // Remove 's' from 'quotes', 'orders', 'rentals'
                });
                
                // Apply coordinate filter
                const coordinateFiltered = typeFiltered.filter(item => {
                  if (!showOnlyMappable) return true;
                  return item.latitude && item.longitude && item.latitude !== 0 && item.longitude !== 0;
                });
                
                // Apply sorting
                const sorted = [...coordinateFiltered].sort((a, b) => {
                  switch (opportunitiesSort) {
                    case 'amount_desc':
                      return (b.amount || 0) - (a.amount || 0);
                    case 'amount_asc':
                      return (a.amount || 0) - (b.amount || 0);
                    case 'name_asc':
                      return (a.displayName || '').localeCompare(b.displayName || '');
                    case 'date_desc':
                      return new Date(b.lastupdate || 0) - new Date(a.lastupdate || 0);
                    case 'date_asc':
                      return new Date(a.lastupdate || 0) - new Date(b.lastupdate || 0);
                    case 'updated_desc':
                      return new Date(b.lastupdate || 0) - new Date(a.lastupdate || 0);
                    case 'status_asc':
                      return (a.statusname || '').localeCompare(b.statusname || '');
                    case 'delivery_asc':
                      const aDate = a.expecteddeliverydate || a.estimatedclosedate || a.enddate || '9999-12-31';
                      const bDate = b.expecteddeliverydate || b.estimatedclosedate || b.enddate || '9999-12-31';
                      return new Date(aDate) - new Date(bDate);
                    default:
                      return 0;
                  }
                });
                
                // Calculate filtered totals
                const filteredQuotes = sorted.filter(item => item.type === 'quote');
                const filteredOrders = sorted.filter(item => item.type === 'order');
                const filteredRentals = sorted.filter(item => item.type === 'rental');
                
                return (
                  <div className="space-y-6">
                    {/* Summary cards with filtered counts */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded">
                        <h3 className="font-bold text-blue-700 dark:text-blue-300">Quotes</h3>
                        <p className="text-2xl font-bold">
                          {filteredQuotes.length}
                          {opportunitiesFilter !== 'all' || opportunitiesSearch ? 
                            <span className="text-sm text-gray-500">/{quotes.length}</span> : ''
                          }
                        </p>
                        <p className="text-sm">${filteredQuotes.reduce((sum, q) => sum + (q.amount || 0), 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded">
                        <h3 className="font-bold text-green-700 dark:text-green-300">Orders</h3>
                        <p className="text-2xl font-bold">
                          {filteredOrders.length}
                          {opportunitiesFilter !== 'all' || opportunitiesSearch ? 
                            <span className="text-sm text-gray-500">/{orders.length}</span> : ''
                          }
                        </p>
                        <p className="text-sm">${filteredOrders.reduce((sum, o) => sum + (o.amount || 0), 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded">
                        <h3 className="font-bold text-purple-700 dark:text-purple-300">Rentals</h3>
                        <p className="text-2xl font-bold">
                          {filteredRentals.length}
                          {opportunitiesFilter !== 'all' || opportunitiesSearch ? 
                            <span className="text-sm text-gray-500">/{rentals.length}</span> : ''
                          }
                        </p>
                        <p className="text-sm">${filteredRentals.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}</p>
                      </div>
                    </div>
                    
                    {/* Filtered and sorted results */}
                    <div className="space-y-4">
                      {sorted.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No opportunities found matching your filters.
                        </div>
                      ) : (
                        sorted.map((item, idx) => (
                          <div key={`${item.type}-${idx}`} className={`p-4 rounded-lg mb-3 border ${
                            item.type === 'quote' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' :
                            item.type === 'order' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' :
                            'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700'
                          }`}>
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                                    item.type === 'quote' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' :
                                    item.type === 'order' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                                    'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                                  }`}>
                                    {item.type.toUpperCase()}
                                  </span>
                                  {item.statusname && (
                                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                      {item.statusname}
                                    </span>
                                  )}
                                </div>
                                <h4 className="font-semibold text-lg mb-1">{item.displayName}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">{item.id}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-gray-900 dark:text-white">
                                  ${(item.amount || 0).toLocaleString()}
                                </p>
                                {item.type === 'rental' && item.monthlyrate && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    ${item.monthlyrate}/month
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Additional Details Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              {/* Last Updated */}
                              {item.lastupdate && (
                                <div>
                                  <p className="text-gray-500 dark:text-gray-400 font-medium">Last Updated</p>
                                  <p className="text-gray-900 dark:text-white">
                                    {new Date(item.lastupdate).toLocaleDateString()}
                                  </p>
                                </div>
                              )}
                              
                              {/* Type-specific information */}
                              {item.type === 'quote' && (
                                <>
                                  {/* Quote-specific fields removed per request */}
                                </>
                              )}
                              
                              {item.type === 'order' && (
                                <>
                                  {item.expecteddeliverydate && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">Expected Delivery</p>
                                      <p className="text-gray-900 dark:text-white">
                                        {new Date(item.expecteddeliverydate).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                  {item.priority && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">Priority</p>
                                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                                        item.priority === 'High' ? 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' :
                                        item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100' :
                                        'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                      }`}>
                                        {item.priority}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {item.type === 'rental' && (
                                <>
                                  {item.startdate && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">Start Date</p>
                                      <p className="text-gray-900 dark:text-white">
                                        {new Date(item.startdate).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                  {item.enddate && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">End Date</p>
                                      <p className="text-gray-900 dark:text-white">
                                        {new Date(item.enddate).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                  {item.quantity && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">Quantity</p>
                                      <p className="text-gray-900 dark:text-white">{item.quantity} units</p>
                                    </div>
                                  )}
                                  {item.Category && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400 font-medium">Category</p>
                                      <p className="text-gray-900 dark:text-white">{item.Category}</p>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {/* Owner/Contact Information - removed per request */}
                              
                              {/* Revenue Information */}
                              {item.estimatedvalue && item.estimatedvalue !== item.amount && (
                                <div>
                                  <p className="text-gray-500 dark:text-gray-400 font-medium">Estimated Value</p>
                                  <p className="text-gray-900 dark:text-white">${item.estimatedvalue.toLocaleString()}</p>
                                </div>
                              )}
                            </div>
                            
                            {/* Location Information */}
                            {(item.address || item.deliveryaddress || item.siteaddress) && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-xs mb-2">
                                  {item.type === 'quote' ? 'Location' : 
                                   item.type === 'order' ? 'Delivery Address' : 'Site Address'}
                                </p>
                                <div className="flex items-start gap-2">
                                  <MapIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                      {item.address || item.deliveryaddress || item.siteaddress}
                                    </p>
                                    {(item.city || item.state) && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {[item.city, item.state, item.zipcode].filter(Boolean).join(', ')}
                                      </p>
                                    )}
                                    <div className="flex items-center justify-between mt-2">
                                      {(item.latitude && item.longitude) && (
                                        <p className="text-xs text-gray-400 font-mono">
                                          {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
                                        </p>
                                      )}
                                      {(item.latitude && item.longitude && item.latitude !== 0 && item.longitude !== 0) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 px-2 text-xs"
                                          onClick={() => {
                                            setSelectedMapOpportunity(item);
                                            setMapPopupOpen(true);
                                          }}
                                        >
                                          <MapIcon className="w-3 h-3 mr-1" />
                                          View Map
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Description */}
                            {item.description && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-xs mb-1">Description</p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  {item.description}
                                </p>
                              </div>
                            )}
                            
                            {/* Product Details for Rentals */}
                            {item.type === 'rental' && item.ProductName && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-xs mb-2">Product Details</p>
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-gray-400" />
                                  <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                                    {item.ProductName}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Legacy sections (hidden when using unified view) */}
                    <div className="space-y-4" style={{ display: 'none' }}>
                      {quotes.length > 0 && (
                        <div>
                          <h3 className="font-bold mb-2">Open Quotes</h3>
                          {quotes.map((quote, idx) => (
                            <div key={idx} className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded mb-2">
                              <div className="flex justify-between">
                                <div>
                                  <p className="font-medium">{quote.opportunityname || 'Unnamed Quote'}</p>
                                  <p className="text-sm text-gray-600">{quote.opportunitynum}</p>
                                </div>
                                <p className="font-bold">${(quote.amount || 0).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {orders.length > 0 && (
                        <div>
                          <h3 className="font-bold mb-2">Open Orders</h3>
                          {orders.map((order, idx) => (
                            <div key={idx} className="bg-green-50 dark:bg-green-900/20 p-3 rounded mb-2">
                              <div className="flex justify-between">
                                <div>
                                  <p className="font-medium">{order.opportunityname || 'Unnamed Order'}</p>
                                  <p className="text-sm text-gray-600">{order.ordernumber}</p>
                                </div>
                                <p className="font-bold">${(order.amount || 0).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {rentals.length > 0 && (
                        <div>
                          <h3 className="font-bold mb-2">Active Rentals</h3>
                          {rentals.map((rental, idx) => (
                            <div key={idx} className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded mb-2">
                              <div className="flex justify-between">
                                <div>
                                  <p className="font-medium">{rental.CommonName || rental.ProductCode || 'Unnamed Rental'}</p>
                                  <p className="text-sm text-gray-600">{rental.ProductCode}</p>
                                </div>
                                <p className="font-bold">${(rental.amount || 0).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Popup Dialog */}
      <Dialog open={mapPopupOpen} onOpenChange={(open) => {
        setMapPopupOpen(open);
        if (!open) {
          // Small delay to allow map cleanup
          setTimeout(() => {
            setSelectedMapOpportunity(null);
          }, 100);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedMapOpportunity?.displayName} - Location Map
            </DialogTitle>
          </DialogHeader>
          
          {selectedMapOpportunity && mapPopupOpen && (
            <div className="space-y-4">
              {/* Opportunity Info Bar */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className={`text-xs px-2 py-1 rounded font-medium ${
                  selectedMapOpportunity.type === 'quote' ? 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100' :
                  selectedMapOpportunity.type === 'order' ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' :
                  'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                }`}>
                  {selectedMapOpportunity.type.toUpperCase()}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{selectedMapOpportunity.displayName}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedMapOpportunity.address || selectedMapOpportunity.deliveryaddress || selectedMapOpportunity.siteaddress}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold">${(selectedMapOpportunity.amount || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500 font-mono">
                    {selectedMapOpportunity.latitude?.toFixed(4)}, {selectedMapOpportunity.longitude?.toFixed(4)}
                  </p>
                </div>
              </div>
              
              {/* Map Container */}
              <div className="h-96 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                <MapContainer
                  key={`map-${selectedMapOpportunity.id}-${selectedMapOpportunity.type}`}
                  center={[selectedMapOpportunity.latitude, selectedMapOpportunity.longitude]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                  whenReady={() => {
                    // Map is ready, no additional setup needed
                  }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker 
                    position={[selectedMapOpportunity.latitude, selectedMapOpportunity.longitude]}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div style="background-color: ${
                        selectedMapOpportunity.type === 'quote' ? '#3b82f6' :
                        selectedMapOpportunity.type === 'order' ? '#10b981' : '#8b5cf6'
                      }; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
                      iconSize: [20, 20],
                      iconAnchor: [10, 10]
                    })}
                  >
                    <LeafletPopup>
                      <div className="p-2">
                        <h3 className="font-semibold">{selectedMapOpportunity.displayName}</h3>
                        <p className="text-sm text-gray-600">{selectedMapOpportunity.id}</p>
                        <p className="text-sm">{selectedMapOpportunity.address || selectedMapOpportunity.deliveryaddress || selectedMapOpportunity.siteaddress}</p>
                        <p className="font-medium">${(selectedMapOpportunity.amount || 0).toLocaleString()}</p>
                      </div>
                    </LeafletPopup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ItineraryDetailsPage;
